import React, { useState, useEffect } from "react";
import {
  Package,
  Plus,
  Send,
  Edit2,
  Trash2,
  Clock,
  CheckCircle,
  Truck,
  Home,
  X,
  Save,
  MessageSquare,
  Bot,
  Users,
  FileText,
  ChevronRight,
  RefreshCw,
  ChevronLeft,
  ChevronDown,
  Box,
  AlertCircle,
  CreditCard,
  List,
  RotateCcw,
  Layers,
  Printer,
  Share2,
  ImagePlus,
  DollarSign,
  LogOut,
  Globe,
} from "lucide-react";
import { supabase } from "../supabaseClient";
import { isOnlineOrder, onlineOrderLabel } from "../utils/orderSource";
import { ITEM_TERMINAL_STATUSES, RECEIPT_TERMINAL_STATUSES, ITEM_ARRIVED_STATUSES } from '../utils/statusConstants';
import { formatCurrency } from '../utils/formatCurrency';
import { useReceiptDashboard } from "./useReceiptDashboard";
import ReceiptDashboardMobile from "./ReceiptDashboardMobile";
import ReceiptDashboardDesktop from "./ReceiptDashboardDesktop";
import InvoiceScanner from "./InvoiceScanner";
import StoreSettings from "./StoreSettings";
import SurveyFeedbackDisplay from "./SurveyFeedbackDisplay";
import AddressAutocomplete from "./AddressAutocomplete";
import ReceiptDetailMobile from "./ReceiptDetailMobile";
import SalesTaxExportModal from "../components/SalesTaxExportModal";
import SubscriptionPlansModal from "../components/SubscriptionPlansModal";
import ReceiptDashboardAuthScreen from "../components/ReceiptDashboardAuthScreen";
import { useDashboardSubscriptionModal } from "../hooks/useDashboardSubscriptionModal";
import ItemAutocomplete, { GroupItemNameAutocomplete } from "./ItemAutocomplete";
import {
  FulfillmentItemRow,
  FulfillmentSummary,
  BulkActionBar,
  HeaderActionsOverflow,
} from "./ReceiptFulfillmentUI";

/**
 * ReceiptDashboard - Main Wrapper Component
 *
 * Contains:
 * - All shared state via useReceiptDashboard hook
 * - Auth loading/login screens
 * - Shared CSS animations
 * - Toast notifications
 * - ALL MODALS (so they work on both mobile and desktop)
 * - Renders Mobile or Desktop main content based on screen size
 */
const ReceiptDashboard = () => {
  // Initialize all state and handlers from the shared hook
  const dashboard = useReceiptDashboard();

  // New phone receipt view (ReceiptDetailMobile), behind a per-device switch
  // while it is being tried out. Turn on by opening the dashboard once with
  // ?mobileV2=1 (sticks for that device), off with ?mobileV2=0.
  const [mobileV2] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      if (p.get("mobileV2") === "1") localStorage.setItem("receipt-mobile-v2", "1");
      if (p.get("mobileV2") === "0") localStorage.removeItem("receipt-mobile-v2");
      return localStorage.getItem("receipt-mobile-v2") === "1";
    } catch {
      return false;
    }
  });
  const {
    isSubscriptionModalOpen,
    isSubscriptionDisabledForUser,
    openSubscriptionModal,
    closeSubscriptionModal,
  } = useDashboardSubscriptionModal(dashboard.session, dashboard.isPaid);

  // Helper function to detect media URLs (ClickSend MMS or Supabase storage)
  const isMediaUrl = (content) => {
    if (!content || typeof content !== "string") return false;
    const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp"];
    const hasImageExt = imageExtensions.some((ext) =>
      content.toLowerCase().includes(ext),
    );

    // Check for ClickSend MMS URLs or Supabase storage URLs
    const isClickSend = content.includes("clicksend-api-downloads.s3");
    const isSupabase = content.includes("supabase.co/storage");

    return hasImageExt && (isClickSend || isSupabase);
  };

  // Render message content - handles both text and MMS images
  const renderMessageContent = (content, isUserMessage = false) => {
    if (isMediaUrl(content)) {
      return (
        <img
          src={content.trim()}
          alt="Attachment"
          className="max-w-full rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
          style={{ maxHeight: "300px" }}
          onClick={() => window.open(content.trim(), "_blank")}
          onError={(e) => {
            // If image fails to load, show the URL as text fallback
            e.target.style.display = "none";
            e.target.nextSibling &&
              (e.target.nextSibling.style.display = "block");
          }}
        />
      );
    }
    return <span className="whitespace-pre-wrap">{content}</span>;
  };

  // Format a message timestamp for display in the chat
  // Accepts message.created_at (ISO string from Supabase) or message.timestamp (Date object)
  const formatMessageTime = (message) => {
    const raw = message.created_at || message.timestamp;
    if (!raw) return "";
    const date = raw instanceof Date ? raw : new Date(raw);
    if (isNaN(date.getTime())) return "";
    const now = new Date();
    const todayStart = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );
    const weekStart = new Date(todayStart);
    weekStart.setDate(todayStart.getDate() - 6);
    const timeStr = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    if (date >= todayStart) {
      return timeStr;
    } else if (date >= weekStart) {
      const dayStr = date.toLocaleDateString("en-US", { weekday: "short" });
      return `${dayStr} ${timeStr}`;
    } else {
      const dateStr = date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
      return `${dateStr}, ${timeStr}`;
    }
  };

  // Destructure everything we need for auth screens, toasts, and modals
  const {
    // Auth
    session,
    authLoading,
    authEmail,
    setAuthEmail,
    authPassword,
    setAuthPassword,
    authError,
    setAuthError,
    authMode,
    setAuthMode,
    handleLogin,
    handleSignup,
    logoutConfirmOpen,
    isLoggingOut,
    confirmLogout,
    cancelLogout,

    // Notifications/Toasts
    notifications,
    setNotifications,
    addNotification,
    notificationAudioRef,
    receipts,
    handleViewReceipt,
    escalations,
    setEscalations,

    // Modal states
    showModal,
    showReceiptModal,
    showPartialDeliveryModal,
    setShowPartialDeliveryModal,
    showScanner,
    setShowScanner,
    showBulkAddModal,
    setShowBulkAddModal,
    showBulkSetModal,
    setShowBulkSetModal,
    showStoreSettings,
    setShowStoreSettings,
    showSalesTaxExportModal,
    setShowSalesTaxExportModal,
    showSignaturesDrawer,
    setShowSignaturesDrawer,

    // Receipt/Edit state needed by modals
    selectedReceipt,
    editingReceipt,
    formData,
    setFormData,
    editFormData,
    setEditFormData,
    isEditingInModal,
    setIsEditingInModal,
    showUnsavedChangesWarning,
    setShowUnsavedChangesWarning,
    showDeleteWarning,
    expandedSkuGroups,
    setExpandedSkuGroups,
    expandedSets,

    // Partial delivery state
    partialDeliveryReceipt,
    setPartialDeliveryReceipt,
    selectedDeliveryItems,
    setSelectedDeliveryItems,
    isProcessingPartialDelivery,
    isUndoingDelivery,
    selectedItemsForDelivery,
    isCreatingDeliveryFromReceipt,

    // Bulk add state
    bulkSkuInput,
    setBulkSkuInput,
    setBulkAddTarget,
    setName,
    setSetName,
    setPrice,
    setSetPrice,
    groupedItems,
    setGroupedItems,
    editingGroupItemIndex,
    setEditingGroupItemIndex,
    groupItemFormData,
    setGroupItemFormData,
    handleAddInlineGroupRow,
    handleUpdateGroupItemInline,
    handleUpdateGroupItemFields,
    handleAddGroupItemsBulk,
    handleRemoveGroupItem,
    handleCloseGroupedItemsModal,

    // Payment History
    showPaymentForm,
    setShowPaymentForm,
    paymentFormData,
    setPaymentFormData,
    handleAddPayment,
    handleRemovePayment,

    // Signatures
    signatures,
    loadingSignatures,

    // Chat state
    staffMessage,
    setStaffMessage,
    staffImage,
    setStaffImage,
    isStaffMode,
    sendingMessage,
    chatContainerRef,
    mobileChatContainerRef,
    isTakingOver,
    isReturningToAI,
    isMobileChatOpen,
    setIsMobileChatOpen,

    // Loading states
    isSavingReceipt,
    sendingSMSReceiptId,
    smsSentReceipts,
    isDeletingReceipt,
    isSavingEdit,

    // Store
    currentStore,
    storeSettings,
    setStoreSettings,
    // Manufacturers
    manufacturers,
    loadManufacturers,
    createManufacturer,

    // Inventory
    allInventoryUnits,
    selectedMobileInvoice,
    setSelectedMobileInvoice,

    // Constants
    deliveryStatuses,

    // Handlers needed by modals
    handleCloseModal,
    handleAttemptCloseModal,
    confirmDiscardEditModalChanges,
    cancelDiscardEditModalChanges,
    showEditModalUnsavedWarning,
    setShowEditModalUnsavedWarning,
    handleOpenModal,
    handleSaveReceipt,
    handleDeleteReceipt,
    confirmDeleteReceipt,
    cancelDeleteReceipt,
    handleSendSMS,
    openPartialDeliveryModal,
    handlePrintReceipt,
    handleShareReceipt,
    handleCloseReceiptModal,
    handleAttemptCloseReceiptModal,
    confirmDiscardChanges,
    cancelDiscardChanges,
    handleSaveEditInModal,
    handleCancelEditInModal,
    handleTakeOverChat,
    handleReturnToAI,
    handleSendStaffMessage,
    handleChatScroll,
    handleAddItem,
    handleEditItem,
    handleSaveItemFromModal,
    handleCloseItemModal,
    showItemModal,
    editingItemIndex,
    itemFormData,
    setItemFormData,
    itemModalTarget,
    handleRemoveItem,
    handleBulkAddItems,
    handleAddSet,
    handleRemoveSet,
    handleEditSet,
    editingSetId,
    toggleSetExpansion,
    toggleDeliveryItem,
    handleUndoDelivery,
    handlePartialDelivery,
    handleMarkUnitStatus,
    handleMarkItemReady,
    handleUndoReady,
    markingItemReady,
    itemStatusBusy,
    isLoadingReceiptDetails,
    toggleItemForDelivery,
    clearSelectedItemsForDelivery,
    handleCreateDeliveryFromReceipt,
    tabVisibility,
    openSignaturesDrawer,
    loadReceipts,
    loadInvoices,

    // Utility functions
    formatPhoneNumber,
    calculateTotals,
    getStatusColor,
    getStatusIcon,
    getItemStatusByInventory,
    getAllItemStatusesForReceipt,
    getPaymentStatus,
    groupItemsForDisplay,
    getSetTotal,
  } = dashboard;
  // Track screen size for responsive rendering
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth < 1024;
    }
    return false;
  });

  // Receipt drawer: chat panel open/closed (desktop). Defaults to closed so
  // the receipt details get full width — opening the chat is opt-in. Choice
  // is persisted to localStorage so it sticks across sessions.
  const [isChatPanelOpen, setIsChatPanelOpen] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return localStorage.getItem("receiptDrawerChatOpen") === "true";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("receiptDrawerChatOpen", String(isChatPanelOpen));
    } catch {
      /* ignore quota/private-mode errors */
    }
  }, [isChatPanelOpen]);

  // Manufacturer inline-add state
  const [showNewManufacturerInput, setShowNewManufacturerInput] =
    useState(false);
  const [newManufacturerName, setNewManufacturerName] = useState("");
  const [isCreatingManufacturer, setIsCreatingManufacturer] = useState(false);

  // Handle creating a new manufacturer from the dropdown
  const handleCreateNewManufacturer = async (target = "item") => {
    if (!newManufacturerName.trim() || !currentStore?.id) return;
    setIsCreatingManufacturer(true);
    try {
      const newMfr = await createManufacturer(
        currentStore.id,
        newManufacturerName,
      );
      if (newMfr) {
        await loadManufacturers(currentStore.id);
        if (target === "group") {
          // Update the inline group row if editingGroupItemIndex is set
          if (
            editingGroupItemIndex !== null &&
            groupedItems[editingGroupItemIndex]
          ) {
            handleUpdateGroupItemInline(
              editingGroupItemIndex,
              "manufacturerId",
              newMfr.id,
            );
          }
          setGroupItemFormData({
            ...groupItemFormData,
            manufacturerId: newMfr.id,
          });
        } else {
          setItemFormData({ ...itemFormData, manufacturerId: newMfr.id });
        }
      }
    } finally {
      setIsCreatingManufacturer(false);
      setShowNewManufacturerInput(false);
      setNewManufacturerName("");
    }
  };

  // Resolve a catalog brand name -> this store's manufacturerId.
  // Only matches an EXISTING manufacturer. Does NOT create one — if the
  // store doesn't have that brand yet, the operator selects/adds it.
  const resolveBrandToManufacturerId = (brandName) => {
    if (!brandName) return null;
    const wanted = brandName.trim().toLowerCase();
    const existing = manufacturers.find(
      (m) => (m.name || "").trim().toLowerCase() === wanted,
    );
    return existing ? existing.id : null;
  };

  // ==========================================
  // ON-ORDER PRIOR for the invoice scanner
  // ------------------------------------------
  // The SKUs this store is actively waiting on (sold items on undelivered
  // receipts, not yet fulfilled by an inventory unit). Passed to
  // InvoiceScanner so the backend can prime the SKU re-read and validate
  // readings against what the store actually expects to receive. Mirrors
  // InventoryTab's On Order derivation, flattened to {sku, name, brand}.
  // ==========================================
  const [onOrderSkusForScanner, setOnOrderSkusForScanner] = useState([]);

  const loadOnOrderSkusForScanner = async () => {
    const storeId = currentStore?.id;
    if (!storeId) {
      setOnOrderSkusForScanner([]);
      return;
    }
    try {
      const chunk = (arr, n) => {
        const out = [];
        for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
        return out;
      };

      // 1. Undelivered receipts for this store
      const { data: openReceipts, error: rErr } = await supabase
        .from("receipts")
        .select("id, delivery_status")
        .eq("store_id", storeId)
        .not(
          "delivery_status",
          "in",
          `(${RECEIPT_TERMINAL_STATUSES.join(",")})`,
        );
      if (rErr) throw rErr;
      const receiptIds = (openReceipts || []).map((r) => r.id);
      if (receiptIds.length === 0) {
        setOnOrderSkusForScanner([]);
        return;
      }

      // 2. Their items
      let allItems = [];
      for (const ids of chunk(receiptIds, 100)) {
        const { data } = await supabase
          .from("receipt_items")
          .select("id, sku, item_name, manufacturer_id")
          .in("receipt_id", ids);
        allItems = allItems.concat(data || []);
      }
      allItems = allItems.filter((i) => (i.sku || "").trim());
      if (allItems.length === 0) {
        setOnOrderSkusForScanner([]);
        return;
      }
      const itemIds = allItems.map((i) => i.id);

      // 3. Drop items already fulfilled by an inventory unit
      const fulfilled = new Set();
      for (const ids of chunk(itemIds, 200)) {
        const { data } = await supabase
          .from("inventory_units")
          .select("receipt_item_id")
          .in("receipt_item_id", ids);
        for (const u of data || []) fulfilled.add(u.receipt_item_id);
      }

      // 3.5 Drop items marked arrived/handled via item_statuses
      for (const ids of chunk(itemIds, 200)) {
        const { data } = await supabase
          .from("item_statuses")
          .select("receipt_item_id, status")
          .in("receipt_item_id", ids);
        for (const s of data || []) {
          if (ITEM_ARRIVED_STATUSES.includes(s.status)) {
            fulfilled.add(s.receipt_item_id);
          }
        }
      }

      // 4. Drop items dismissed from the on-order backlog
      const dismissed = new Set();
      for (const ids of chunk(itemIds, 200)) {
        const { data } = await supabase
          .from("on_order_status")
          .select("receipt_item_id, status")
          .in("receipt_item_id", ids);
        for (const o of data || []) {
          if (o.status === "dismissed") dismissed.add(o.receipt_item_id);
        }
      }

      // 5. Flatten to unique {sku, name, brand}, attaching brand name from
      //    the manufacturers list already loaded for this store.
      const mfrName = (id) =>
        manufacturers.find((m) => m.id === id)?.name || "";
      const bySku = new Map();
      for (const item of allItems) {
        if (fulfilled.has(item.id) || dismissed.has(item.id)) continue;
        const sku = (item.sku || "").trim();
        if (!sku) continue;
        const key = sku.toUpperCase();
        if (!bySku.has(key)) {
          bySku.set(key, {
            sku,
            name: item.item_name || "",
            brand: mfrName(item.manufacturer_id),
          });
        }
      }
      const list = [...bySku.values()];
      console.log(
        `📦 On-order prior for scanner: ${list.length} open SKU(s) for store ${storeId}`,
      );
      setOnOrderSkusForScanner(list);
    } catch (err) {
      console.error("Error loading on-order prior for scanner:", err);
      setOnOrderSkusForScanner([]);
    }
  };

  // Refresh the prior whenever the scanner opens, so it reflects the
  // current open orders at scan time (cheap; open orders only).
  useEffect(() => {
    if (showScanner) {
      loadOnOrderSkusForScanner();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showScanner, currentStore?.id]);

  if (typeof window !== "undefined") {
    window.__receiptDashboardTestApi = {
      openPartialDeliveryModal,
      openInvoiceScannerModal: () => setShowScanner(true),
      openSignaturesDrawerForReceipt: (receipt) => openSignaturesDrawer(receipt),
      getReceipts: () => receipts || [],
    };
  }

  // Listen for window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ==========================================
  // AUTH SCREENS (early returns)
  // ==========================================

  if (authLoading || !session) {
    return (
      <ReceiptDashboardAuthScreen
        session={session}
        authLoading={authLoading}
        authEmail={authEmail}
        setAuthEmail={setAuthEmail}
        authPassword={authPassword}
        setAuthPassword={setAuthPassword}
        authError={authError}
        setAuthError={setAuthError}
        authMode={authMode}
        setAuthMode={setAuthMode}
        handleLogin={handleLogin}
        handleSignup={handleSignup}
      />
    );
  }

  // ==========================================
  // MAIN RENDER
  // ==========================================
  return (
    <>
      {/* Animation Keyframes */}
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(100%); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(100%); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes pulse-ring {
          0% { transform: scale(1); opacity: 1; }
          100% { transform: scale(1.5); opacity: 0; }
        }
        @keyframes shake {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(-10deg); }
          75% { transform: rotate(10deg); }
        }
        @keyframes urgent-blink {
          0%, 50% { opacity: 1; background-color: rgb(239, 68, 68); }
          51%, 100% { opacity: 0.6; background-color: rgb(185, 28, 28); }
        }
        @keyframes urgent-pulse {
          0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
          70% { box-shadow: 0 0 0 15px rgba(239, 68, 68, 0); }
          100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
        }
        @keyframes urgent-shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
          20%, 40%, 60%, 80% { transform: translateX(2px); }
        }
        .escalation-badge {
          animation: urgent-blink 0.8s ease-in-out infinite, urgent-pulse 1.5s ease-out infinite;
        }
        .escalation-card {
          animation: urgent-pulse 2s ease-out infinite;
        }
        .escalation-shake {
          animation: urgent-shake 0.5s ease-in-out;
        }
        .notification-badge-pulse::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background: inherit;
          animation: pulse-ring 1.5s ease-out infinite;
        }
        .bell-shake {
          animation: shake 0.5s ease-in-out;
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 2s ease-in-out infinite;
        }
        @keyframes pulse-subtle {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
        .animate-pulse-subtle {
          animation: pulse-subtle 2s ease-in-out infinite;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-2px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        @keyframes glow-pulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
        .animate-glow-pulse {
          animation: glow-pulse 2s ease-in-out infinite;
        }
      `}</style>

      {/* Notification Sound */}
      <audio ref={notificationAudioRef} preload="auto">
        <source
          src="/notification.mp3"
          type="audio/mpeg"
        />
      </audio>

      {/* Toast Notifications - Premium Design */}
      <div
        className="fixed right-4 z-[200] space-y-3 pointer-events-none"
        style={{ top: "calc(env(safe-area-inset-top) + 1rem)" }}
      >
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`pointer-events-auto rounded-2xl p-4 shadow-2xl max-w-sm border ${
              notification.isEscalation
                ? "bg-white border-red-200 shadow-red-100"
                : "bg-white border-emerald-200 shadow-emerald-100"
            }`}
            style={{ animation: "slideInRight 0.3s ease-out" }}
          >
            <div className="flex items-start space-x-3">
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg ${
                  notification.isEscalation
                    ? "bg-gradient-to-br from-red-500 to-red-600 shadow-red-500/30"
                    : "bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-emerald-500/30"
                }`}
              >
                {notification.isEscalation ? (
                  <AlertCircle className="w-5 h-5 text-white" />
                ) : (
                  <CheckCircle className="w-5 h-5 text-white" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-slate-900">
                  {notification.title}
                </p>
                <p className="text-xs mt-0.5 text-slate-500 line-clamp-2">
                  {notification.message}
                </p>
              </div>
              <button
                data-tip={notification.isEscalation ? "Opens the order and marks this escalation handled" : "Opens the order and clears this notice"}
                onClick={async () => {
                  const receipt = receipts.find(
                    (r) => r.id === notification.receiptId,
                  );
                  if (receipt) handleViewReceipt(receipt);
                  setNotifications((prev) =>
                    prev.filter((n) => n.id !== notification.id),
                  );
                  if (notification.isEscalation) {
                    setEscalations((prev) => ({
                      ...prev,
                      [notification.receiptId]: {
                        ...prev[notification.receiptId],
                        acknowledged: true,
                      },
                    }));
                    try {
                      await supabase
                        .from("messages")
                        .update({ escalation_acknowledged: true })
                        .eq("receipt_id", notification.receiptId)
                        .eq("escalation_needed", true);
                    } catch (err) {
                      console.error("Error acknowledging escalation:", err);
                    }
                  }
                }}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  notification.isEscalation
                    ? "bg-red-50 text-red-700 hover:bg-red-100 border border-red-200"
                    : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200"
                }`}
              >
                {notification.isEscalation ? "Handle" : "Dismiss"}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content - Mobile or Desktop */}
      {isMobile ? (
        <ReceiptDashboardMobile {...dashboard} />
      ) : (
        <ReceiptDashboardDesktop {...dashboard} />
      )}

      {/* ==========================================
          ALL MODALS (shared between mobile and desktop)
      ========================================== */}

      {/* Create/Edit Receipt Modal */}
      {showModal && (
        <>
          <style>{`
            @keyframes createDrawerSlideIn {
              from { transform: translateX(100%); }
              to { transform: translateX(0); }
            }
            @keyframes createDrawerBackdropFade {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            .create-drawer-backdrop { animation: createDrawerBackdropFade 180ms ease-out; }
            /* Desktop-only slide-in. Mobile keeps original bottom-sheet rendering (no animation). */
            @media (min-width: 640px) {
              .create-drawer-panel { animation: createDrawerSlideIn 240ms cubic-bezier(0.32, 0.72, 0, 1); }
            }
          `}</style>
          <div
            className="fixed inset-0 bg-slate-900/50 z-[100] overflow-hidden sm:flex sm:items-stretch sm:justify-end create-drawer-backdrop"
            style={{ touchAction: "none" }}
            onClick={(e) =>
              e.target === e.currentTarget && handleAttemptCloseModal()
            }
          >
            {/*
              Mobile: bottom sheet — unchanged from original
                (absolute bottom, rounded-t-3xl, border-t, max-h respects safe-area).
              Desktop: right-side drawer matching the receipt detail view
                (sm:static, sm:rounded-none, sm:border-l, sm:h-screen, sm:w-[88%]+).
            */}
            <div
              className="absolute inset-x-0 bottom-0 sm:static sm:inset-auto bg-white rounded-t-3xl sm:rounded-none border-t sm:border-t-0 sm:border-l border-slate-200 sm:border-slate-300 w-full sm:w-[88%] lg:w-[80%] xl:w-[74%] 2xl:max-w-[1500px] max-h-[calc(100vh-env(safe-area-inset-top,0px)-12px)] sm:max-h-screen sm:h-screen flex flex-col shadow-2xl overflow-hidden create-drawer-panel"
              style={{
                touchAction: "pan-y",
                overscrollBehavior: "contain",
              }}
            >
            {/* Drag Handle - Mobile only */}
            <div className="sm:hidden flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-slate-300" />
            </div>

            {/* Modal Header */}
            <div className="flex-shrink-0 px-5 pt-2 pb-4 sm:p-6 border-b-2 border-slate-200 flex items-center justify-between bg-slate-50">
              <div className="min-w-0 flex-1">
                {editingReceipt ? (
                  <>
                    <p className="text-xs text-slate-500 mb-0.5">
                      Editing Receipt
                    </p>
                    <h2 className="text-xl sm:text-2xl font-bold text-slate-900 truncate">
                      {editingReceipt.customerName ||
                        editingReceipt.customer_name ||
                        "Receipt"}
                    </h2>
                  </>
                ) : (
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                    New Receipt
                  </h2>
                )}
              </div>
              <button
                aria-label="Close"
                onClick={handleAttemptCloseModal}
                className="p-2 rounded-lg bg-slate-100 border-2 border-slate-300 text-slate-500 hover:text-slate-700 hover:bg-slate-200 transition-colors flex-shrink-0 ml-3"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div
              className="flex-1 overflow-y-auto overscroll-y-contain overflow-x-hidden px-5 py-4 sm:p-6 space-y-4 sm:space-y-6 bg-slate-50"
              style={{
                WebkitOverflowScrolling: "touch",
                overscrollBehavior: "contain",
              }}
            >
              {/* Order Status - Only show when editing existing receipt */}
              {editingReceipt && (
                <div className="flex items-center justify-between p-3 sm:p-4 bg-white rounded-xl border-2 border-slate-200 shadow-sm">
                  <div className="flex items-center space-x-3">
                    <span
                      className={`inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold ${
                        (formData.deliveryStatus ||
                          editingReceipt.deliveryStatus ||
                          editingReceipt.delivery_status) === "delivered"
                          ? "bg-emerald-100 text-emerald-700 border border-emerald-400"
                          : (formData.deliveryStatus ||
                                editingReceipt.deliveryStatus ||
                                editingReceipt.delivery_status) ===
                              "out_for_delivery"
                            ? "bg-cyan-100 text-cyan-700 border border-cyan-400"
                            : (formData.deliveryStatus ||
                                  editingReceipt.deliveryStatus ||
                                  editingReceipt.delivery_status) ===
                                "partial_out_for_delivery"
                              ? "bg-indigo-100 text-indigo-700 border border-indigo-400"
                              : (formData.deliveryStatus ||
                                    editingReceipt.deliveryStatus ||
                                    editingReceipt.delivery_status) ===
                                  "partially_delivered"
                                ? "bg-amber-100 text-amber-700 border border-amber-400"
                                : "bg-blue-100 text-blue-700 border border-blue-400"
                      }`}
                    >
                      {getStatusIcon(
                        formData.deliveryStatus ||
                          editingReceipt.deliveryStatus ||
                          editingReceipt.delivery_status,
                      )}
                      <span>
                        {deliveryStatuses.find(
                          (s) =>
                            s.value ===
                            (formData.deliveryStatus ||
                              editingReceipt.deliveryStatus ||
                              editingReceipt.delivery_status),
                        )?.label || "Order Received"}
                      </span>
                    </span>
                  </div>

                  {/* Mark Completed - only when not delivered */}
                  {(formData.deliveryStatus ||
                    editingReceipt.deliveryStatus ||
                    editingReceipt.delivery_status) !== "delivered" && (
                    <button
                      data-tip="Sets this order to completed once you save"
                      type="button"
                      onClick={() =>
                        setFormData({
                          ...formData,
                          deliveryStatus: "delivered",
                        })
                      }
                      className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg bg-emerald-100 border border-emerald-400 text-emerald-700 text-sm font-medium hover:bg-emerald-200 active:bg-emerald-200 transition-colors"
                    >
                      <CheckCircle className="w-4 h-4" />
                      <span>Mark Completed</span>
                    </button>
                  )}

                  {/* Move to Active - only when delivered */}
                  {(formData.deliveryStatus ||
                    editingReceipt.deliveryStatus ||
                    editingReceipt.delivery_status) === "delivered" && (
                    <button
                      data-tip="Returns this order to the active list once you save"
                      type="button"
                      onClick={() =>
                        setFormData({ ...formData, deliveryStatus: "pending" })
                      }
                      className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg bg-blue-100 border border-blue-400 text-blue-700 text-sm font-medium hover:bg-blue-200 active:bg-blue-200 transition-colors"
                    >
                      <RotateCcw className="w-4 h-4" />
                      <span>Move to Active</span>
                    </button>
                  )}
                </div>
              )}

              {/* Customer Information */}
              <div className="space-y-3 sm:space-y-4">
                <h3 className="text-base sm:text-lg font-bold text-slate-800 border-b-2 border-slate-200 pb-2">
                  Customer Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-slate-700 mb-2">
                      Customer Name *
                    </label>
                    <input
                      type="text"
                      value={formData.customerName}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          customerName: e.target.value,
                        })
                      }
                      placeholder="John Smith"
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white border-2 border-slate-300 rounded-lg sm:rounded-xl text-slate-900 text-base placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all shadow-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-slate-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="customer-phone-input"
                      autoComplete="off"
                      data-form-type="other"
                      value={formData.customerPhone}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          customerPhone: formatPhoneNumber(e.target.value),
                        })
                      }
                      placeholder="(916) 555-0123"
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white border-2 border-slate-300 rounded-lg sm:rounded-xl text-slate-900 text-base placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all shadow-sm"
                    />
                    {/* SMS Consent Checkbox (TCPA Compliance) */}
                    <label className="flex items-start gap-2 mt-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={formData.smsConsent}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            smsConsent: e.target.checked,
                          })
                        }
                        className="mt-0.5 w-4 h-4 rounded border-slate-300 bg-white text-blue-600 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
                      />
                      <span className="text-xs text-slate-600 group-hover:text-slate-800 transition-colors">
                        Customer agrees to receive SMS updates about their
                        order.{" "}
                        <span className="text-slate-400">(Asked verbally)</span>
                      </span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-slate-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={formData.customerEmail}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          customerEmail: e.target.value,
                        })
                      }
                      placeholder="john@example.com"
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white border-2 border-slate-300 rounded-lg sm:rounded-xl text-slate-900 text-base placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all shadow-sm"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs sm:text-sm font-semibold text-slate-700 mb-2">
                      Delivery Address
                    </label>
                    <div className="space-y-3">
                      {/* Street Address with Google Autocomplete */}
                      <AddressAutocomplete
                        value={formData.streetAddress}
                        formData={formData}
                        setFormData={setFormData}
                        placeholder="Street Address"
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white border-2 border-slate-300 rounded-lg sm:rounded-xl text-slate-900 text-base placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all shadow-sm"
                      />
                      {/* Apartment Number */}
                      <input
                        type="text"
                        value={formData.apartmentNumber}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            apartmentNumber: e.target.value,
                          })
                        }
                        placeholder="Apt, Suite, Unit # (optional)"
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white border-2 border-slate-300 rounded-lg sm:rounded-xl text-slate-900 text-base placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all shadow-sm"
                      />
                      {/* City, State, Zip Row */}
                      <div className="grid grid-cols-5 gap-2 sm:gap-3">
                        <input
                          type="text"
                          value={formData.city}
                          onChange={(e) =>
                            setFormData({ ...formData, city: e.target.value })
                          }
                          placeholder="City"
                          className="col-span-2 px-3 sm:px-4 py-2 sm:py-3 bg-white border-2 border-slate-300 rounded-lg sm:rounded-xl text-slate-900 text-base placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all shadow-sm"
                        />
                        <input
                          type="text"
                          value={formData.state}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              state: e.target.value.toUpperCase().slice(0, 2),
                            })
                          }
                          placeholder="State"
                          maxLength={2}
                          className="col-span-1 px-3 sm:px-4 py-2 sm:py-3 bg-white border-2 border-slate-300 rounded-lg sm:rounded-xl text-slate-900 text-base placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all shadow-sm text-center uppercase"
                        />
                        <input
                          type="text"
                          value={formData.zipCode}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              zipCode: e.target.value
                                .replace(/\D/g, "")
                                .slice(0, 5),
                            })
                          }
                          placeholder="ZIP"
                          maxLength={5}
                          inputMode="numeric"
                          className="col-span-2 px-3 sm:px-4 py-2 sm:py-3 bg-white border-2 border-slate-300 rounded-lg sm:rounded-xl text-slate-900 text-base placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all shadow-sm"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-slate-700 mb-2">
                      Salesperson
                    </label>
                    <input
                      type="text"
                      value={formData.salesperson}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          salesperson: e.target.value,
                        })
                      }
                      placeholder="e.g., Mike"
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white border-2 border-slate-300 rounded-lg sm:rounded-xl text-slate-900 text-base placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all shadow-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Items */}
              <div className="space-y-4">
                <div className="border-b-2 border-slate-200 pb-3">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-slate-800">Items</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      data-tip="Opens a form to add one item to this receipt"
                      onClick={() => handleAddItem("create")}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white active:scale-[0.98] transition-all shadow-md"
                    >
                      <Plus className="w-4 h-4" />
                      <span className="text-xs font-bold">Add Item</span>
                    </button>
                    <button
                      data-tip="Adds several items to this receipt as one grouped set"
                      onClick={() => {
                        setBulkAddTarget("create");
                        setShowBulkSetModal(true);
                      }}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-slate-100 border-2 border-slate-300 text-slate-700 hover:bg-slate-200 active:scale-[0.98] transition-all shadow-sm"
                    >
                      <Layers className="w-4 h-4" />
                      <span className="text-xs font-bold">
                        Grouped Items
                      </span>
                    </button>
                  </div>
                </div>

                {/* Inventory Progress Banner */}
                {editingReceipt &&
                  formData.items.length > 0 &&
                  (() => {
                    const inStockCount = formData.items.filter((item) => {
                      const status = formData.itemStatuses?.find(
                        (s) => s.sku?.toLowerCase() === item.sku?.toLowerCase(),
                      );
                      return (
                        status &&
                        ["at_store", "in_warehouse", "delivered"].includes(
                          status.status,
                        )
                      );
                    }).length;
                    const totalCount = formData.items.length;
                    const progressPercent = (inStockCount / totalCount) * 100;
                    const allReady = inStockCount === totalCount;

                    return (
                      <div
                        className={`p-3 rounded-xl border-2 flex items-center justify-between shadow-sm ${
                          allReady
                            ? "bg-emerald-50 border-emerald-400"
                            : "bg-slate-50 border-slate-300"
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          {allReady ? (
                            <CheckCircle className="w-5 h-5 text-emerald-600" />
                          ) : (
                            <Clock className="w-5 h-5 text-slate-600" />
                          )}
                          <span
                            className={`text-sm font-bold ${allReady ? "text-emerald-700" : "text-slate-600"}`}
                          >
                            {allReady
                              ? "All items in stock!"
                              : `${inStockCount}/${totalCount} items in stock`}
                          </span>
                        </div>
                        <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${allReady ? "bg-emerald-500" : "bg-slate-500"}`}
                            style={{ width: `${progressPercent}%` }}
                          />
                        </div>
                      </div>
                    );
                  })()}

                {/* Empty State - No Items */}
                {formData.items.length === 0 && (
                  <div className="p-8 rounded-xl border-2 border-dashed border-slate-300 bg-slate-100 text-center">
                    <Package className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                    <p className="text-slate-700 font-semibold mb-1">
                      No items added yet
                    </p>
                    <p className="text-slate-500 text-sm">
                      Click "Add Item" or "Add Grouped Items" to add furniture
                      to this receipt
                    </p>
                  </div>
                )}

                {/* Items Display - Grouped by Sets */}
                {groupItemsForDisplay(formData.items).map(
                  (group, groupIndex) => {
                    if (group.type === "set") {
                      // Render SET group
                      const isExpanded = expandedSets[group.setId] !== false; // Default expanded
                      const setTotal = getSetTotal(group.items);

                      // Check how many items in this set are in stock
                      const inStockCount = group.items.filter((item) => {
                        const status = formData.itemStatuses?.find(
                          (s) =>
                            s.sku?.toLowerCase() === item.sku?.toLowerCase(),
                        );
                        return (
                          status &&
                          ["at_store", "in_warehouse", "delivered"].includes(
                            status.status,
                          )
                        );
                      }).length;
                      const allInStock = inStockCount === group.items.length;
                      const someInStock =
                        inStockCount > 0 && inStockCount < group.items.length;

                      return (
                        <div
                          key={group.setId}
                          className="rounded-xl border-2 border-slate-300 overflow-hidden shadow-sm bg-white"
                        >
                          {/* Set Header - Collapsible */}
                          <div
                            className={`p-3 cursor-pointer hover:bg-slate-50 transition-colors ${
                              allInStock
                                ? "bg-emerald-50"
                                : someInStock
                                  ? "bg-slate-50"
                                  : "bg-slate-50"
                            }`}
                            onClick={() => toggleSetExpansion(group.setId)}
                          >
                            {/* Row 1: Expand icon, Name, Price, Delete */}
                            <div className="flex items-start gap-2 mb-1">
                              <div
                                className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                  allInStock ? "bg-emerald-200" : "bg-slate-200"
                                }`}
                              >
                                {isExpanded ? (
                                  <ChevronDown
                                    className={`w-4 h-4 ${allInStock ? "text-emerald-700" : "text-slate-600"}`}
                                  />
                                ) : (
                                  <ChevronRight
                                    className={`w-4 h-4 ${allInStock ? "text-emerald-700" : "text-slate-600"}`}
                                  />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="text-slate-900 font-bold">
                                  {group.setName}
                                </h3>
                                <p className="text-slate-500 text-sm">
                                  {group.items.length} pieces
                                </p>
                              </div>
                              <span className="text-lg font-bold text-slate-900 flex-shrink-0">
                                ${formatCurrency(setTotal)}
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditSet(group.setId);
                                }}
                                className="w-8 h-8 rounded bg-blue-50 border border-blue-200 text-blue-500 flex items-center justify-center flex-shrink-0 hover:bg-blue-100 transition-colors"
                                title="Edit group"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                aria-label="Delete this set"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (
                                    window.confirm(
                                      `Delete entire "${group.setName}" set (${group.items.length} items)?`,
                                    )
                                  ) {
                                    handleRemoveSet(group.setId);
                                  }
                                }}
                                className="w-8 h-8 rounded bg-red-50 border border-red-200 text-red-500 flex items-center justify-center flex-shrink-0"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                            {/* Row 2: Status badge */}
                            <div className="ml-10">
                              {allInStock ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium">
                                  <CheckCircle className="w-3 h-3" /> All Ready
                                </span>
                              ) : someInStock ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-medium">
                                  <Clock className="w-3 h-3" /> {inStockCount}/
                                  {group.items.length} Ready
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-medium">
                                  <Clock className="w-3 h-3" /> Waiting
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Set Items - Expandable - CLEAN CARD DISPLAY */}
                          {isExpanded && (
                            <div className="divide-y divide-slate-200">
                              {group.items.map((item, idx) => {
                                const itemStatus = formData.itemStatuses?.find(
                                  (s) =>
                                    s.sku?.toLowerCase() ===
                                    item.sku?.toLowerCase(),
                                );
                                const isInStock =
                                  itemStatus &&
                                  [
                                    "at_store",
                                    "in_warehouse",
                                    "delivered",
                                  ].includes(itemStatus.status);
                                const isDelivered =
                                  itemStatus?.status === "delivered";
                                const isPickedUp =
                                  itemStatus?.status === "picked_up";
                                const showActionButtons =
                                  editingReceipt &&
                                  isInStock &&
                                  !isDelivered &&
                                  !isPickedUp;

                                return (
                                  <div
                                    key={item.originalIndex}
                                    className={`p-3 ${showActionButtons ? "bg-green-50" : ""}`}
                                  >
                                    {/* Row 1: Name and delete */}
                                    <div className="flex items-start justify-between gap-2 mb-1">
                                      <div className="flex-1">
                                        <span className="text-slate-900 font-medium">
                                          #{idx + 1}{" "}
                                          {item.name || "Unnamed Item"}
                                        </span>
                                      </div>
                                      <button
                                        aria-label="Remove item from set"
                                        onClick={() => {
                                          if (
                                            window.confirm(
                                              `Remove "${item.name || "this item"}" from the set?`,
                                            )
                                          ) {
                                            handleRemoveItem(
                                              item.originalIndex,
                                            );
                                          }
                                        }}
                                        className="w-7 h-7 rounded bg-red-50 border border-red-200 text-red-500 flex items-center justify-center flex-shrink-0"
                                      >
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    </div>

                                    {/* Row 2: SKU and Status */}
                                    <div className="flex items-center gap-2 flex-wrap">
                                      {item.sku && (
                                        <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 text-xs font-mono">
                                          {item.sku}
                                        </span>
                                      )}
                                      {isDelivered ? (
                                        <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium flex items-center gap-1">
                                          <CheckCircle className="w-3 h-3" />{" "}
                                          Delivered
                                        </span>
                                      ) : isPickedUp ? (
                                        <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium flex items-center gap-1">
                                          <CheckCircle className="w-3 h-3" />{" "}
                                          Picked Up
                                        </span>
                                      ) : isInStock ? (
                                        <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium flex items-center gap-1">
                                          <CheckCircle className="w-3 h-3" />{" "}
                                          Ready
                                        </span>
                                      ) : (
                                        <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-xs font-medium flex items-center gap-1">
                                          <Clock className="w-3 h-3" /> Waiting
                                        </span>
                                      )}
                                    </div>

                                    {/* Row 3: Action buttons (if needed) */}
                                    {showActionButtons && (
                                      <div className="flex gap-2 mt-3">
                                        <button
                                          data-tip="Marks this item as delivered"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleMarkUnitStatus(
                                              editingReceipt.id,
                                              item,
                                              "delivered",
                                            );
                                          }}
                                          className="flex-1 py-2.5 text-sm font-semibold text-white bg-emerald-600 rounded-lg active:bg-emerald-700 flex items-center justify-center gap-1.5"
                                        >
                                          <Home className="w-4 h-4" />
                                          Delivered
                                        </button>
                                        <button
                                          data-tip="Marks this item as picked up"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleMarkUnitStatus(
                                              editingReceipt.id,
                                              item,
                                              "picked_up",
                                            );
                                          }}
                                          className="flex-1 py-2.5 text-sm font-semibold text-white bg-purple-600 rounded-lg active:bg-purple-700 flex items-center justify-center gap-1.5"
                                        >
                                          <Box className="w-4 h-4" />
                                          Picked Up
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    } else {
                      // Render SINGLE item (not part of a set) - CLEAN CARD DISPLAY
                      const item = group.items[0];
                      const index = item.originalIndex;
                      const itemStatus = formData.itemStatuses?.find(
                        (s) => s.sku?.toLowerCase() === item.sku?.toLowerCase(),
                      );
                      const isInStock =
                        itemStatus &&
                        ["at_store", "in_warehouse", "delivered"].includes(
                          itemStatus.status,
                        );
                      const isDelivered = itemStatus?.status === "delivered";
                      const isPickedUp = itemStatus?.status === "picked_up";
                      const showActionButtons =
                        editingReceipt &&
                        isInStock &&
                        !isDelivered &&
                        !isPickedUp;

                      return (
                        <div
                          key={index}
                          className={`p-4 rounded-xl border cursor-pointer transition-all ${
                            showActionButtons
                              ? "bg-green-50 border-green-200"
                              : "bg-white border-slate-200"
                          }`}
                          onClick={() => handleEditItem(index, "create")}
                        >
                          {/* Row 1: Name and Price + Delete */}
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h4 className="text-slate-900 font-semibold flex-1">
                              {item.name || "Unnamed Item"}
                            </h4>
                            <span className="text-lg font-bold text-slate-900 flex-shrink-0">
                              ${formatCurrency(parseFloat(item.price) || 0)}
                            </span>
                            <button
                              aria-label="Remove item"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (
                                  window.confirm(
                                    `Remove "${item.name || "this item"}"?`,
                                  )
                                ) {
                                  handleRemoveItem(index);
                                }
                              }}
                              className="w-8 h-8 rounded bg-red-50 border border-red-200 text-red-500 flex items-center justify-center flex-shrink-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>

                          {/* Row 2: SKU, Status, Qty */}
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            {item.sku && (
                              <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 text-xs font-mono">
                                {item.sku}
                              </span>
                            )}
                            {item.manufacturerId &&
                              manufacturers.find(
                                (m) => m.id === item.manufacturerId,
                              ) && (
                                <span className="px-1.5 py-0.5 rounded bg-purple-50 text-purple-600 text-xs font-medium">
                                  {
                                    manufacturers.find(
                                      (m) => m.id === item.manufacturerId,
                                    )?.name
                                  }
                                </span>
                              )}
                            {isDelivered ? (
                              <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" /> Delivered
                              </span>
                            ) : isPickedUp ? (
                              <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" /> Picked Up
                              </span>
                            ) : isInStock ? (
                              <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" /> Ready
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-xs font-medium flex items-center gap-1">
                                <Clock className="w-3 h-3" /> Waiting
                              </span>
                            )}
                            <span className="text-xs text-slate-500">
                              Qty: {item.quantity || 1}
                            </span>
                          </div>

                          {/* Row 3: Action buttons (if needed) */}
                          {showActionButtons && (
                            <div className="flex gap-2 mt-3">
                              <button
                                data-tip="Marks this item as delivered"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMarkUnitStatus(
                                    editingReceipt.id,
                                    item,
                                    "delivered",
                                  );
                                }}
                                className="flex-1 py-2.5 text-sm font-semibold text-white bg-emerald-600 rounded-lg active:bg-emerald-700 flex items-center justify-center gap-1.5"
                              >
                                <Home className="w-4 h-4" />
                                Delivered
                              </button>
                              <button
                                data-tip="Marks this item as picked up"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMarkUnitStatus(
                                    editingReceipt.id,
                                    item,
                                    "picked_up",
                                  );
                                }}
                                className="flex-1 py-2.5 text-sm font-semibold text-white bg-purple-600 rounded-lg active:bg-purple-700 flex items-center justify-center gap-1.5"
                              >
                                <Box className="w-4 h-4" />
                                Picked Up
                              </button>
                            </div>
                          )}

                          <p className="text-xs text-slate-400 mt-2">
                            Tap to edit
                          </p>
                        </div>
                      );
                    }
                  },
                )}

                {/* Totals */}
                <div className="p-4 bg-white border-2 border-slate-300 rounded-xl space-y-2 shadow-sm">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Subtotal:</span>
                    <span className="text-slate-900 font-bold">
                      $
                      {formatCurrency(
                        calculateTotals(
                          formData.items,
                          formData.deliveryServices,
                        ).subtotal,
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">
                      Tax ({storeSettings.tax_rate || 8}%):
                    </span>
                    <span className="text-slate-900 font-bold">
                      $
                      {formatCurrency(
                        calculateTotals(
                          formData.items,
                          formData.deliveryServices,
                        ).tax,
                      )}
                    </span>
                  </div>
                  {calculateTotals(formData.items, formData.deliveryServices)
                    .servicesTotal > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600 font-semibold">
                        Services:
                      </span>
                      <span className="text-slate-700 font-bold">
                        $
                        {formatCurrency(
                          calculateTotals(
                            formData.items,
                            formData.deliveryServices,
                          ).servicesTotal,
                        )}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg border-t-2 border-slate-200 pt-2">
                    <span className="text-slate-900 font-bold">Total:</span>
                    <span className="text-slate-900 font-bold">
                      $
                      {formatCurrency(
                        calculateTotals(
                          formData.items,
                          formData.deliveryServices,
                        ).total,
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Delivery Information */}
              <div className="p-5 rounded-xl bg-white/[0.03] border border-slate-300 space-y-4">
                <h3 className="text-lg font-semibold text-blue-600 flex items-center gap-2">
                  <Truck className="w-5 h-5" />
                  Delivery Information
                </h3>

                {/* Delivery Date */}
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">
                      Expected Delivery Date
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        value={formData.deliveryDate}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            deliveryDate: e.target.value,
                          })
                        }
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:border-blue-500 transition-colors appearance-none sm:[color-scheme:dark] sm:appearance-auto"
                      />
                      {!formData.deliveryDate && (
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none sm:hidden">
                          Select date...
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Tax on Delivery Services Toggle */}
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex-1 pr-3">
                    <label
                      htmlFor="create-tax-exempt-services"
                      className="text-sm font-semibold text-slate-800 cursor-pointer block"
                    >
                      Exempt delivery services from tax
                    </label>
                    <p className="text-xs text-slate-600 mt-0.5">
                      When on, delivery, setup, customer pickup, and waste removal are NOT taxed.
                    </p>
                  </div>
                  <label
                    htmlFor="create-tax-exempt-services"
                    className="relative inline-flex items-center cursor-pointer shrink-0"
                  >
                    <input
                      id="create-tax-exempt-services"
                      type="checkbox"
                      className="sr-only peer"
                      checked={!!formData.deliveryServices?.taxExempt}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          deliveryServices: {
                            ...formData.deliveryServices,
                            taxExempt: e.target.checked,
                          },
                        })
                      }
                    />
                    <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-amber-400 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                  </label>
                </div>

                {/* Delivery Services */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-600">
                    Services & Pricing
                  </label>
                  <div className="grid grid-cols-1 gap-2">
                    {/* Delivery */}
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-300">
                      <label className="flex items-center gap-3 cursor-pointer flex-1">
                        <input
                          type="checkbox"
                          checked={
                            formData.deliveryServices?.delivery?.enabled ||
                            false
                          }
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              deliveryServices: {
                                ...formData.deliveryServices,
                                delivery: {
                                  ...formData.deliveryServices?.delivery,
                                  enabled: e.target.checked,
                                },
                              },
                            })
                          }
                          className="w-5 h-5 rounded border-slate-300 bg-slate-100 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
                        />
                        <span className="text-slate-900 font-medium">
                          Delivery
                        </span>
                      </label>
                      <div className="flex items-center gap-1">
                        <span
                          className={
                            formData.deliveryServices?.delivery?.enabled
                              ? "text-slate-500"
                              : "text-slate-300"
                          }
                        >
                          $
                        </span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={
                            formData.deliveryServices?.delivery?.price || ""
                          }
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              deliveryServices: {
                                ...formData.deliveryServices,
                                delivery: {
                                  ...formData.deliveryServices?.delivery,
                                  price: e.target.value,
                                },
                              },
                            })
                          }
                          disabled={
                            !formData.deliveryServices?.delivery?.enabled
                          }
                          placeholder="0"
                          className={`w-20 px-3 py-2 border rounded-lg text-right focus:outline-none ${formData.deliveryServices?.delivery?.enabled ? "bg-slate-50 border-slate-300 text-slate-900 focus:border-blue-500" : "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed"}`}
                        />
                      </div>
                    </div>

                    {/* Setup */}
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-300">
                      <label className="flex items-center gap-3 cursor-pointer flex-1">
                        <input
                          type="checkbox"
                          checked={
                            formData.deliveryServices?.setup?.enabled || false
                          }
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              deliveryServices: {
                                ...formData.deliveryServices,
                                setup: {
                                  ...formData.deliveryServices?.setup,
                                  enabled: e.target.checked,
                                },
                              },
                            })
                          }
                          className="w-5 h-5 rounded border-slate-300 bg-slate-100 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
                        />
                        <span className="text-slate-900 font-medium">
                          Setup / Assembly
                        </span>
                      </label>
                      <div className="flex items-center gap-1">
                        <span
                          className={
                            formData.deliveryServices?.setup?.enabled
                              ? "text-slate-500"
                              : "text-slate-300"
                          }
                        >
                          $
                        </span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={formData.deliveryServices?.setup?.price || ""}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              deliveryServices: {
                                ...formData.deliveryServices,
                                setup: {
                                  ...formData.deliveryServices?.setup,
                                  price: e.target.value,
                                },
                              },
                            })
                          }
                          disabled={!formData.deliveryServices?.setup?.enabled}
                          placeholder="0"
                          className={`w-20 px-3 py-2 border rounded-lg text-right focus:outline-none ${formData.deliveryServices?.setup?.enabled ? "bg-slate-50 border-slate-300 text-slate-900 focus:border-blue-500" : "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed"}`}
                        />
                      </div>
                    </div>

                    {/* Waste Pickup */}
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-300">
                      <label className="flex items-center gap-3 cursor-pointer flex-1">
                        <input
                          type="checkbox"
                          checked={
                            formData.deliveryServices?.wastePickup?.enabled ||
                            false
                          }
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              deliveryServices: {
                                ...formData.deliveryServices,
                                wastePickup: {
                                  ...formData.deliveryServices?.wastePickup,
                                  enabled: e.target.checked,
                                },
                              },
                            })
                          }
                          className="w-5 h-5 rounded border-slate-300 bg-slate-100 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
                        />
                        <span className="text-slate-900 font-medium">
                          Waste Pickup
                        </span>
                      </label>
                      <div className="flex items-center gap-1">
                        <span
                          className={
                            formData.deliveryServices?.wastePickup?.enabled
                              ? "text-slate-500"
                              : "text-slate-300"
                          }
                        >
                          $
                        </span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={
                            formData.deliveryServices?.wastePickup?.price || ""
                          }
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              deliveryServices: {
                                ...formData.deliveryServices,
                                wastePickup: {
                                  ...formData.deliveryServices?.wastePickup,
                                  price: e.target.value,
                                },
                              },
                            })
                          }
                          disabled={
                            !formData.deliveryServices?.wastePickup?.enabled
                          }
                          placeholder="0"
                          className={`w-20 px-3 py-2 border rounded-lg text-right focus:outline-none ${formData.deliveryServices?.wastePickup?.enabled ? "bg-slate-50 border-slate-300 text-slate-900 focus:border-blue-500" : "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed"}`}
                        />
                      </div>
                    </div>

                    {/* Customer Pickup */}
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-300">
                      <label className="flex items-center gap-3 cursor-pointer flex-1">
                        <input
                          type="checkbox"
                          checked={
                            formData.deliveryServices?.pickup?.enabled || false
                          }
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              deliveryServices: {
                                ...formData.deliveryServices,
                                pickup: {
                                  ...formData.deliveryServices?.pickup,
                                  enabled: e.target.checked,
                                },
                              },
                            })
                          }
                          className="w-5 h-5 rounded border-slate-300 bg-slate-100 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
                        />
                        <span className="text-slate-900 font-medium">
                          Customer Pickup
                        </span>
                      </label>
                      <div className="flex items-center gap-1">
                        <span
                          className={
                            formData.deliveryServices?.pickup?.enabled
                              ? "text-slate-500"
                              : "text-slate-300"
                          }
                        >
                          $
                        </span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={formData.deliveryServices?.pickup?.price || ""}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              deliveryServices: {
                                ...formData.deliveryServices,
                                pickup: {
                                  ...formData.deliveryServices?.pickup,
                                  price: e.target.value,
                                },
                              },
                            })
                          }
                          disabled={!formData.deliveryServices?.pickup?.enabled}
                          placeholder="0"
                          className={`w-20 px-3 py-2 border rounded-lg text-right focus:outline-none ${formData.deliveryServices?.pickup?.enabled ? "bg-slate-50 border-slate-300 text-slate-900 focus:border-blue-500" : "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed"}`}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Delivery Preferences */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">
                      Preferred Day
                    </label>
                    <input
                      type="text"
                      value={formData.customerPreferences.preferredDeliveryDay}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          customerPreferences: {
                            ...formData.customerPreferences,
                            preferredDeliveryDay: e.target.value,
                          },
                        })
                      }
                      placeholder="e.g., Weekends, Mondays"
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">
                      Preferred Time
                    </label>
                    <select
                      value={formData.customerPreferences.preferredTimeWindow}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          customerPreferences: {
                            ...formData.customerPreferences,
                            preferredTimeWindow: e.target.value,
                          },
                        })
                      }
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:border-blue-500 transition-colors"
                    >
                      <option value="" className="bg-white">
                        Any Time
                      </option>
                      <option value="Morning (8am-12pm)" className="bg-white">
                        Morning (8am-12pm)
                      </option>
                      <option value="Afternoon (12pm-5pm)" className="bg-white">
                        Afternoon (12pm-5pm)
                      </option>
                      <option value="Evening (5pm-8pm)" className="bg-white">
                        Evening (5pm-8pm)
                      </option>
                    </select>
                  </div>
                </div>

                {/* Delivery Instructions */}
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">
                    Delivery Instructions
                  </label>
                  <input
                    type="text"
                    value={formData.customerPreferences.notes}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        customerPreferences: {
                          ...formData.customerPreferences,
                          notes: e.target.value,
                        },
                      })
                    }
                    placeholder="e.g., Use side entrance, call 30 min before arrival"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
                  />
                </div>
              </div>

              {/* Payment Tracking Section */}
              <div className="bg-slate-50/50 rounded-xl p-5 border border-slate-300">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-700 mb-4">
                  <DollarSign className="w-5 h-5" />
                  Payment Tracking
                </h3>

                {/* Balance Summary */}
                <div className="mb-4">
                  {(() => {
                    const total = calculateTotals(
                      formData.items,
                      formData.deliveryServices,
                    ).total;
                    const paid =
                      Math.round((parseFloat(formData.amountPaid) || 0) * 100) /
                      100;
                    const balance = Math.round((total - paid) * 100) / 100;
                    const isPaidInFull = paid >= total && total > 0;

                    return (
                      <div
                        className={`rounded-xl p-4 ${
                          isPaidInFull
                            ? "bg-blue-50 border border-blue-300"
                            : balance > 0
                              ? "bg-slate-200 border border-slate-300"
                              : "bg-slate-50 border border-slate-300"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {isPaidInFull ? (
                              <CheckCircle className="w-5 h-5 text-emerald-400" />
                            ) : (
                              <CreditCard className="w-5 h-5 text-slate-500" />
                            )}
                            <span
                              className={`text-sm font-medium ${
                                isPaidInFull
                                  ? "text-emerald-400"
                                  : "text-slate-500"
                              }`}
                            >
                              {isPaidInFull ? "Paid in Full" : "Balance Owed"}
                            </span>
                          </div>
                          <div className="text-right">
                            <span
                              className={`text-2xl font-bold ${
                                isPaidInFull
                                  ? "text-emerald-400"
                                  : "text-slate-500"
                              }`}
                            >
                              ${formatCurrency(balance)}
                            </span>
                            <p className="text-xs text-slate-400">
                              of ${formatCurrency(total)} total
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Payment History */}
                <div className="border-t border-slate-300 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs text-slate-600 font-semibold uppercase tracking-wider flex items-center gap-1.5">
                      <List className="w-3.5 h-3.5" />
                      Payment History
                    </p>
                    <button
                      data-tip="Shows a form to record a payment on this receipt"
                      onClick={() => setShowPaymentForm(!showPaymentForm)}
                      className="text-sm px-3 py-2 rounded-lg bg-blue-50 border border-blue-300 text-blue-600 hover:bg-blue-100 active:bg-blue-200 transition-colors flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" />
                      Add Payment
                    </button>
                  </div>

                  {/* Add Payment Form */}
                  {showPaymentForm && (
                    <div className="mb-3 p-3 rounded-xl bg-slate-50 border border-slate-300 space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">
                            Amount *
                          </label>
                          <div className="flex items-center">
                            <span className="text-slate-500 mr-1">$</span>
                            <input
                              type="number"
                              step="0.01"
                              value={paymentFormData.amount}
                              onChange={(e) =>
                                setPaymentFormData({
                                  ...paymentFormData,
                                  amount: e.target.value,
                                })
                              }
                              placeholder="0.00"
                              className="w-full px-2 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 text-sm focus:outline-none focus:border-blue-500"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">
                            Date
                          </label>
                          <input
                            type="date"
                            value={paymentFormData.date}
                            onChange={(e) =>
                              setPaymentFormData({
                                ...paymentFormData,
                                date: e.target.value,
                              })
                            }
                            className="w-full px-2 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 text-sm focus:outline-none focus:border-blue-500"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">
                            Payment Type
                          </label>
                          <select
                            value={paymentFormData.paymentType || "cash"}
                            onChange={(e) =>
                              setPaymentFormData({
                                ...paymentFormData,
                                paymentType: e.target.value,
                              })
                            }
                            className="w-full px-2 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 text-sm focus:outline-none focus:border-blue-500"
                          >
                            <option value="cash">Cash</option>
                            <option value="card">Card</option>
                            <option value="check">Check</option>
                            <option value="financing">Financing</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">
                            Note (optional)
                          </label>
                          <input
                            type="text"
                            value={paymentFormData.note}
                            onChange={(e) =>
                              setPaymentFormData({
                                ...paymentFormData,
                                note: e.target.value,
                              })
                            }
                            placeholder="e.g. Deposit..."
                            className="w-full px-2 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 text-sm placeholder-slate-400 focus:outline-none focus:border-blue-500"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 pt-2">
                        <button
                          data-tip="Adds this payment to the receipt's payment history"
                          onClick={() => handleAddPayment("create")}
                          disabled={
                            !paymentFormData.amount ||
                            parseFloat(paymentFormData.amount) <= 0
                          }
                          className="flex-1 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Record Payment
                        </button>
                        <button
                          onClick={() => {
                            setShowPaymentForm(false);
                            setPaymentFormData({
                              amount: "",
                              date: new Date().toISOString().split("T")[0],
                              note: "",
                              paymentType: "cash",
                            });
                          }}
                          className="px-4 py-2.5 rounded-lg bg-slate-100 text-slate-500 text-sm hover:bg-white/20 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Payment List */}
                  {formData.paymentHistory &&
                  formData.paymentHistory.length > 0 ? (
                    <div className="space-y-2">
                      {formData.paymentHistory.map((payment, idx) => (
                        <div
                          key={payment.id || idx}
                          className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50 border border-slate-200"
                        >
                          <div className="flex items-center gap-3">
                            <CheckCircle className="w-4 h-4 text-blue-600" />
                            <div>
                              <p className="text-slate-900 text-sm font-semibold">
                                ${formatCurrency(payment.amount)}
                              </p>
                              <p className="text-slate-400 text-xs">
                                {new Date(payment.date).toLocaleDateString(
                                  "en-US",
                                  {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  },
                                )}
                                {payment.paymentType && (
                                  <span className="ml-1 px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 text-[10px] uppercase font-medium">
                                    {payment.paymentType}
                                  </span>
                                )}
                                {payment.note && ` • ${payment.note}`}
                              </p>
                            </div>
                          </div>
                          <button
                            aria-label="Remove payment"
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Remove payment of $${formatCurrency(payment.amount)}?`,
                                )
                              ) {
                                handleRemovePayment(payment.id, "create");
                              }
                            }}
                            className="p-2 rounded-lg bg-red-500/10 text-red-600 hover:bg-red-100 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-400 text-xs text-center py-2">
                      No payments recorded yet
                    </p>
                  )}
                </div>
              </div>

              {/* Receipt Notes - Prominent Section */}
              <div className="bg-slate-50/50 rounded-xl p-5 border border-slate-300">
                <div className="flex items-start gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-blue-100">
                    <Bot className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-blue-600">
                      Receipt Notes
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      AI uses these notes to answer customer questions about
                      their order
                    </p>
                  </div>
                </div>
                <textarea
                  value={formData.internalNotes}
                  onChange={(e) =>
                    setFormData({ ...formData, internalNotes: e.target.value })
                  }
                  placeholder="Examples:&#10;• Sofa is in stock, table arriving Nov 16&#10;• Customer has narrow stairwell, may need extra help&#10;• Requested call 30 min before delivery&#10;• Previous order was a bed frame, very satisfied customer"
                  rows="4"
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors resize-none text-base"
                />
                <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                  <span className="text-blue-600">💡</span>
                  The AI uses these notes when chatting with customers about
                  delivery status, item availability, and order details
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div
              className="flex-shrink-0 bg-white p-4 sm:p-6 border-t-2 border-slate-200 flex items-center justify-end space-x-3"
              style={{
                paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)",
              }}
            >
              <button
                onClick={handleCloseModal}
                className="px-5 sm:px-6 py-2.5 rounded-lg font-bold bg-slate-100 border-2 border-slate-300 text-slate-600 hover:bg-slate-200 active:scale-[0.98] transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveReceipt}
                disabled={isSavingReceipt}
                className="flex items-center space-x-2 px-5 sm:px-6 py-2.5 rounded-lg font-bold transition-all bg-blue-600 hover:bg-blue-700 active:scale-[0.98] text-white disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
              >
                {isSavingReceipt ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    <span>
                      {editingReceipt ? "Update Receipt" : "Create Receipt"}
                    </span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
        </>
      )}

      {/* Receipt Detail & Chat — Drawer (desktop) / Full screen (mobile) */}
      {showReceiptModal && selectedReceipt && (
        <>
          <style>{`
            @keyframes receiptDrawerSlideIn {
              from { transform: translateX(100%); }
              to { transform: translateX(0); }
            }
            @keyframes receiptDrawerBackdropFade {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            .receipt-drawer-backdrop { animation: receiptDrawerBackdropFade 180ms ease-out; }
            .receipt-drawer-panel { animation: receiptDrawerSlideIn 240ms cubic-bezier(0.32, 0.72, 0, 1); }
          `}</style>
          <div
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-stretch sm:justify-end justify-center p-0 z-[100] receipt-drawer-backdrop"
            onClick={(e) => {
              // Backdrop click closes drawer (only when clicking the overlay itself,
              // not children). Mobile interior is full-bleed so this never fires there.
              if (e.target === e.currentTarget) {
                handleAttemptCloseReceiptModal();
              }
            }}
          >
          {/* ============================================
              MOBILE RECEIPT VIEW - Enterprise Design
          ============================================ */}
          {mobileV2 ? (
            <ReceiptDetailMobile
              dashboard={dashboard}
              renderMessageContent={renderMessageContent}
              formatMessageTime={formatMessageTime}
            />
          ) : (
          <div className="sm:hidden w-full h-full bg-white flex flex-col">
            {/* Status bar background */}
            <div
              className="flex-shrink-0 bg-white"
              style={{ height: "env(safe-area-inset-top)" }}
            />

            {/* Clean Mobile Header */}
            <div className="flex-shrink-0 bg-white border-b border-slate-300">
              {/* Top Row - Back & Actions */}
              <div className="px-4 pt-3 pb-3 flex items-center justify-between">
                <button
                  onClick={handleAttemptCloseReceiptModal}
                  className="flex items-center space-x-1.5 py-2 pr-3 text-slate-500 active:text-slate-900 transition-colors -ml-1"
                >
                  <ChevronLeft className="w-6 h-6" />
                  <span className="text-base font-medium">Orders</span>
                </button>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleShareReceipt(selectedReceipt)}
                    className="w-11 h-11 rounded-xl bg-slate-50 border border-slate-300 text-slate-500 flex items-center justify-center active:bg-slate-100 transition-colors"
                    title="Share Receipt"
                  >
                    <Share2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handlePrintReceipt(selectedReceipt)}
                    className="w-11 h-11 rounded-xl bg-slate-50 border border-slate-300 text-slate-500 flex items-center justify-center active:bg-slate-100 transition-colors"
                    title="Print Receipt"
                  >
                    <Printer className="w-5 h-5" />
                  </button>
                  <button
                    aria-label="Edit receipt"
                    onClick={() => {
                      handleCloseReceiptModal();
                      handleOpenModal(selectedReceipt);
                    }}
                    className="w-11 h-11 rounded-xl bg-slate-50 border border-slate-300 text-slate-500 flex items-center justify-center active:bg-slate-100 transition-colors"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Online order banner (website orders, not floor sales) */}
              {isOnlineOrder(selectedReceipt) && (
                <div className="mx-4 mb-3 px-4 py-2.5 rounded-xl bg-violet-600 text-white flex items-center gap-2">
                  <Globe className="w-5 h-5 flex-shrink-0" />
                  <span className="text-[14px] font-black uppercase tracking-wide truncate">
                    {onlineOrderLabel(selectedReceipt)}
                  </span>
                </div>
              )}

              {/* Customer Info Bar */}
              <div className="px-4 pb-4">
                <div className="flex items-center space-x-3">
                  {/* Avatar */}
                  <div className="w-12 h-12 rounded-xl bg-blue-100 border border-blue-400 flex items-center justify-center text-blue-600 font-semibold text-lg flex-shrink-0">
                    {(selectedReceipt.customerName ||
                      selectedReceipt.customer_name ||
                      "?")[0].toUpperCase()}
                  </div>

                  {/* Name & Phone */}
                  <div className="flex-1 min-w-0">
                    <h1 className="text-lg font-semibold text-slate-900 truncate">
                      {selectedReceipt.customerName ||
                        selectedReceipt.customer_name}
                    </h1>
                    <div className="flex items-center space-x-2 mt-0.5">
                      <span className="text-slate-500 text-sm">
                        {selectedReceipt.customerPhone ||
                          selectedReceipt.customer_phone ||
                          "No phone"}
                      </span>
                      {(selectedReceipt.smsConsent ||
                        selectedReceipt.sms_consent) && (
                        <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-600 text-[10px] font-medium">
                          SMS
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Total */}
                  <div className="text-right flex-shrink-0">
                    <p className="text-xl font-bold text-slate-900">
                      ${formatCurrency(selectedReceipt.total || 0)}
                    </p>
                    {(() => {
                      const paymentInfo = getPaymentStatus(selectedReceipt);
                      return (
                        <span
                          className={`text-xs font-medium ${
                            paymentInfo.status === "paid"
                              ? "text-emerald-600"
                              : paymentInfo.status === "partial"
                                ? "text-slate-500"
                                : "text-red-600"
                          }`}
                        >
                          {paymentInfo.status === "paid"
                            ? "Paid"
                            : paymentInfo.status === "partial"
                              ? `$${paymentInfo.balance.toFixed(0)} due`
                              : "Unpaid"}
                        </span>
                      );
                    })()}
                  </div>
                </div>

                {/* Status Badge */}
                <div className="mt-3">
                  <span
                    className={`inline-flex items-center space-x-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium ${
                      (selectedReceipt.deliveryStatus ||
                        selectedReceipt.delivery_status) === "delivered"
                        ? "bg-emerald-100 text-emerald-700"
                        : (selectedReceipt.deliveryStatus ||
                              selectedReceipt.delivery_status) ===
                            "out_for_delivery"
                          ? "bg-cyan-100 text-cyan-700"
                          : (selectedReceipt.deliveryStatus ||
                                selectedReceipt.delivery_status) ===
                              "partial_out_for_delivery"
                            ? "bg-indigo-100 text-indigo-700"
                            : (selectedReceipt.deliveryStatus ||
                                  selectedReceipt.delivery_status) ===
                                "partially_delivered"
                              ? "bg-amber-100 text-amber-700"
                              : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {getStatusIcon(
                      selectedReceipt.deliveryStatus ||
                        selectedReceipt.delivery_status,
                    )}
                    <span>
                      {deliveryStatuses.find(
                        (s) =>
                          s.value ===
                          (selectedReceipt.deliveryStatus ||
                            selectedReceipt.delivery_status),
                      )?.label || "Order Received"}
                    </span>
                  </span>
                </div>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto">
              {/* Contact Details Card */}
              <div className="px-4 py-3">
                <div className="rounded-xl bg-slate-50 border border-slate-300 overflow-hidden">
                  {/* Header Row */}
                  <div className="px-4 py-3 border-b border-slate-200">
                    <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Contact Details
                    </span>
                  </div>

                  {/* Details */}
                  <div className="p-4">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between py-2 border-b border-slate-200">
                        <span className="text-xs font-medium text-slate-400">
                          Phone
                        </span>
                        <a
                          href={`tel:${selectedReceipt.customerPhone || selectedReceipt.customer_phone}`}
                          className="text-sm text-slate-900"
                        >
                          {selectedReceipt.customerPhone ||
                            selectedReceipt.customer_phone || (
                              <span className="text-slate-400">—</span>
                            )}
                        </a>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b border-slate-200">
                        <span className="text-xs font-medium text-slate-400">
                          Email
                        </span>
                        <span className="text-sm text-slate-900 truncate ml-4 max-w-[180px]">
                          {selectedReceipt.customerEmail ||
                            selectedReceipt.customer_email || (
                              <span className="text-slate-400">—</span>
                            )}
                        </span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b border-slate-200">
                        <span className="text-xs font-medium text-slate-400">
                          Salesperson
                        </span>
                        <span className="text-sm text-slate-900">
                          {selectedReceipt.salesperson || (
                            <span className="text-slate-400">—</span>
                          )}
                        </span>
                      </div>
                      <div className="flex items-start justify-between py-2">
                        <span className="text-xs font-medium text-slate-400">
                          Address
                        </span>
                        <span className="text-sm text-slate-900 text-right ml-4 max-w-[200px]">
                          {selectedReceipt.deliveryAddress ||
                            selectedReceipt.delivery_address || (
                              <span className="text-slate-400">—</span>
                            )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Inventory Status - Clean Card */}
              {(() => {
                const items = selectedReceipt.items || [];
                const inventoryUnits = selectedReceipt.inventoryUnits || [];

                // FIX: Use getAllItemStatusesForReceipt to properly track claimed units
                const itemStatusMap = getAllItemStatusesForReceipt(
                  items,
                  inventoryUnits,
                  allInventoryUnits,
                  selectedReceipt?.itemStatuses || selectedReceipt?.item_statuses || [],
                );
                let arrivedCount = 0;
                let outForDeliveryCount = 0;
                let deliveredCount = 0;

                items.forEach((item) => {
                  const itemStatus = itemStatusMap.get(item.id) || {
                    status: "waiting",
                  };
                  if (itemStatus.status === "delivered") {
                    deliveredCount++;
                  } else if (itemStatus.status === "out_for_delivery") {
                    outForDeliveryCount++;
                  } else if (itemStatus.status === "ready") {
                    arrivedCount++;
                  }
                });

                const totalCount = items.length;
                const progressPercent =
                  totalCount > 0
                    ? ((arrivedCount + outForDeliveryCount + deliveredCount) /
                        totalCount) *
                      100
                    : 0;
                const allArrived =
                  arrivedCount === totalCount && totalCount > 0;
                const allOutForDelivery =
                  outForDeliveryCount === totalCount && totalCount > 0;
                const isPartialOutForDelivery =
                  outForDeliveryCount > 0 && outForDeliveryCount < totalCount;

                // Determine display mode
                let bgClass, iconClass, icon, label;
                if (allOutForDelivery) {
                  bgClass = "bg-blue-600/10";
                  iconClass = "text-blue-600";
                  icon = <Truck className="w-4 h-4 text-blue-600" />;
                  label = "Out for Delivery";
                } else if (isPartialOutForDelivery) {
                  bgClass = "bg-blue-500/10";
                  iconClass = "text-blue-600";
                  icon = <Truck className="w-4 h-4 text-blue-600" />;
                  label = `${outForDeliveryCount} on truck, ${arrivedCount} ready`;
                } else if (allArrived) {
                  bgClass = "bg-emerald-50";
                  iconClass = "text-emerald-600";
                  icon = <CheckCircle className="w-4 h-4 text-emerald-600" />;
                  label = "Ready to Deliver";
                } else {
                  bgClass = "bg-slate-100";
                  iconClass = "text-slate-500";
                  icon = <Clock className="w-4 h-4 text-slate-500" />;
                  label = `${arrivedCount + outForDeliveryCount}/${totalCount} Items Ready`;
                }

                return (
                  <div className="px-4 pb-3">
                    <div
                      className={`p-4 rounded-xl ${bgClass} border border-slate-200`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          {icon}
                          <span className={`font-medium text-sm ${iconClass}`}>
                            {label}
                          </span>
                        </div>
                        <span className={`text-lg font-semibold ${iconClass}`}>
                          {Math.round(progressPercent)}%
                        </span>
                      </div>
                      <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            allOutForDelivery
                              ? "bg-blue-600"
                              : isPartialOutForDelivery
                                ? "bg-blue-500"
                                : allArrived
                                  ? "bg-green-500"
                                  : "bg-slate-500"
                          }`}
                          style={{ width: `${progressPercent}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Order Items */}
              <div className="px-4 pb-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Order Items
                  </span>
                  <span className="text-xs text-slate-400 flex items-center gap-1.5">
                    {isLoadingReceiptDetails && (
                      <RefreshCw className="w-3 h-3 animate-spin text-slate-300" />
                    )}
                    {(selectedReceipt.items || []).length} items
                  </span>
                </div>
                {!isEditingInModal && (
                  <>
                    <FulfillmentSummary
                      items={selectedReceipt.items || []}
                      statusMap={getAllItemStatusesForReceipt(
                        selectedReceipt.items || [],
                        selectedReceipt.inventoryUnits || [],
                        allInventoryUnits,
                        selectedReceipt?.itemStatuses ||
                          selectedReceipt?.item_statuses ||
                          [],
                      )}
                    />
                  </>
                )}
                <div className="space-y-2">
                  {/* Skeleton rows for the rare open-by-id path (push tap /
                      deep link) where no seed items exist yet — the template
                      appears instantly, real rows replace it on hydrate. */}
                  {isLoadingReceiptDetails &&
                    (selectedReceipt.items || []).length === 0 && (
                      <>
                        <div className="h-[72px] rounded-xl border border-slate-200 bg-slate-50 animate-pulse" />
                        <div className="h-[72px] rounded-xl border border-slate-200 bg-slate-50 animate-pulse" />
                      </>
                    )}
                  {(() => {
                    // FIX: Compute all statuses once with proper unit tracking
                    const itemStatusMap = getAllItemStatusesForReceipt(
                      selectedReceipt.items || [],
                      selectedReceipt.inventoryUnits,
                      allInventoryUnits,
                      selectedReceipt?.itemStatuses || selectedReceipt?.item_statuses || [],
                    );

                    const allItems = selectedReceipt.items || [];

                    // NEW: Group items by SET first, then by SKU for non-set items
                    // This creates a structure: [ { type: 'set', setId, setName, items }, { type: 'sku', sku, items }, { type: 'single', item } ]
                    const groupItemsForReceiptView = (items) => {
                      const groups = [];
                      const processedIndices = new Set();

                      // First pass: collect all sets
                      const setGroups = {};
                      items.forEach((item, index) => {
                        const setId = item.set_id || item.setId;
                        if (setId) {
                          if (!setGroups[setId]) {
                            setGroups[setId] = {
                              type: "set",
                              setId: setId,
                              setName: item.set_name || item.setName || "Set",
                              items: [],
                            };
                          }
                          setGroups[setId].items.push({
                            ...item,
                            originalIndex: index,
                          });
                          processedIndices.add(index);
                        }
                      });

                      // Add set groups to result
                      Object.values(setGroups).forEach((setGroup) => {
                        groups.push(setGroup);
                      });

                      // Second pass: group remaining items by SKU
                      const skuGroups = {};
                      items.forEach((item, index) => {
                        if (processedIndices.has(index)) return; // Skip set items

                        const sku =
                          (item.sku || "").toUpperCase().trim() ||
                          `NO-SKU-${index}`;
                        if (!skuGroups[sku]) {
                          skuGroups[sku] = [];
                        }
                        skuGroups[sku].push({ ...item, originalIndex: index });
                      });

                      // Add SKU groups to result
                      Object.entries(skuGroups).forEach(([sku, skuItems]) => {
                        if (skuItems.length === 1) {
                          groups.push({ type: "single", item: skuItems[0] });
                        } else {
                          groups.push({ type: "sku", sku, items: skuItems });
                        }
                      });

                      return groups;
                    };

                    const displayGroups = groupItemsForReceiptView(allItems);

                    // Bulk action helpers for set/SKU group headers (mobile)
                    const bulkMarkReady = async (items, e) => {
                      e.stopPropagation();
                      const toMark = items.filter((it) => {
                        const s =
                          itemStatusMap.get(it.id)?.status || "waiting";
                        return s === "waiting";
                      });
                      if (toMark.length === 0) return;
                      if (
                        !window.confirm(
                          `Mark ${toMark.length} waiting item${toMark.length === 1 ? "" : "s"} as Ready? This will reserve inventory for each.`,
                        )
                      )
                        return;
                      for (const it of toMark) {
                        await handleMarkItemReady(selectedReceipt.id, it);
                      }
                    };
                    const bulkMarkStatus = async (items, newStatus, e) => {
                      e.stopPropagation();
                      const toMark = items.filter((it) => {
                        const s =
                          itemStatusMap.get(it.id)?.status || "waiting";
                        return (
                          s === "ready" ||
                          s === "out_for_delivery" ||
                          s === "partial"
                        );
                      });
                      if (toMark.length === 0) return;
                      const label =
                        newStatus === "picked_up" ? "Picked Up" : "Delivered";
                      if (
                        !window.confirm(
                          `Mark ${toMark.length} item${toMark.length === 1 ? "" : "s"} as ${label}?`,
                        )
                      )
                        return;
                      for (const it of toMark) {
                        await handleMarkUnitStatus(
                          selectedReceipt.id,
                          it,
                          newStatus,
                          { skipConfirm: true },
                        );
                      }
                    };

                    // Render single item card (used for both standalone and expanded drawer items)
                    const renderMobileItemCard = (item, isNested = false) => {
                      const itemStatus = itemStatusMap.get(item.id) || {
                        status: "waiting",
                        delivered: 0,
                        ready: 0,
                        waiting: 1,
                        outForDelivery: 0,
                      };
                      const qty = item.quantity || 1;

                      // Determine display status
                      const isFullyDelivered = itemStatus.delivered >= qty;
                      // A picked-up item also satisfies delivered>=qty, so distinguish
                      // by the authoritative status field (single source of truth).
                      const isPickedUp = itemStatus.status === "picked_up";
                      const isFullyReady =
                        !isFullyDelivered &&
                        itemStatus.delivered + itemStatus.ready >= qty;
                      const isPartialReady =
                        !isFullyDelivered &&
                        !isFullyReady &&
                        itemStatus.ready > 0;
                      const isOutForDelivery = itemStatus.outForDelivery > 0;

                      // For selection logic
                      const isReady = itemStatus.ready > 0;
                      const isDelivered = isFullyDelivered;
                      const isSelected =
                        selectedItemsForDelivery[item.id] || false;
                      const canSelect =
                        isReady && !isFullyDelivered && !isOutForDelivery;

                      return (
                        <FulfillmentItemRow
                          key={item.id || item.originalIndex}
                          item={item}
                          inventoryStatus={itemStatus}
                          isSelected={isSelected}
                          canSelect={canSelect}
                          isNested={isNested}
                          manufacturerName={(() => {
                            const mfrId =
                              item.manufacturerId || item.manufacturer_id;
                            const mfr =
                              mfrId &&
                              manufacturers.find((m) => m.id === mfrId);
                            return mfr ? mfr.name : null;
                          })()}
                          busyReady={markingItemReady}
                          busyStatus={itemStatusBusy[item.id] || null}
                          busyUndoId={isUndoingDelivery}
                          onToggleSelect={() =>
                            toggleItemForDelivery(item.id)
                          }
                          onMarkReady={() =>
                            handleMarkItemReady(selectedReceipt.id, item)
                          }
                          onMarkStatus={(status) =>
                            handleMarkUnitStatus(
                              selectedReceipt.id,
                              item,
                              status,
                            )
                          }
                          onUndoReady={() =>
                            handleUndoReady(selectedReceipt.id, item)
                          }
                          onUndoDelivery={() =>
                            handleUndoDelivery(item, selectedReceipt)
                          }
                        />
                      );
                    };

                    return displayGroups.map((group, groupIndex) => {
                      // RENDER SET GROUP
                      if (group.type === "set") {
                        const isExpanded =
                          expandedSkuGroups[`set-${group.setId}`] ?? false; // Default collapsed
                        const totalPrice = group.items.reduce(
                          (sum, item) => sum + (item.price || 0),
                          0,
                        );

                        // Calculate aggregate status for the set
                        const statusCounts = group.items.reduce((acc, item) => {
                          const status =
                            itemStatusMap.get(item.id)?.status || "waiting";
                          acc[status] = (acc[status] || 0) + 1;
                          return acc;
                        }, {});

                        const allDelivered =
                          statusCounts.delivered === group.items.length;
                        const allReady =
                          statusCounts.ready === group.items.length;
                        const allOutForDelivery =
                          statusCounts.out_for_delivery === group.items.length;
                        const hasReady = statusCounts.ready > 0;
                        const hasDelivered = statusCounts.delivered > 0;
                        const hasOutForDelivery =
                          statusCounts.out_for_delivery > 0;

                        return (
                          <div
                            key={`set-${group.setId}`}
                            className="rounded-xl border border-blue-400 overflow-hidden"
                          >
                            {/* Set Header - Always visible */}
                            <button
                              data-tip="Shows or hides the items in this set"
                              onClick={() =>
                                setExpandedSkuGroups((prev) => ({
                                  ...prev,
                                  [`set-${group.setId}`]: !(
                                    prev[`set-${group.setId}`] ?? false
                                  ),
                                }))
                              }
                              className={`w-full p-3 flex items-center justify-between transition-all ${
                                allDelivered
                                  ? "bg-emerald-500/10"
                                  : allOutForDelivery
                                    ? "bg-slate-50"
                                    : allReady
                                      ? "bg-emerald-50"
                                      : hasReady || hasDelivered
                                        ? "bg-slate-50"
                                        : "bg-slate-50"
                              }`}
                            >
                              <div className="flex items-center space-x-3 min-w-0 flex-1">
                                <div
                                  className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                    allDelivered
                                      ? "bg-emerald-500/20"
                                      : allOutForDelivery
                                        ? "bg-blue-100"
                                        : allReady
                                          ? "bg-emerald-100"
                                          : "bg-blue-100"
                                  }`}
                                >
                                  <Layers
                                    className={`w-4 h-4 ${
                                      allDelivered
                                        ? "text-emerald-400"
                                        : allOutForDelivery
                                          ? "text-blue-600"
                                          : allReady
                                            ? "text-emerald-600"
                                            : "text-blue-600"
                                    }`}
                                  />
                                </div>
                                <div className="text-left min-w-0 flex-1">
                                  <div className="flex items-start justify-between gap-2">
                                    <span className="text-slate-600 font-semibold text-sm leading-tight">
                                      {group.setName}
                                    </span>
                                    <span className="font-bold text-slate-900 text-sm whitespace-nowrap">
                                      ${formatCurrency(totalPrice)}
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between mt-1">
                                    <div className="flex items-center gap-2">
                                      <span className="px-2 py-0.5 rounded-full bg-blue-100 text-slate-600 text-xs font-medium whitespace-nowrap">
                                        {group.items.length} pieces
                                      </span>
                                      <span className="text-xs text-slate-400">
                                        {allDelivered
                                          ? "All delivered"
                                          : allReady
                                            ? "All ready for delivery"
                                            : hasReady
                                              ? `${statusCounts.ready || 0}/${group.items.length} ready`
                                              : "Waiting on items"}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      {hasDelivered && (
                                        <span className="text-xs text-emerald-400">
                                          {statusCounts.delivered}✓
                                        </span>
                                      )}
                                      {hasOutForDelivery && (
                                        <span className="text-xs text-blue-600">
                                          {statusCounts.out_for_delivery}🚚
                                        </span>
                                      )}
                                      {hasReady && !hasDelivered && (
                                        <span className="text-xs text-emerald-600">
                                          {statusCounts.ready}✓
                                        </span>
                                      )}
                                      {statusCounts.waiting > 0 && (
                                        <span className="text-xs text-slate-500">
                                          {statusCounts.waiting}⏳
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <ChevronDown
                                className={`w-5 h-5 text-blue-600 transition-transform duration-200 flex-shrink-0 ${isExpanded ? "rotate-180" : ""}`}
                              />
                            </button>



                            {/* Set Content - Expanded items */}
                            <div
                              className={`overflow-hidden transition-all duration-200 ease-in-out ${
                                isExpanded
                                  ? "max-h-[2000px] opacity-100"
                                  : "max-h-0 opacity-0"
                              }`}
                            >
                              <div className="p-2 space-y-2 bg-slate-50/50 border-t border-slate-300">
                                {group.items.map((item, idx) => (
                                  <div key={item.id || idx}>
                                    {renderMobileItemCard(item, true)}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      }

                      // RENDER SINGLE ITEM (not in a set, unique SKU)
                      if (group.type === "single") {
                        return renderMobileItemCard(group.item, false);
                      }

                      // RENDER SKU GROUP (multiple items with same SKU, not in a set)
                      if (group.type === "sku") {
                        const items = group.items;
                        const sku = group.sku;
                        const isExpanded = expandedSkuGroups[sku] || false;
                        const firstItem = items[0];
                        const totalPrice = items.reduce(
                          (sum, item) => sum + (item.price || 0),
                          0,
                        );

                        // Calculate aggregate status for the group
                        const statusCounts = items.reduce((acc, item) => {
                          const status =
                            itemStatusMap.get(item.id)?.status || "waiting";
                          acc[status] = (acc[status] || 0) + 1;
                          return acc;
                        }, {});

                        const allDelivered =
                          statusCounts.delivered === items.length;
                        const allReady = statusCounts.ready === items.length;
                        const allOutForDelivery =
                          statusCounts.out_for_delivery === items.length;
                        const hasReady = statusCounts.ready > 0;
                        const hasDelivered = statusCounts.delivered > 0;
                        const hasOutForDelivery =
                          statusCounts.out_for_delivery > 0;

                        return (
                          <div
                            key={sku}
                            className="rounded-xl border border-slate-300 overflow-hidden"
                          >
                            {/* Drawer Header - Always visible */}
                            <button
                              data-tip="Shows or hides the units for this item"
                              onClick={() =>
                                setExpandedSkuGroups((prev) => ({
                                  ...prev,
                                  [sku]: !prev[sku],
                                }))
                              }
                              className={`w-full p-3 flex items-center justify-between transition-all ${
                                allDelivered
                                  ? "bg-emerald-500/10"
                                  : allOutForDelivery
                                    ? "bg-slate-50"
                                    : allReady
                                      ? "bg-emerald-500/5"
                                      : "bg-white/[0.03]"
                              }`}
                            >
                              <div className="flex items-center space-x-3">
                                <div
                                  className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                    allDelivered
                                      ? "bg-emerald-500/20"
                                      : allOutForDelivery
                                        ? "bg-blue-100"
                                        : allReady
                                          ? "bg-emerald-500/20"
                                          : hasReady || hasDelivered
                                            ? "bg-blue-600/20"
                                            : "bg-slate-200"
                                  }`}
                                >
                                  <Package
                                    className={`w-4 h-4 ${
                                      allDelivered
                                        ? "text-emerald-400"
                                        : allOutForDelivery
                                          ? "text-blue-600"
                                          : allReady
                                            ? "text-emerald-400"
                                            : hasReady || hasDelivered
                                              ? "text-blue-600"
                                              : "text-slate-500"
                                    }`}
                                  />
                                </div>
                                <div className="text-left">
                                  <div className="flex items-center space-x-2">
                                    <span className="text-blue-600 font-mono font-semibold text-sm">
                                      {sku}
                                    </span>
                                    <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-medium">
                                      {items.length} items
                                    </span>
                                  </div>
                                  <p className="text-xs text-slate-400 mt-0.5">
                                    {firstItem.name || firstItem.item_name}
                                    {firstItem.color && (
                                      <span className="ml-1">
                                        • {firstItem.color}
                                      </span>
                                    )}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-3">
                                <div className="text-right">
                                  <p className="font-bold text-slate-900 text-sm">
                                    ${formatCurrency(totalPrice)}
                                  </p>
                                  <div className="flex items-center justify-end space-x-1 mt-0.5">
                                    {hasDelivered && (
                                      <span className="text-xs text-emerald-400">
                                        {statusCounts.delivered}✓
                                      </span>
                                    )}
                                    {hasOutForDelivery && (
                                      <span className="text-xs text-blue-600">
                                        {statusCounts.out_for_delivery}🚚
                                      </span>
                                    )}
                                    {hasReady && (
                                      <span className="text-xs text-emerald-400">
                                        {statusCounts.ready}✓
                                      </span>
                                    )}
                                    {statusCounts.waiting > 0 && (
                                      <span className="text-xs text-slate-500">
                                        {statusCounts.waiting}⏳
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <ChevronDown
                                  className={`w-5 h-5 text-slate-500 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                                />
                              </div>
                            </button>



                            {/* Drawer Content - Expanded items */}
                            <div
                              className={`overflow-hidden transition-all duration-200 ease-in-out ${
                                isExpanded
                                  ? "max-h-[2000px] opacity-100"
                                  : "max-h-0 opacity-0"
                              }`}
                            >
                              <div className="p-2 space-y-2 bg-slate-100 border-t border-slate-200">
                                {items.map((item, idx) => (
                                  <div key={item.id || idx}>
                                    {renderMobileItemCard(item, true)}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      }

                      return null;
                    });
                  })()}
                </div>

                {/* Bulk fulfillment bar — sticky inside the drawer scroll area.
                    Replaces the old below-the-fold "Create Delivery Order" panel. */}
                {!isEditingInModal && (
                  <BulkActionBar
                    items={selectedReceipt.items || []}
                    statusMap={getAllItemStatusesForReceipt(
                      selectedReceipt.items || [],
                      selectedReceipt.inventoryUnits || [],
                      allInventoryUnits,
                      selectedReceipt?.itemStatuses ||
                        selectedReceipt?.item_statuses ||
                        [],
                    )}
                    selected={selectedItemsForDelivery}
                    canRoute={tabVisibility.deliveries}
                    isCreatingRoute={isCreatingDeliveryFromReceipt}
                    onClear={clearSelectedItemsForDelivery}
                    onCreateRoute={handleCreateDeliveryFromReceipt}
                    onMarkReadyItem={(it) =>
                      handleMarkItemReady(selectedReceipt.id, it)
                    }
                    onMarkStatusItem={(it, status) =>
                      handleMarkUnitStatus(selectedReceipt.id, it, status, {
                        skipConfirm: true,
                      })
                    }
                  />
                )}
              </div>

              {/* Order Summary */}
              <div className="px-4 pb-3">
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-300">
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Summary
                  </span>
                  <div className="mt-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Subtotal</span>
                      <span className="text-slate-900">
                        ${formatCurrency(selectedReceipt.subtotal)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">
                        Tax ({storeSettings.tax_rate || 8}%)
                      </span>
                      <span className="text-slate-900">
                        ${formatCurrency(selectedReceipt.tax)}
                      </span>
                    </div>
                    <div className="pt-2 mt-2 border-t border-slate-300 flex justify-between">
                      <span className="text-slate-900 font-medium">Total</span>
                      <span className="text-lg font-semibold text-slate-900">
                        ${formatCurrency(selectedReceipt.total || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Status */}
              <div className="px-4 pb-3">
                {(() => {
                  const paymentInfo = getPaymentStatus(selectedReceipt);
                  const amountPaid =
                    parseFloat(
                      selectedReceipt.amount_paid || selectedReceipt.amountPaid,
                    ) || 0;
                  const total = parseFloat(selectedReceipt.total) || 0;

                  return (
                    <div
                      className={`p-4 rounded-xl border ${
                        paymentInfo.status === "paid"
                          ? "bg-emerald-500/10 border-emerald-500/30"
                          : paymentInfo.status === "partial"
                            ? "bg-slate-100 border-slate-300"
                            : "bg-red-500/10 border-red-500/30"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {paymentInfo.status === "paid" ? (
                            <CheckCircle className="w-5 h-5 text-emerald-400" />
                          ) : (
                            <CreditCard className="w-5 h-5 text-slate-500" />
                          )}
                          <span
                            className={`font-semibold ${
                              paymentInfo.status === "paid"
                                ? "text-emerald-400"
                                : paymentInfo.status === "partial"
                                  ? "text-slate-500"
                                  : "text-red-600"
                            }`}
                          >
                            {paymentInfo.label}
                          </span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-slate-400 text-xs">Amount Paid</p>
                          <p className="text-slate-900 font-semibold">
                            ${formatCurrency(amountPaid)}
                          </p>
                        </div>
                        <div>
                          <p className="text-slate-400 text-xs">Balance Owed</p>
                          <p
                            className={`font-semibold ${
                              paymentInfo.status === "paid"
                                ? "text-emerald-400"
                                : "text-slate-500"
                            }`}
                          >
                            ${formatCurrency(paymentInfo.balance)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Payment History */}
              {(() => {
                const paymentHistory =
                  selectedReceipt.payment_history ||
                  selectedReceipt.paymentHistory ||
                  [];
                if (paymentHistory.length === 0) return null;
                return (
                  <div className="px-4 pb-3">
                    <div className="p-3 rounded-xl bg-slate-50 border border-slate-300">
                      <p className="text-xs text-slate-600 font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5">
                        <List className="w-3.5 h-3.5" />
                        Payment History
                      </p>
                      <div className="space-y-2">
                        {paymentHistory.map((payment, idx) => (
                          <div
                            key={payment.id || idx}
                            className="flex items-center justify-between py-2 border-b border-emerald-500/10 last:border-0"
                          >
                            <div>
                              <p className="text-slate-900 text-sm font-medium">
                                ${formatCurrency(payment.amount)}
                              </p>
                              <p className="text-slate-400 text-xs">
                                {new Date(payment.date).toLocaleDateString(
                                  "en-US",
                                  {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  },
                                )}
                                {payment.note && ` • ${payment.note}`}
                              </p>
                            </div>
                            <CheckCircle className="w-4 h-4 text-emerald-400" />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Timeline */}
              <div className="px-4 pb-3">
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Timeline
                </span>
                <div className="mt-2 grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-xl bg-slate-50 border border-slate-300">
                    <p className="text-xs text-slate-400 mb-1">Created</p>
                    <p className="text-slate-900 text-sm font-medium">
                      {selectedReceipt.createdAt || selectedReceipt.created_at
                        ? new Date(
                            selectedReceipt.createdAt ||
                              selectedReceipt.created_at,
                          ).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })
                        : "—"}
                    </p>
                  </div>
                  <div className="p-3 rounded-xl bg-slate-50 border border-slate-300">
                    <p className="text-xs text-slate-400 mb-1">
                      Expected Delivery
                    </p>
                    <p className="text-slate-900 text-sm font-medium">
                      {selectedReceipt.deliveryDate || "TBD"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Receipt SMS Status */}
              <div className="px-4 pb-3">
                <div
                  className={`p-4 rounded-xl border ${
                    selectedReceipt.sentAt || selectedReceipt.sent_at
                      ? "bg-emerald-500/10 border-emerald-500/30"
                      : "bg-gray-500/10 border-gray-500/20"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      {selectedReceipt.sentAt || selectedReceipt.sent_at ? (
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <FileText className="w-4 h-4 text-slate-500" />
                      )}
                      <div>
                        <p
                          className={`text-sm font-semibold ${
                            selectedReceipt.sentAt || selectedReceipt.sent_at
                              ? "text-blue-600"
                              : "text-slate-500"
                          }`}
                        >
                          {selectedReceipt.sentAt || selectedReceipt.sent_at
                            ? "Receipt Sent"
                            : "Receipt Not Sent"}
                        </p>
                        {(selectedReceipt.sentAt ||
                          selectedReceipt.sent_at) && (
                          <p className="text-xs text-slate-400">
                            {new Date(
                              selectedReceipt.sent_at || selectedReceipt.sentAt,
                            ).toLocaleString("en-US", {
                              month: "short",
                              day: "numeric",
                              hour: "numeric",
                              minute: "2-digit",
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      data-tip={selectedReceipt.sentAt || selectedReceipt.sent_at ? "Texts the customer their receipt link again" : "Texts the customer their receipt link"}
                      onClick={() => handleSendSMS(selectedReceipt)}
                      disabled={sendingSMSReceiptId === selectedReceipt.id}
                      className={`flex items-center space-x-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed ${
                        smsSentReceipts[selectedReceipt.id]
                          ? "bg-blue-100 border border-emerald-500/40 text-emerald-400"
                          : selectedReceipt.sentAt || selectedReceipt.sent_at
                            ? "bg-blue-500/20 border border-blue-500/30 text-blue-600 active:bg-blue-500/30"
                            : "bg-blue-50 border border-blue-300 text-emerald-400 active:bg-blue-200"
                      }`}
                    >
                      {sendingSMSReceiptId === selectedReceipt.id ? (
                        <>
                          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                          <span>Sending...</span>
                        </>
                      ) : smsSentReceipts[selectedReceipt.id] ? (
                        <>
                          <CheckCircle className="w-3.5 h-3.5" />
                          <span>Sent ✓</span>
                        </>
                      ) : selectedReceipt.sentAt || selectedReceipt.sent_at ? (
                        <>
                          <RotateCcw className="w-3.5 h-3.5" />
                          <span>Resend SMS</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-3.5 h-3.5" />
                          <span>Send SMS</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Delivery Preferences */}
              {(selectedReceipt.customerPreferences?.preferredDeliveryDay ||
                selectedReceipt.customerPreferences?.preferredTimeWindow ||
                selectedReceipt.customerPreferences?.notes) && (
                <div className="px-4 pb-3">
                  <div className="p-3 rounded-xl bg-slate-50 border border-slate-300">
                    <p className="text-xs text-blue-600 font-semibold mb-2">
                      🚚 Delivery Preferences
                    </p>
                    <div className="space-y-1 text-sm">
                      {(selectedReceipt.customerPreferences
                        ?.preferredDeliveryDay ||
                        selectedReceipt.customerPreferences
                          ?.preferredTimeWindow) && (
                        <p className="text-slate-600">
                          {
                            selectedReceipt.customerPreferences
                              .preferredDeliveryDay
                          }
                          {selectedReceipt.customerPreferences
                            .preferredDeliveryDay &&
                            selectedReceipt.customerPreferences
                              .preferredTimeWindow &&
                            " • "}
                          {
                            selectedReceipt.customerPreferences
                              .preferredTimeWindow
                          }
                        </p>
                      )}
                      {selectedReceipt.customerPreferences?.notes && (
                        <p className="text-slate-500 text-xs">
                          {selectedReceipt.customerPreferences.notes}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Receipt Notes */}
              {(selectedReceipt.internalNotes ||
                selectedReceipt.internal_notes) && (
                <div className="px-4 pb-3">
                  <div className="p-3 rounded-xl bg-slate-50 border border-slate-300">
                    <div className="flex items-center space-x-1.5 mb-2">
                      <Bot className="w-3.5 h-3.5 text-blue-600" />
                      <p className="text-xs text-blue-600 font-semibold">
                        Receipt Notes
                      </p>
                      <span className="text-[10px] text-slate-400">
                        (AI uses for customer questions)
                      </span>
                    </div>
                    <p className="text-slate-600 text-sm">
                      {selectedReceipt.internalNotes ||
                        selectedReceipt.internal_notes}
                    </p>
                  </div>
                </div>
              )}

              {/* Delivery Signatures Button - Compact */}
              {(selectedReceipt.delivery_signature ||
                selectedReceipt.deliverySignature) && (
                <div className="px-4 pb-3">
                  <button
                    data-tip="Opens the delivery signatures captured for this order"
                    onClick={() => openSignaturesDrawer(selectedReceipt)}
                    className="w-full p-4 rounded-xl bg-slate-50 border border-slate-300 hover:bg-slate-100 transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-300 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="text-left">
                          <p className="text-sm font-semibold text-slate-900">
                            Delivery Signatures
                          </p>
                          <p className="text-xs text-blue-600">
                            ✓ Customer confirmed delivery
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </button>
                </div>
              )}

              {/* Customer Satisfaction Survey - Mobile */}
              {(selectedReceipt?.delivery_status === "delivered" ||
                selectedReceipt?.deliveryStatus === "delivered") && (
                <div className="px-4 pb-3">
                  <SurveyFeedbackDisplay receipt={selectedReceipt} />
                </div>
              )}

              {/* Bottom Spacer for Fixed Button */}
              <div className="h-36" />
            </div>

            {/* Fixed Bottom Action Bar */}
            <div
              className="flex-shrink-0 fixed bottom-0 left-0 right-0 bg-white border-t border-slate-300 px-4 pt-3"
              style={{
                paddingBottom: "calc(env(safe-area-inset-bottom) + 0.75rem)",
              }}
            >
              {/* AI Status */}
              <div
                className={`mb-3 px-3 py-2 rounded-lg flex items-center justify-center space-x-2 ${
                  isStaffMode
                    ? "bg-emerald-50 border border-emerald-200"
                    : "bg-blue-50 border border-blue-200"
                }`}
              >
                <div
                  className={`w-1.5 h-1.5 rounded-full ${isStaffMode ? "bg-emerald-500" : "bg-blue-500"}`}
                />
                <span
                  className={`text-xs font-medium ${isStaffMode ? "text-emerald-700" : "text-blue-700"}`}
                >
                  {isStaffMode ? "You are responding" : "AI handling chat"}
                </span>
              </div>

              {/* Chat Button - Brand Gradient */}
              <button
                data-tip="Opens the text conversation with this customer"
                onClick={() => setIsMobileChatOpen(true)}
                className="w-full flex items-center justify-center space-x-2 px-6 py-3.5 rounded-xl bg-blue-600 text-white font-medium transition-colors active:opacity-90"
              >
                <MessageSquare className="w-5 h-5" />
                <span>Customer Chat</span>
                {selectedReceipt.messages &&
                  selectedReceipt.messages.filter(
                    (m) => m.message_type !== "survey",
                  ).length > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-white/20 text-sm font-medium">
                      {
                        selectedReceipt.messages.filter(
                          (m) => m.message_type !== "survey",
                        ).length
                      }
                    </span>
                  )}
              </button>
            </div>
          </div>
          )}

          {/* ============================================
              DESKTOP RECEIPT VIEW - Unified Experience
          ============================================ */}
          <div className="hidden sm:flex relative bg-white border-l border-slate-300 w-full sm:w-[88%] lg:w-[80%] xl:w-[74%] 2xl:max-w-[1500px] h-screen overflow-hidden flex-col shadow-2xl receipt-drawer-panel">
            {/* ===== ONLINE ORDER BANNER (website orders, not floor sales) ===== */}
            {isOnlineOrder(selectedReceipt) && (
              <div className="flex-shrink-0 px-6 py-2.5 bg-violet-600 text-white flex items-center gap-2.5">
                <Globe className="w-5 h-5 flex-shrink-0" />
                <span className="text-[15px] font-black uppercase tracking-wide">
                  {onlineOrderLabel(selectedReceipt)}
                </span>
                <span className="text-[13px] text-violet-100 font-medium">
                  · placed by the customer on the website
                </span>
              </div>
            )}

            {/* ===== UNIFIED HEADER ===== */}
            <div className="flex-shrink-0 px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                {/* Left: Customer Info */}
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-lg">
                    {(selectedReceipt.customerName ||
                      selectedReceipt.customer_name ||
                      "?")[0].toUpperCase()}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">
                      {isEditingInModal && editFormData ? (
                        <input
                          type="text"
                          value={editFormData.customerName}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              customerName: e.target.value,
                            })
                          }
                          className="bg-transparent border-b-2 border-blue-500 outline-none text-slate-900 font-bold text-xl w-64"
                        />
                      ) : (
                        selectedReceipt.customerName ||
                        selectedReceipt.customer_name
                      )}
                    </h2>
                    <div className="flex items-center space-x-3 mt-1">
                      {isEditingInModal && editFormData ? (
                        <>
                          <input
                            type="tel"
                            autoComplete="off"
                            value={editFormData.customerPhone}
                            onChange={(e) =>
                              setEditFormData({
                                ...editFormData,
                                customerPhone: formatPhoneNumber(
                                  e.target.value,
                                ),
                              })
                            }
                            placeholder="(916) 555-0123"
                            className="bg-transparent border-b border-gray-500 outline-none text-slate-600 text-sm w-36"
                          />
                          {/* SMS Consent Checkbox */}
                          <label className="flex items-center gap-1.5 cursor-pointer group">
                            <input
                              type="checkbox"
                              checked={editFormData.smsConsent || false}
                              onChange={(e) =>
                                setEditFormData({
                                  ...editFormData,
                                  smsConsent: e.target.checked,
                                })
                              }
                              className="w-3.5 h-3.5 rounded border-slate-400 bg-slate-200 text-blue-500 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
                            />
                            <span className="text-xs text-slate-400 group-hover:text-slate-500">
                              SMS consent
                            </span>
                          </label>
                        </>
                      ) : (
                        <>
                          <a
                            href={`tel:${selectedReceipt.customerPhone || selectedReceipt.customer_phone}`}
                            className="text-blue-600 text-sm hover:underline"
                          >
                            {selectedReceipt.customerPhone ||
                              selectedReceipt.customer_phone || (
                                <span className="text-slate-400 italic">
                                  No phone
                                </span>
                              )}
                          </a>
                          {(selectedReceipt.smsConsent ||
                            selectedReceipt.sms_consent) && (
                            <span className="text-xs bg-emerald-100 text-emerald-600 px-2 py-0.5 rounded">
                              SMS ✓
                            </span>
                          )}
                        </>
                      )}
                      {(selectedReceipt.customerEmail ||
                        selectedReceipt.customer_email) && (
                        <>
                          <span className="text-gray-600">•</span>
                          <a
                            href={`mailto:${selectedReceipt.customerEmail || selectedReceipt.customer_email}`}
                            className="text-slate-500 text-sm hover:text-blue-600"
                          >
                            {selectedReceipt.customerEmail ||
                              selectedReceipt.customer_email}
                          </a>
                        </>
                      )}
                      <span className="text-gray-600">•</span>
                      <span className="text-slate-400 text-sm">
                        Created{" "}
                        {selectedReceipt.createdAt || selectedReceipt.created_at
                          ? new Date(
                              selectedReceipt.createdAt ||
                                selectedReceipt.created_at,
                            ).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                            })
                          : "N/A"}
                      </span>
                    </div>
                    {(selectedReceipt.deliveryAddress ||
                      selectedReceipt.delivery_address) && (
                      <p className="text-slate-500 text-sm mt-1">
                        📍{" "}
                        {selectedReceipt.deliveryAddress ||
                          selectedReceipt.delivery_address}
                      </p>
                    )}
                    {(selectedReceipt.salesperson ||
                      (isEditingInModal && editFormData)) && (
                      <p className="text-slate-400 text-sm mt-1">
                        {isEditingInModal && editFormData ? (
                          <span className="flex items-center gap-2">
                            <span>Sold by:</span>
                            <input
                              type="text"
                              value={editFormData.salesperson || ""}
                              onChange={(e) =>
                                setEditFormData({
                                  ...editFormData,
                                  salesperson: e.target.value,
                                })
                              }
                              placeholder="Salesperson name"
                              className="bg-transparent border-b border-gray-500 outline-none text-slate-600 text-sm w-32"
                            />
                          </span>
                        ) : (
                          <>Sold by {selectedReceipt.salesperson}</>
                        )}
                      </p>
                    )}
                  </div>
                </div>

                {/* Center: Status Badge (always read-only).
                    mr-14 mirrors the action row's pr-14: when the drawer
                    narrows (chat open) and the header wraps, justify-between
                    pushes this badge to the far right of row one — without
                    the clearance it slides under the absolutely-positioned
                    close (X) button. In the wide single-row layout,
                    justify-between absorbs the margin invisibly. */}
                <div className="flex items-center mr-14">
                  <div
                    className={`flex items-center space-x-1.5 px-3 py-2 rounded-lg border text-sm font-semibold whitespace-nowrap ${getStatusColor(selectedReceipt.deliveryStatus || selectedReceipt.delivery_status)}`}
                  >
                    {getStatusIcon(
                      selectedReceipt.deliveryStatus ||
                        selectedReceipt.delivery_status,
                    )}
                    <span>
                      {deliveryStatuses.find(
                        (s) =>
                          s.value ===
                          (selectedReceipt.deliveryStatus ||
                            selectedReceipt.delivery_status),
                      )?.label || "Order Received"}
                    </span>
                  </div>
                </div>

                {/* Right: Action Buttons */}
                <div className="flex items-center space-x-3 ml-4 flex-wrap gap-y-2 pr-14">
                  {/* Chat Toggle (desktop drawer) */}
                  <button
                    type="button"
                    onClick={() => setIsChatPanelOpen((open) => !open)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-semibold transition-colors ${
                      isChatPanelOpen
                        ? "bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
                        : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"
                    }`}
                    title={isChatPanelOpen ? "Hide chat" : "Show chat"}
                  >
                    <MessageSquare className="w-4 h-4" />
                    <span>{isChatPanelOpen ? "Hide Chat" : "Chat"}</span>
                  </button>

                  {/* Edit Toggle */}
                  {isEditingInModal ? (
                    <div className="flex items-center space-x-2">
                      {/* Mark Completed - only when not already delivered */}
                      {editFormData &&
                        editFormData.deliveryStatus !== "delivered" && (
                          <button
                            onClick={() =>
                              setEditFormData({
                                ...editFormData,
                                deliveryStatus: "delivered",
                              })
                            }
                            className="flex items-center space-x-1.5 px-3 py-2 rounded-lg bg-emerald-100 border border-emerald-400 text-emerald-700 hover:bg-emerald-200 transition-colors text-sm font-medium whitespace-nowrap"
                            title="Mark as completed (e.g., customer picked up)"
                          >
                            <CheckCircle className="w-4 h-4" />
                            <span>Mark Completed</span>
                          </button>
                        )}
                      {/* Move to Active - only when delivered */}
                      {editFormData &&
                        editFormData.deliveryStatus === "delivered" && (
                          <button
                            onClick={() =>
                              setEditFormData({
                                ...editFormData,
                                deliveryStatus: "pending",
                              })
                            }
                            className="flex items-center space-x-1.5 px-3 py-2 rounded-lg bg-blue-100 border border-blue-400 text-blue-700 hover:bg-blue-200 transition-colors text-sm font-medium whitespace-nowrap"
                            title="Move back to active orders"
                          >
                            <RotateCcw className="w-4 h-4" />
                            <span>Move to Active</span>
                          </button>
                        )}
                      <button
                        onClick={handleCancelEditInModal}
                        className="flex items-center space-x-1.5 px-3 py-2 rounded-lg bg-slate-100 border border-slate-300 text-slate-600 hover:bg-slate-200 transition-colors text-sm font-medium whitespace-nowrap"
                      >
                        <X className="w-4 h-4" />
                        <span>Cancel</span>
                      </button>
                      <button
                        onClick={handleSaveEditInModal}
                        disabled={isSavingEdit}
                        className="flex items-center space-x-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {isSavingEdit ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            <span>Saving...</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4" />
                            <span>Save</span>
                          </>
                        )}
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* Send/Resend SMS */}
                      <button
                        onClick={() => handleSendSMS(selectedReceipt)}
                        disabled={sendingSMSReceiptId === selectedReceipt.id}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-xl border transition-all text-sm font-medium whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed ${
                          smsSentReceipts[selectedReceipt.id]
                            ? "bg-blue-100 border-blue-400 text-blue-600"
                            : selectedReceipt.sentAt || selectedReceipt.sent_at
                              ? "bg-blue-50 border-blue-300 text-blue-600 hover:bg-blue-100"
                              : "bg-blue-100 border-blue-400 text-blue-600 hover:bg-blue-200"
                        }`}
                        title={
                          selectedReceipt.sentAt || selectedReceipt.sent_at
                            ? "Resend Receipt via SMS"
                            : "Send Receipt via SMS"
                        }
                      >
                        {sendingSMSReceiptId === selectedReceipt.id ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            <span>Sending...</span>
                          </>
                        ) : smsSentReceipts[selectedReceipt.id] ? (
                          <>
                            <CheckCircle className="w-4 h-4" />
                            <span>Sent ✓</span>
                          </>
                        ) : selectedReceipt.sentAt ||
                          selectedReceipt.sent_at ? (
                          <>
                            <RotateCcw className="w-4 h-4" />
                            <span>Resend SMS</span>
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            <span>Send SMS</span>
                          </>
                        )}
                      </button>

                      {/* Edit Button */}
                      <button
                        data-tip="Switches this receipt into edit mode"
                        onClick={() => {
                          // Ensure editFormData.items is properly populated before entering edit mode
                          if (
                            !editFormData?.items?.length &&
                            selectedReceipt?.items?.length
                          ) {
                            const normalizedItems = (
                              selectedReceipt.items || []
                            ).map((item) => ({
                              id: item.id, // CRITICAL: preserve ID so updates don't break inventory links
                              name: item.name || item.item_name || "",
                              sku: item.sku || "",
                              color: item.color || "",
                              quantity: item.quantity || 1,
                              price:
                                item.price !== undefined && item.price !== null
                                  ? item.price
                                  : "",
                              setId: item.set_id || item.setId || null,
                              setName: item.set_name || item.setName || null,
                            }));
                            setEditFormData((prev) => ({
                              ...prev,
                              items: normalizedItems,
                            }));
                          }
                          setIsEditingInModal(true);
                        }}
                        className="flex items-center space-x-2 px-4 py-2 rounded-xl bg-white border border-slate-200 text-blue-600 hover:bg-blue-50 hover:border-blue-200 transition-colors text-sm font-medium"
                      >
                        <Edit2 className="w-4 h-4" />
                        <span>Edit</span>
                      </button>
                      {/* Print, Share and Delete live behind the overflow —
                          used weekly, not hourly. Delete keeps its existing
                          confirmation flow and is red only inside the menu. */}
                      <HeaderActionsOverflow
                        onPrint={() => handlePrintReceipt(selectedReceipt)}
                        onShare={() => handleShareReceipt(selectedReceipt)}
                        onDelete={() =>
                          handleDeleteReceipt(selectedReceipt.id)
                        }
                      />
                    </>
                  )}
                  {/* Close — isolated in the drawer's top-right corner,
                      never adjacent to any action (least of all Delete). */}
                  <button
                    onClick={handleAttemptCloseReceiptModal}
                    aria-label="Close receipt"
                    title="Close"
                    className="absolute top-3 right-3 z-20 p-2 rounded-xl bg-white border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-900 shadow-sm transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* ===== MAIN CONTENT: Two Columns (chat collapsible) ===== */}
            <div className="flex flex-1 overflow-hidden">
              {/* ===== LEFT PANEL: Receipt Details ===== */}
              <div className={`${isChatPanelOpen ? "w-1/2 border-r border-slate-300" : "w-full"} flex flex-col overflow-hidden transition-[width] duration-200`}>
                <div className="flex-1 overflow-y-auto p-6 space-y-5">
                  {/* Inventory Status Banner */}
                  {(() => {
                    // FIX: Always use selectedReceipt.items for status calculation (it has item.id)
                    // editFormData.items doesn't have id field
                    const items = selectedReceipt.items || [];
                    const inventoryUnits = selectedReceipt.inventoryUnits || [];
                    const deliveryStatus =
                      selectedReceipt.deliveryStatus ||
                      selectedReceipt.delivery_status ||
                      "pending";
                    const isDelivered = deliveryStatus === "delivered";

                    // FIX: Use getAllItemStatusesForReceipt to properly track claimed units
                    const itemStatusMap = getAllItemStatusesForReceipt(
                      items,
                      inventoryUnits,
                      allInventoryUnits,
                      selectedReceipt?.itemStatuses || selectedReceipt?.item_statuses || [],
                    );
                    let arrivedCount = 0;
                    let outForDeliveryCount = 0;
                    let deliveredCount = 0;
                    let waitingCount = 0;

                    items.forEach((item) => {
                      const itemStatus = itemStatusMap.get(item.id) || {
                        status: "waiting",
                      };

                      if (ITEM_TERMINAL_STATUSES.includes(itemStatus.status)) {
                        deliveredCount++;
                      } else if (itemStatus.status === "out_for_delivery") {
                        outForDeliveryCount++;
                      } else if (itemStatus.status === "ready") {
                        arrivedCount++;
                      } else {
                        waitingCount++;
                      }
                    });

                    const totalCount = items.length;
                    const readyOrDelivered =
                      arrivedCount + deliveredCount + outForDeliveryCount;
                    const progressPercent =
                      totalCount > 0
                        ? (readyOrDelivered / totalCount) * 100
                        : 0;
                    const allArrived =
                      arrivedCount === totalCount && totalCount > 0;

                    // FIX: Calculate out for delivery status from inventory, not just delivery_status field
                    const allOutForDelivery =
                      totalCount > 0 && outForDeliveryCount === totalCount;
                    const isOutForDelivery =
                      deliveryStatus === "out_for_delivery" ||
                      allOutForDelivery;
                    const isPartialOutForDelivery =
                      deliveryStatus === "partial_out_for_delivery" ||
                      (outForDeliveryCount > 0 &&
                        outForDeliveryCount < totalCount);

                    // Determine banner state
                    let bannerConfig;
                    if (isDelivered) {
                      bannerConfig = {
                        gradient:
                          "bg-gradient-to-r from-green-500/15 to-emerald-500/10 border-emerald-400",
                        iconBg: "bg-emerald-100",
                        icon: (
                          <CheckCircle className="w-6 h-6 text-emerald-600" />
                        ),
                        title: "Delivered!",
                        titleColor: "text-emerald-600",
                        subtitle: `${totalCount} item${totalCount !== 1 ? "s" : ""} delivered`,
                        progressColor:
                          "bg-gradient-to-r from-green-500 to-emerald-400",
                        percent: 100,
                      };
                    } else if (isOutForDelivery) {
                      bannerConfig = {
                        gradient:
                          "bg-gradient-to-r from-blue-100 to-blue-50 border-blue-600/30",
                        iconBg: "bg-blue-600/30",
                        icon: <Truck className="w-6 h-6 text-blue-600" />,
                        title: "Out for Delivery",
                        titleColor: "text-blue-600",
                        subtitle: `${totalCount} item${totalCount !== 1 ? "s" : ""} on the truck`,
                        progressColor:
                          "bg-gradient-to-r from-blue-500 to-blue-400",
                        percent: 75,
                      };
                    } else if (isPartialOutForDelivery) {
                      // Partial out for delivery - some items on truck, some not
                      bannerConfig = {
                        gradient:
                          "bg-gradient-to-r from-blue-100 to-blue-50 border-blue-500/30",
                        iconBg: "bg-blue-500/30",
                        icon: <Truck className="w-6 h-6 text-blue-600" />,
                        title: "Partial Out for Delivery",
                        titleColor: "text-blue-600",
                        subtitle: `${outForDeliveryCount} on truck, ${deliveredCount > 0 ? deliveredCount + " delivered, " : ""}${waitingCount > 0 ? waitingCount + " waiting" : arrivedCount + " ready"}`,
                        progressColor:
                          "bg-gradient-to-r from-blue-500 to-indigo-400",
                        percent: Math.round(
                          ((outForDeliveryCount + deliveredCount) /
                            totalCount) *
                            100,
                        ),
                      };
                    } else if (allArrived) {
                      bannerConfig = {
                        gradient:
                          "bg-gradient-to-r from-emerald-500/15 to-green-500/10 border-emerald-500/30",
                        iconBg: "bg-blue-100",
                        icon: (
                          <CheckCircle className="w-6 h-6 text-emerald-400" />
                        ),
                        title: "Ready for Delivery!",
                        titleColor: "text-emerald-400",
                        subtitle: `${arrivedCount} of ${totalCount} items in stock`,
                        progressColor:
                          "bg-gradient-to-r from-emerald-500 to-green-400",
                        percent: progressPercent,
                      };
                    } else {
                      bannerConfig = {
                        gradient: "bg-slate-50 border-slate-300",
                        iconBg: "bg-slate-200",
                        icon: <Clock className="w-6 h-6 text-slate-500" />,
                        title: "Waiting on Items",
                        titleColor: "text-slate-600",
                        subtitle: `${arrivedCount} of ${totalCount} items in stock${deliveredCount > 0 ? `, ${deliveredCount} completed` : ""}`,
                        progressColor: "bg-slate-400",
                        percent: progressPercent,
                      };
                    }

                    return (
                      <div
                        className={`p-4 rounded-2xl border ${bannerConfig.gradient}`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div
                              className={`p-2.5 rounded-xl ${bannerConfig.iconBg}`}
                            >
                              {bannerConfig.icon}
                            </div>
                            <div>
                              <h3
                                className={`text-lg font-bold ${bannerConfig.titleColor}`}
                              >
                                {bannerConfig.title}
                              </h3>
                              <p className="text-sm text-slate-500">
                                {bannerConfig.subtitle}
                              </p>
                            </div>
                          </div>
                          <div
                            className={`text-3xl font-bold ${bannerConfig.titleColor}`}
                          >
                            {Math.round(bannerConfig.percent)}%
                          </div>
                        </div>
                        <div className="h-2.5 bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${bannerConfig.progressColor}`}
                            style={{ width: `${bannerConfig.percent}%` }}
                          />
                        </div>
                      </div>
                    );
                  })()}

                  {/* Order Items */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider flex items-center space-x-2">
                        <Package className="w-4 h-4" />
                        <span>
                          Order Items (
                          {
                            (editFormData?.items?.length > 0
                              ? editFormData.items
                              : selectedReceipt.items || []
                            ).length
                          }
                          )
                        </span>
                        {isLoadingReceiptDetails && (
                          <RefreshCw className="w-3.5 h-3.5 animate-spin text-slate-300" />
                        )}
                      </h3>
                      {/* Add Item Buttons - only show in edit mode */}
                      {isEditingInModal && editFormData && (
                        <div className="flex items-center space-x-2">
                          <button
                            data-tip="Opens a form to add one item to this receipt"
                            onClick={() => handleAddItem("edit")}
                            className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:shadow-lg transition-all"
                          >
                            <Plus className="w-3.5 h-3.5" />
                            <span>Add Item</span>
                          </button>
                          <button
                            data-tip="Adds several items to this receipt as one grouped set"
                            onClick={() => {
                              setBulkAddTarget("edit");
                              setShowBulkSetModal(true);
                            }}
                            className="flex items-center space-x-1.5 px-2.5 py-1.5 rounded-lg bg-slate-100 border border-slate-300 text-slate-600 text-xs font-semibold hover:bg-slate-200 transition-all"
                          >
                            <Layers className="w-3.5 h-3.5" />
                            <span>Add Group</span>
                          </button>
                        </div>
                      )}
                    </div>
                    {!isEditingInModal && (
                      <>
                        <FulfillmentSummary
                          items={selectedReceipt.items || []}
                          statusMap={getAllItemStatusesForReceipt(
                            selectedReceipt.items || [],
                            selectedReceipt.inventoryUnits || [],
                            allInventoryUnits,
                            selectedReceipt?.itemStatuses ||
                              selectedReceipt?.item_statuses ||
                              [],
                          )}
                        />
                      </>
                    )}
                    <div className="space-y-2">
                      {/* Skeleton rows for the rare open-by-id path (push /
                          deep link) — replaced by real rows on hydrate. */}
                      {isLoadingReceiptDetails &&
                        !isEditingInModal &&
                        (selectedReceipt.items || []).length === 0 && (
                          <>
                            <div className="h-[72px] rounded-xl border border-slate-200 bg-slate-50 animate-pulse" />
                            <div className="h-[72px] rounded-xl border border-slate-200 bg-slate-50 animate-pulse" />
                          </>
                        )}
                      {/* FIX: For read-only view, always use selectedReceipt.items which has item.id */}
                      {/* Only use editFormData.items when actually editing */}
                      {(() => {
                        // FIX: Compute status map once for proper unit tracking
                        const itemStatusMap = getAllItemStatusesForReceipt(
                          selectedReceipt.items || [],
                          selectedReceipt.inventoryUnits || [],
                          allInventoryUnits,
                          selectedReceipt?.itemStatuses || selectedReceipt?.item_statuses || [],
                        );

                        const allItems =
                          isEditingInModal && editFormData?.items?.length > 0
                            ? editFormData.items
                            : selectedReceipt.items || [];
                        const itemStatuses =
                          editFormData?.itemStatuses ||
                          selectedReceipt.itemStatuses ||
                          [];

                        // In edit mode, render items as clean cards (matching create flow)
                        if (isEditingInModal && editFormData) {
                          // Group items by set for display
                          const groupedEditItems = [];
                          const processedIndices = new Set();

                          // First pass: collect all sets
                          const setGroups = {};
                          allItems.forEach((item, index) => {
                            const setId = item.set_id || item.setId;
                            if (setId) {
                              if (!setGroups[setId]) {
                                setGroups[setId] = {
                                  type: "set",
                                  setId: setId,
                                  setName:
                                    item.set_name || item.setName || "Group",
                                  items: [],
                                };
                              }
                              setGroups[setId].items.push({
                                ...item,
                                originalIndex: index,
                              });
                              processedIndices.add(index);
                            }
                          });

                          // Add sets to groups
                          Object.values(setGroups).forEach((setGroup) => {
                            groupedEditItems.push(setGroup);
                          });

                          // Second pass: group non-set items by name+sku+color (like view mode)
                          const skuGroups = {};
                          allItems.forEach((item, index) => {
                            if (!processedIndices.has(index)) {
                              const key = `${(item.name || item.item_name || "").toLowerCase()}-${(item.sku || "").toLowerCase()}-${(item.color || "").toLowerCase()}`;
                              if (!skuGroups[key]) {
                                skuGroups[key] = {
                                  type: "sku",
                                  name:
                                    item.name ||
                                    item.item_name ||
                                    "Unnamed Item",
                                  sku: item.sku || "",
                                  color: item.color || "",
                                  items: [],
                                };
                              }
                              skuGroups[key].items.push({
                                ...item,
                                originalIndex: index,
                              });
                            }
                          });

                          // Add SKU groups - if only 1 item, mark as single for simpler rendering
                          Object.values(skuGroups).forEach((skuGroup) => {
                            if (skuGroup.items.length === 1) {
                              groupedEditItems.push({
                                type: "single",
                                items: skuGroup.items,
                              });
                            } else {
                              groupedEditItems.push(skuGroup);
                            }
                          });

                          // Empty state
                          if (allItems.length === 0) {
                            return (
                              <div className="p-6 rounded-xl border border-dashed border-slate-300 bg-slate-100 text-center">
                                <Package className="w-10 h-10 text-gray-600 mx-auto mb-2" />
                                <p className="text-slate-500 text-sm">
                                  No items added yet
                                </p>
                                <p className="text-slate-400 text-xs mt-1">
                                  Click "Add Item" or "Add Group" to add
                                  furniture
                                </p>
                              </div>
                            );
                          }

                          return groupedEditItems.map((group, groupIndex) => {
                            if (group.type === "set") {
                              // Render SET group as collapsible card
                              const isExpanded =
                                expandedSets[group.setId] !== false;
                              const setTotal = group.items.reduce(
                                (sum, item) =>
                                  sum + (parseFloat(item.price) || 0),
                                0,
                              );

                              // Group items within set by name+sku+color for cleaner display
                              const itemSubgroups = {};
                              group.items.forEach((item) => {
                                const key = `${(item.name || item.item_name || "").toLowerCase()}-${(item.sku || "").toLowerCase()}-${(item.color || "").toLowerCase()}`;
                                if (!itemSubgroups[key]) {
                                  itemSubgroups[key] = {
                                    name:
                                      item.name ||
                                      item.item_name ||
                                      "Unnamed Item",
                                    sku: item.sku || "",
                                    color: item.color || "",
                                    items: [],
                                  };
                                }
                                itemSubgroups[key].items.push(item);
                              });
                              const subgroupList = Object.values(itemSubgroups);

                              return (
                                <div
                                  key={group.setId}
                                  className="rounded-xl border border-blue-400 overflow-hidden"
                                >
                                  {/* Set Header */}
                                  <div
                                    className="p-3 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                                    onClick={() =>
                                      toggleSetExpansion(group.setId)
                                    }
                                  >
                                    {/* Row 1: Expand icon, Name, Price, Delete */}
                                    <div className="flex items-start gap-2 mb-1">
                                      <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                                        {isExpanded ? (
                                          <ChevronDown className="w-4 h-4 text-slate-600" />
                                        ) : (
                                          <ChevronRight className="w-4 h-4 text-slate-600" />
                                        )}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <h4 className="text-slate-900 font-semibold text-sm">
                                          {group.setName}
                                        </h4>
                                        <p className="text-slate-600 text-xs">
                                          {group.items.length} pieces
                                        </p>
                                      </div>
                                      <span className="text-lg font-bold text-slate-900 flex-shrink-0">
                                        ${formatCurrency(setTotal)}
                                      </span>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditSet(group.setId, "edit");
                                        }}
                                        className="w-7 h-7 rounded bg-blue-50 border border-blue-200 text-blue-500 flex items-center justify-center flex-shrink-0 hover:bg-blue-100 transition-colors"
                                        title="Edit group"
                                      >
                                        <Edit2 className="w-3.5 h-3.5" />
                                      </button>
                                      <button
                                        aria-label="Delete this set"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (
                                            window.confirm(
                                              `Delete entire "${group.setName}" set (${group.items.length} items)?`,
                                            )
                                          ) {
                                            // Remove all items in this set
                                            const newItems = allItems.filter(
                                              (item) =>
                                                (item.set_id || item.setId) !==
                                                group.setId,
                                            );
                                            const { subtotal, tax, total } =
                                              calculateTotals(newItems);
                                            setEditFormData({
                                              ...editFormData,
                                              items: newItems,
                                              subtotal,
                                              tax,
                                              total,
                                            });
                                          }
                                        }}
                                        className="w-7 h-7 rounded bg-red-50 border border-red-200 text-red-500 flex items-center justify-center flex-shrink-0"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  </div>

                                  {/* Set Items - Expandable, grouped by name+sku */}
                                  {isExpanded && (
                                    <div className="divide-y divide-slate-200">
                                      {subgroupList.map((subgroup, subIdx) => {
                                        const subgroupKey = `${group.setId}-subgroup-${subIdx}`;
                                        const isSubgroupExpanded =
                                          expandedSkuGroups[subgroupKey];
                                        const count = subgroup.items.length;

                                        // Calculate status counts for this subgroup
                                        let readyCount = 0;
                                        let waitingCount = 0;
                                        subgroup.items.forEach((item) => {
                                          const status =
                                            itemStatusMap.get(item.id)
                                              ?.status || "waiting";
                                          if (
                                            [
                                              "ready",
                                              "at_store",
                                              "in_warehouse",
                                            ].includes(status)
                                          )
                                            readyCount++;
                                          else if (
                                            status !== "delivered" &&
                                            status !== "picked_up"
                                          )
                                            waitingCount++;
                                        });

                                        // If only 1 item in subgroup, render directly without nesting
                                        if (count === 1) {
                                          const item = subgroup.items[0];
                                          const statusInfo = itemStatusMap.get(
                                            item.id,
                                          ) || { status: "waiting" };
                                          const isInStock = [
                                            "at_store",
                                            "in_warehouse",
                                            "delivered",
                                            "ready",
                                          ].includes(statusInfo.status);
                                          const isDelivered =
                                            statusInfo.status === "delivered";
                                          const isPickedUp =
                                            statusInfo.status === "picked_up";
                                          const showActionButtons =
                                            isInStock &&
                                            !isDelivered &&
                                            !isPickedUp;

                                          return (
                                            <div
                                              key={item.originalIndex}
                                              className={`p-3 ${showActionButtons ? "bg-green-50" : ""}`}
                                            >
                                              <div className="flex items-start justify-between gap-2 mb-1">
                                                <div className="flex-1">
                                                  <span className="text-slate-900 font-medium">
                                                    {item.name ||
                                                      item.item_name ||
                                                      "Unnamed Item"}
                                                  </span>
                                                </div>
                                                <button
                                                  aria-label="Remove item from set"
                                                  onClick={() => {
                                                    if (
                                                      window.confirm(
                                                        `Remove "${item.name || item.item_name || "this item"}" from the set?`,
                                                      )
                                                    ) {
                                                      const newItems =
                                                        allItems.filter(
                                                          (_, i) =>
                                                            i !==
                                                            item.originalIndex,
                                                        );
                                                      const {
                                                        subtotal,
                                                        tax,
                                                        total,
                                                      } =
                                                        calculateTotals(
                                                          newItems,
                                                        );
                                                      setEditFormData({
                                                        ...editFormData,
                                                        items: newItems,
                                                        subtotal,
                                                        tax,
                                                        total,
                                                      });
                                                    }
                                                  }}
                                                  className="w-7 h-7 rounded bg-red-50 border border-red-200 text-red-500 flex items-center justify-center flex-shrink-0"
                                                >
                                                  <X className="w-3.5 h-3.5" />
                                                </button>
                                              </div>
                                              <div className="flex items-center gap-2 flex-wrap">
                                                {item.sku && (
                                                  <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 text-xs font-mono">
                                                    {item.sku}
                                                  </span>
                                                )}
                                                {isDelivered ? (
                                                  <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium flex items-center gap-1">
                                                    <CheckCircle className="w-3 h-3" />{" "}
                                                    Delivered
                                                  </span>
                                                ) : isPickedUp ? (
                                                  <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium flex items-center gap-1">
                                                    <CheckCircle className="w-3 h-3" />{" "}
                                                    Picked Up
                                                  </span>
                                                ) : isInStock ? (
                                                  <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium flex items-center gap-1">
                                                    <CheckCircle className="w-3 h-3" />{" "}
                                                    Ready
                                                  </span>
                                                ) : (
                                                  <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-xs font-medium flex items-center gap-1">
                                                    <Clock className="w-3 h-3" />{" "}
                                                    Waiting
                                                  </span>
                                                )}
                                              </div>
                                              {showActionButtons && (
                                                <div className="flex gap-2 mt-3">
                                                  <button
                                                    data-tip="Marks this item as delivered"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleMarkUnitStatus(
                                                        selectedReceipt.id,
                                                        item,
                                                        "delivered",
                                                      );
                                                    }}
                                                    className="flex-1 py-2.5 text-sm font-semibold text-white bg-emerald-600 rounded-lg active:bg-emerald-700 flex items-center justify-center gap-1.5"
                                                  >
                                                    <Home className="w-4 h-4" />
                                                    Delivered
                                                  </button>
                                                  <button
                                                    data-tip="Marks this item as picked up"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleMarkUnitStatus(
                                                        selectedReceipt.id,
                                                        item,
                                                        "picked_up",
                                                      );
                                                    }}
                                                    className="flex-1 py-2.5 text-sm font-semibold text-white bg-purple-600 rounded-lg active:bg-purple-700 flex items-center justify-center gap-1.5"
                                                  >
                                                    <Box className="w-4 h-4" />
                                                    Picked Up
                                                  </button>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        }

                                        // Multiple items - render as collapsible drawer
                                        return (
                                          <div
                                            key={subgroupKey}
                                            className="bg-white"
                                          >
                                            {/* Drawer header - clickable to expand */}
                                            <div
                                              className="p-3 cursor-pointer hover:bg-slate-50 transition-colors flex items-center justify-between"
                                              onClick={() =>
                                                setExpandedSkuGroups(
                                                  (prev) => ({
                                                    ...prev,
                                                    [subgroupKey]:
                                                      !prev[subgroupKey],
                                                  }),
                                                )
                                              }
                                            >
                                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                                <div className="w-6 h-6 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
                                                  {isSubgroupExpanded ? (
                                                    <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
                                                  ) : (
                                                    <ChevronRight className="w-3.5 h-3.5 text-slate-500" />
                                                  )}
                                                </div>
                                                {subgroup.sku && (
                                                  <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 text-xs font-mono flex-shrink-0">
                                                    {subgroup.sku}
                                                  </span>
                                                )}
                                                <span className="text-slate-900 font-medium truncate">
                                                  {subgroup.name}
                                                </span>
                                              </div>
                                              <div className="flex items-center gap-2 flex-shrink-0">
                                                {/* Status summary */}
                                                <div className="flex items-center gap-1 text-xs">
                                                  {readyCount > 0 && (
                                                    <span className="text-emerald-600 font-medium">
                                                      {readyCount} ready
                                                    </span>
                                                  )}
                                                  {waitingCount > 0 && (
                                                    <span className="text-slate-500">
                                                      {waitingCount}⏳
                                                    </span>
                                                  )}
                                                </div>
                                                <span className="px-2 py-0.5 rounded bg-blue-600 text-white text-xs font-bold">
                                                  {count}x
                                                </span>
                                                <button
                                                  aria-label="Remove all of these items from the set"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (
                                                      window.confirm(
                                                        `Remove all ${count} "${subgroup.name}" items from the set?`,
                                                      )
                                                    ) {
                                                      const indicesToRemove =
                                                        new Set(
                                                          subgroup.items.map(
                                                            (i) =>
                                                              i.originalIndex,
                                                          ),
                                                        );
                                                      const newItems =
                                                        allItems.filter(
                                                          (_, i) =>
                                                            !indicesToRemove.has(
                                                              i,
                                                            ),
                                                        );
                                                      const {
                                                        subtotal,
                                                        tax,
                                                        total,
                                                      } =
                                                        calculateTotals(
                                                          newItems,
                                                        );
                                                      setEditFormData({
                                                        ...editFormData,
                                                        items: newItems,
                                                        subtotal,
                                                        tax,
                                                        total,
                                                      });
                                                    }
                                                  }}
                                                  className="w-7 h-7 rounded bg-red-50 border border-red-200 text-red-500 flex items-center justify-center"
                                                >
                                                  <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                              </div>
                                            </div>

                                            {/* Expanded individual units */}
                                            {isSubgroupExpanded && (
                                              <div className="border-t border-slate-100 bg-slate-50/50">
                                                {subgroup.items.map(
                                                  (item, unitIdx) => {
                                                    const statusInfo =
                                                      itemStatusMap.get(
                                                        item.id,
                                                      ) || {
                                                        status: "waiting",
                                                      };
                                                    const isInStock = [
                                                      "at_store",
                                                      "in_warehouse",
                                                      "delivered",
                                                      "ready",
                                                    ].includes(
                                                      statusInfo.status,
                                                    );
                                                    const isDelivered =
                                                      statusInfo.status ===
                                                      "delivered";
                                                    const isPickedUp =
                                                      statusInfo.status ===
                                                      "picked_up";
                                                    const showActionButtons =
                                                      isInStock &&
                                                      !isDelivered &&
                                                      !isPickedUp;

                                                    return (
                                                      <div
                                                        key={item.originalIndex}
                                                        className={`p-3 pl-10 border-b border-slate-100 last:border-b-0 ${showActionButtons ? "bg-green-50/50" : ""}`}
                                                      >
                                                        <div className="flex items-center justify-between mb-1">
                                                          <div className="flex items-center gap-2">
                                                            <span className="text-slate-500 text-sm font-medium">
                                                              #{unitIdx + 1}
                                                            </span>
                                                            {isDelivered ? (
                                                              <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium flex items-center gap-1">
                                                                <CheckCircle className="w-3 h-3" />{" "}
                                                                Delivered
                                                              </span>
                                                            ) : isPickedUp ? (
                                                              <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium flex items-center gap-1">
                                                                <CheckCircle className="w-3 h-3" />{" "}
                                                                Picked Up
                                                              </span>
                                                            ) : isInStock ? (
                                                              <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium flex items-center gap-1">
                                                                <CheckCircle className="w-3 h-3" />{" "}
                                                                Ready
                                                              </span>
                                                            ) : (
                                                              <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-xs font-medium flex items-center gap-1">
                                                                <Clock className="w-3 h-3" />{" "}
                                                                Waiting
                                                              </span>
                                                            )}
                                                          </div>
                                                          <button
                                                            aria-label="Remove this unit"
                                                            onClick={() => {
                                                              if (
                                                                window.confirm(
                                                                  `Remove unit #${unitIdx + 1}?`,
                                                                )
                                                              ) {
                                                                const newItems =
                                                                  allItems.filter(
                                                                    (_, i) =>
                                                                      i !==
                                                                      item.originalIndex,
                                                                  );
                                                                const {
                                                                  subtotal,
                                                                  tax,
                                                                  total,
                                                                } =
                                                                  calculateTotals(
                                                                    newItems,
                                                                  );
                                                                setEditFormData(
                                                                  {
                                                                    ...editFormData,
                                                                    items:
                                                                      newItems,
                                                                    subtotal,
                                                                    tax,
                                                                    total,
                                                                  },
                                                                );
                                                              }
                                                            }}
                                                            className="w-6 h-6 rounded bg-red-50 border border-red-200 text-red-500 flex items-center justify-center"
                                                          >
                                                            <X className="w-3 h-3" />
                                                          </button>
                                                        </div>
                                                        {showActionButtons && (
                                                          <div className="flex gap-2 mt-2">
                                                            <button
                                                              data-tip="Marks this item as delivered"
                                                              onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleMarkUnitStatus(
                                                                  selectedReceipt.id,
                                                                  item,
                                                                  "delivered",
                                                                );
                                                              }}
                                                              className="flex-1 py-2 text-xs font-semibold text-white bg-emerald-600 rounded-lg active:bg-emerald-700 flex items-center justify-center gap-1"
                                                            >
                                                              <Home className="w-3 h-3" />
                                                              Delivered
                                                            </button>
                                                            <button
                                                              data-tip="Marks this item as picked up"
                                                              onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleMarkUnitStatus(
                                                                  selectedReceipt.id,
                                                                  item,
                                                                  "picked_up",
                                                                );
                                                              }}
                                                              className="flex-1 py-2 text-xs font-semibold text-white bg-purple-600 rounded-lg active:bg-purple-700 flex items-center justify-center gap-1"
                                                            >
                                                              <Box className="w-3 h-3" />
                                                              Picked Up
                                                            </button>
                                                          </div>
                                                        )}
                                                      </div>
                                                    );
                                                  },
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            } else if (group.type === "sku") {
                              // Render SKU group as collapsible drawer (non-set items grouped by SKU)
                              const skuGroupKey = `sku-${groupIndex}`;
                              const isSkuExpanded =
                                expandedSkuGroups[skuGroupKey];
                              const count = group.items.length;
                              const totalPrice = group.items.reduce(
                                (sum, item) =>
                                  sum + (parseFloat(item.price) || 0),
                                0,
                              );

                              // Calculate status counts
                              let readyCount = 0;
                              let waitingCount = 0;
                              group.items.forEach((item) => {
                                const status =
                                  itemStatusMap.get(item.id)?.status ||
                                  "waiting";
                                if (
                                  [
                                    "ready",
                                    "at_store",
                                    "in_warehouse",
                                  ].includes(status)
                                )
                                  readyCount++;
                                else if (
                                  status !== "delivered" &&
                                  status !== "picked_up"
                                )
                                  waitingCount++;
                              });

                              return (
                                <div
                                  key={skuGroupKey}
                                  className="rounded-xl border border-slate-300 overflow-hidden"
                                >
                                  {/* SKU Group Header */}
                                  <div
                                    className="p-3 bg-white cursor-pointer hover:bg-slate-50 transition-colors flex items-center justify-between"
                                    onClick={() =>
                                      setExpandedSkuGroups((prev) => ({
                                        ...prev,
                                        [skuGroupKey]: !prev[skuGroupKey],
                                      }))
                                    }
                                  >
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                      <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                                        {isSkuExpanded ? (
                                          <ChevronDown className="w-4 h-4 text-slate-500" />
                                        ) : (
                                          <ChevronRight className="w-4 h-4 text-slate-500" />
                                        )}
                                      </div>
                                      {group.sku && (
                                        <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 text-xs font-mono flex-shrink-0">
                                          {group.sku}
                                        </span>
                                      )}
                                      <span className="text-slate-900 font-medium truncate">
                                        {group.name}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      {/* Status summary */}
                                      <div className="flex items-center gap-1 text-xs">
                                        {readyCount > 0 && (
                                          <span className="text-emerald-600 font-medium">
                                            {readyCount} ready
                                          </span>
                                        )}
                                        {waitingCount > 0 && (
                                          <span className="text-slate-500">
                                            {waitingCount}⏳
                                          </span>
                                        )}
                                      </div>
                                      <span className="px-2 py-0.5 rounded bg-blue-600 text-white text-xs font-bold">
                                        {count}x
                                      </span>
                                      <span className="text-lg font-bold text-slate-900">
                                        ${formatCurrency(totalPrice)}
                                      </span>
                                      <button
                                        aria-label="Remove all of these items"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (
                                            window.confirm(
                                              `Remove all ${count} "${group.name}" items?`,
                                            )
                                          ) {
                                            const indicesToRemove = new Set(
                                              group.items.map(
                                                (i) => i.originalIndex,
                                              ),
                                            );
                                            const newItems = allItems.filter(
                                              (_, i) => !indicesToRemove.has(i),
                                            );
                                            const { subtotal, tax, total } =
                                              calculateTotals(newItems);
                                            setEditFormData({
                                              ...editFormData,
                                              items: newItems,
                                              subtotal,
                                              tax,
                                              total,
                                            });
                                          }
                                        }}
                                        className="w-7 h-7 rounded bg-red-50 border border-red-200 text-red-500 flex items-center justify-center"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  </div>

                                  {/* Expanded individual units */}
                                  {isSkuExpanded && (
                                    <div className="border-t border-slate-100 bg-slate-50/50">
                                      {group.items.map((item, unitIdx) => {
                                        const statusInfo = itemStatusMap.get(
                                          item.id,
                                        ) || { status: "waiting" };
                                        const isInStock = [
                                          "at_store",
                                          "in_warehouse",
                                          "delivered",
                                          "ready",
                                        ].includes(statusInfo.status);
                                        const isDelivered =
                                          statusInfo.status === "delivered";
                                        const isPickedUp =
                                          statusInfo.status === "picked_up";
                                        const showActionButtons =
                                          isInStock &&
                                          !isDelivered &&
                                          !isPickedUp;

                                        return (
                                          <div
                                            key={item.originalIndex}
                                            className={`p-3 pl-10 border-b border-slate-100 last:border-b-0 ${showActionButtons ? "bg-green-50/50" : ""}`}
                                          >
                                            <div className="flex items-center justify-between mb-1">
                                              <div className="flex items-center gap-2">
                                                <span className="text-slate-500 text-sm font-medium">
                                                  #{unitIdx + 1}
                                                </span>
                                                {isDelivered ? (
                                                  <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium flex items-center gap-1">
                                                    <CheckCircle className="w-3 h-3" />{" "}
                                                    Delivered
                                                  </span>
                                                ) : isPickedUp ? (
                                                  <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium flex items-center gap-1">
                                                    <CheckCircle className="w-3 h-3" />{" "}
                                                    Picked Up
                                                  </span>
                                                ) : isInStock ? (
                                                  <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium flex items-center gap-1">
                                                    <CheckCircle className="w-3 h-3" />{" "}
                                                    Ready
                                                  </span>
                                                ) : (
                                                  <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-xs font-medium flex items-center gap-1">
                                                    <Clock className="w-3 h-3" />{" "}
                                                    Waiting
                                                  </span>
                                                )}
                                              </div>
                                              <button
                                                aria-label="Remove this unit"
                                                onClick={() => {
                                                  if (
                                                    window.confirm(
                                                      `Remove unit #${unitIdx + 1}?`,
                                                    )
                                                  ) {
                                                    const newItems =
                                                      allItems.filter(
                                                        (_, i) =>
                                                          i !==
                                                          item.originalIndex,
                                                      );
                                                    const {
                                                      subtotal,
                                                      tax,
                                                      total,
                                                    } =
                                                      calculateTotals(newItems);
                                                    setEditFormData({
                                                      ...editFormData,
                                                      items: newItems,
                                                      subtotal,
                                                      tax,
                                                      total,
                                                    });
                                                  }
                                                }}
                                                className="w-6 h-6 rounded bg-red-50 border border-red-200 text-red-500 flex items-center justify-center"
                                              >
                                                <X className="w-3 h-3" />
                                              </button>
                                            </div>
                                            {showActionButtons && (
                                              <div className="flex gap-2 mt-2">
                                                <button
                                                  data-tip="Marks this item as delivered"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleMarkUnitStatus(
                                                      selectedReceipt.id,
                                                      item,
                                                      "delivered",
                                                    );
                                                  }}
                                                  className="flex-1 py-2 text-xs font-semibold text-white bg-emerald-600 rounded-lg active:bg-emerald-700 flex items-center justify-center gap-1"
                                                >
                                                  <Home className="w-3 h-3" />
                                                  Delivered
                                                </button>
                                                <button
                                                  data-tip="Marks this item as picked up"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleMarkUnitStatus(
                                                      selectedReceipt.id,
                                                      item,
                                                      "picked_up",
                                                    );
                                                  }}
                                                  className="flex-1 py-2 text-xs font-semibold text-white bg-purple-600 rounded-lg active:bg-purple-700 flex items-center justify-center gap-1"
                                                >
                                                  <Box className="w-3 h-3" />
                                                  Picked Up
                                                </button>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            } else {
                              // Render SINGLE item as clean card
                              const item = group.items[0];
                              const index = item.originalIndex;
                              // FIX: Use itemStatusMap.get(item.id) for per-unit status, not SKU-based lookup
                              const statusInfo = itemStatusMap.get(item.id) || {
                                status: "waiting",
                              };
                              const isInStock = [
                                "at_store",
                                "in_warehouse",
                                "delivered",
                                "ready",
                              ].includes(statusInfo.status);
                              const isDelivered =
                                statusInfo.status === "delivered";
                              const isPickedUp =
                                statusInfo.status === "picked_up";
                              const showActionButtons =
                                isInStock && !isDelivered && !isPickedUp;

                              return (
                                <div
                                  key={index}
                                  className={`p-4 rounded-xl border cursor-pointer transition-all ${
                                    showActionButtons
                                      ? "bg-green-50 border-green-200"
                                      : "bg-white border-slate-200"
                                  }`}
                                  onClick={() => handleEditItem(index, "edit")}
                                >
                                  {/* Row 1: Name and Price + Delete */}
                                  <div className="flex items-start justify-between gap-2 mb-2">
                                    <h4 className="text-slate-900 font-semibold flex-1">
                                      {item.name ||
                                        item.item_name ||
                                        "Unnamed Item"}
                                    </h4>
                                    <span className="text-lg font-bold text-slate-900 flex-shrink-0">
                                      $
                                      {formatCurrency(parseFloat(item.price) || 0)}
                                    </span>
                                    <button
                                      aria-label="Remove item"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (
                                          window.confirm(
                                            `Remove "${item.name || item.item_name || "this item"}"?`,
                                          )
                                        ) {
                                          const newItems = allItems.filter(
                                            (_, i) => i !== index,
                                          );
                                          const { subtotal, tax, total } =
                                            calculateTotals(newItems);
                                          setEditFormData({
                                            ...editFormData,
                                            items: newItems,
                                            subtotal,
                                            tax,
                                            total,
                                          });
                                        }
                                      }}
                                      className="w-8 h-8 rounded bg-red-50 border border-red-200 text-red-500 flex items-center justify-center flex-shrink-0"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>

                                  {/* Row 2: SKU, Status, Qty */}
                                  <div className="flex items-center gap-2 flex-wrap mb-1">
                                    {item.sku && (
                                      <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 text-xs font-mono">
                                        {item.sku}
                                      </span>
                                    )}
                                    {(item.manufacturerId ||
                                      item.manufacturer_id) &&
                                      manufacturers.find(
                                        (m) =>
                                          m.id ===
                                          (item.manufacturerId ||
                                            item.manufacturer_id),
                                      ) && (
                                        <span className="px-1.5 py-0.5 rounded bg-purple-50 text-purple-600 text-xs font-medium">
                                          {
                                            manufacturers.find(
                                              (m) =>
                                                m.id ===
                                                (item.manufacturerId ||
                                                  item.manufacturer_id),
                                            )?.name
                                          }
                                        </span>
                                      )}
                                    {isDelivered ? (
                                      <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium flex items-center gap-1">
                                        <CheckCircle className="w-3 h-3" />{" "}
                                        Delivered
                                      </span>
                                    ) : isPickedUp ? (
                                      <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium flex items-center gap-1">
                                        <CheckCircle className="w-3 h-3" />{" "}
                                        Picked Up
                                      </span>
                                    ) : isInStock ? (
                                      <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium flex items-center gap-1">
                                        <CheckCircle className="w-3 h-3" />{" "}
                                        Ready
                                      </span>
                                    ) : (
                                      <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-xs font-medium flex items-center gap-1">
                                        <Clock className="w-3 h-3" /> Waiting
                                      </span>
                                    )}
                                    <span className="text-xs text-slate-500">
                                      Qty: {item.quantity || 1}
                                    </span>
                                  </div>

                                  {/* Row 3: Action buttons (if needed) */}
                                  {showActionButtons && (
                                    <div className="flex gap-2 mt-3">
                                      <button
                                        data-tip="Marks this item as delivered"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleMarkUnitStatus(
                                            selectedReceipt.id,
                                            item,
                                            "delivered",
                                          );
                                        }}
                                        className="flex-1 py-2.5 text-sm font-semibold text-white bg-emerald-600 rounded-lg active:bg-emerald-700 flex items-center justify-center gap-1.5"
                                      >
                                        <Home className="w-4 h-4" />
                                        Delivered
                                      </button>
                                      <button
                                        data-tip="Marks this item as picked up"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleMarkUnitStatus(
                                            selectedReceipt.id,
                                            item,
                                            "picked_up",
                                          );
                                        }}
                                        className="flex-1 py-2.5 text-sm font-semibold text-white bg-purple-600 rounded-lg active:bg-purple-700 flex items-center justify-center gap-1.5"
                                      >
                                        <Box className="w-4 h-4" />
                                        Picked Up
                                      </button>
                                    </div>
                                  )}

                                  <p className="text-xs text-slate-400 mt-2">
                                    Tap to edit
                                  </p>
                                </div>
                              );
                            }
                          });
                        }

                        // Read-only mode: Group items by SET first, then by SKU
                        // Use the same grouping logic as mobile view
                        const groupItemsForDesktopView = (items) => {
                          const groups = [];
                          const processedIndices = new Set();

                          // First pass: collect all sets
                          const setGroups = {};
                          items.forEach((item, index) => {
                            const setId = item.set_id || item.setId;
                            if (setId) {
                              if (!setGroups[setId]) {
                                setGroups[setId] = {
                                  type: "set",
                                  setId: setId,
                                  setName:
                                    item.set_name || item.setName || "Set",
                                  items: [],
                                };
                              }
                              setGroups[setId].items.push({
                                ...item,
                                originalIndex: index,
                              });
                              processedIndices.add(index);
                            }
                          });

                          // Add set groups to result
                          Object.values(setGroups).forEach((setGroup) => {
                            groups.push(setGroup);
                          });

                          // Second pass: group remaining items by SKU
                          const skuGroups = {};
                          items.forEach((item, index) => {
                            if (processedIndices.has(index)) return; // Skip set items

                            const sku =
                              (item.sku || "").toUpperCase().trim() ||
                              `NO-SKU-${index}`;
                            if (!skuGroups[sku]) {
                              skuGroups[sku] = [];
                            }
                            skuGroups[sku].push({
                              ...item,
                              originalIndex: index,
                            });
                          });

                          // Add SKU groups to result
                          Object.entries(skuGroups).forEach(
                            ([sku, skuItems]) => {
                              if (skuItems.length === 1) {
                                groups.push({
                                  type: "single",
                                  item: skuItems[0],
                                });
                              } else {
                                groups.push({
                                  type: "sku",
                                  sku,
                                  items: skuItems,
                                });
                              }
                            },
                          );

                          return groups;
                        };

                        const displayGroups =
                          groupItemsForDesktopView(allItems);

                        // Bulk action helpers for set/SKU group headers
                        const bulkMarkReady = async (items, e) => {
                          e.stopPropagation();
                          const toMark = items.filter((it) => {
                            const s =
                              itemStatusMap.get(it.id)?.status || "waiting";
                            return s === "waiting";
                          });
                          if (toMark.length === 0) return;
                          if (
                            !window.confirm(
                              `Mark ${toMark.length} waiting item${toMark.length === 1 ? "" : "s"} as Ready? This will reserve inventory for each.`,
                            )
                          )
                            return;
                          for (const it of toMark) {
                            await handleMarkItemReady(selectedReceipt.id, it);
                          }
                        };
                        const bulkMarkStatus = async (items, newStatus, e) => {
                          e.stopPropagation();
                          const toMark = items.filter((it) => {
                            const s =
                              itemStatusMap.get(it.id)?.status || "waiting";
                            return (
                              s === "ready" ||
                              s === "out_for_delivery" ||
                              s === "partial"
                            );
                          });
                          if (toMark.length === 0) return;
                          const label =
                            newStatus === "picked_up"
                              ? "Picked Up"
                              : "Delivered";
                          if (
                            !window.confirm(
                              `Mark ${toMark.length} item${toMark.length === 1 ? "" : "s"} as ${label}?`,
                            )
                          )
                            return;
                          for (const it of toMark) {
                            await handleMarkUnitStatus(
                              selectedReceipt.id,
                              it,
                              newStatus,
                              { skipConfirm: true },
                            );
                          }
                        };

                        // Render single item card (used for both standalone and expanded drawer items)
                        const renderItemCard = (item, isNested = false) => {
                          const inventoryStatus = itemStatusMap.get(
                            item.id,
                          ) || {
                            status: "waiting",
                            delivered: 0,
                            ready: 0,
                            waiting: 1,
                            outForDelivery: 0,
                          };
                          const isDelivered =
                            inventoryStatus.status === "delivered";
                          const isPickedUp =
                            inventoryStatus.status === "picked_up";
                          const isCompleted = isDelivered || isPickedUp;
                          const isReady = inventoryStatus.status === "ready";
                          const isPartialReady =
                            inventoryStatus.status === "partial";
                          const isOutForDelivery =
                            inventoryStatus.status === "out_for_delivery";
                          const isSelected =
                            selectedItemsForDelivery[item.id] || false;
                          const canSelect =
                            isReady && !isCompleted && !isOutForDelivery;
                          const canMarkComplete =
                            (isReady || isOutForDelivery) && !isCompleted;

                          return (
                            <FulfillmentItemRow
                              key={item.id || item.originalIndex}
                              item={item}
                              inventoryStatus={inventoryStatus}
                              isSelected={isSelected}
                              canSelect={canSelect}
                              isNested={isNested}
                              manufacturerName={(() => {
                                const mfrId =
                                  item.manufacturerId || item.manufacturer_id;
                                const mfr =
                                  mfrId &&
                                  manufacturers.find((m) => m.id === mfrId);
                                return mfr ? mfr.name : null;
                              })()}
                              busyReady={markingItemReady}
                              busyStatus={itemStatusBusy[item.id] || null}
                              busyUndoId={isUndoingDelivery}
                              onToggleSelect={() =>
                                toggleItemForDelivery(item.id)
                              }
                              onMarkReady={() =>
                                handleMarkItemReady(selectedReceipt.id, item)
                              }
                              onMarkStatus={(status) =>
                                handleMarkUnitStatus(
                                  selectedReceipt.id,
                                  item,
                                  status,
                                )
                              }
                              onUndoReady={() =>
                                handleUndoReady(selectedReceipt.id, item)
                              }
                              onUndoDelivery={() =>
                                handleUndoDelivery(item, selectedReceipt)
                              }
                            />
                          );
                        };

                        return displayGroups.map((group, groupIndex) => {
                          // RENDER SET GROUP
                          if (group.type === "set") {
                            const isExpanded =
                              expandedSkuGroups[`set-${group.setId}`] ?? false; // Default collapsed
                            const totalPrice = group.items.reduce(
                              (sum, item) => sum + (item.price || 0),
                              0,
                            );

                            // Calculate aggregate status for the set
                            const statusCounts = group.items.reduce(
                              (acc, item) => {
                                const status =
                                  itemStatusMap.get(item.id)?.status ||
                                  "waiting";
                                acc[status] = (acc[status] || 0) + 1;
                                return acc;
                              },
                              {},
                            );

                            const completedCount =
                              (statusCounts.delivered || 0) +
                              (statusCounts.picked_up || 0);
                            const allCompleted =
                              completedCount === group.items.length;
                            const allDelivered = allCompleted; // For backward compatibility in styling
                            const allReady =
                              statusCounts.ready === group.items.length;
                            const allOutForDelivery =
                              statusCounts.out_for_delivery ===
                              group.items.length;
                            const hasReady = statusCounts.ready > 0;
                            const hasDelivered =
                              (statusCounts.delivered || 0) > 0;
                            const hasPickedUp =
                              (statusCounts.picked_up || 0) > 0;
                            const hasCompleted = hasDelivered || hasPickedUp;
                            const hasOutForDelivery =
                              statusCounts.out_for_delivery > 0;

                            return (
                              <div
                                key={`set-${group.setId}`}
                                className="rounded-xl border border-blue-400 overflow-hidden"
                              >
                                {/* Set Header */}
                                <button
                                  data-tip="Shows or hides the items in this set"
                                  onClick={() =>
                                    setExpandedSkuGroups((prev) => ({
                                      ...prev,
                                      [`set-${group.setId}`]: !(
                                        prev[`set-${group.setId}`] ?? false
                                      ),
                                    }))
                                  }
                                  className={`w-full p-3 flex items-center justify-between transition-all ${
                                    allCompleted
                                      ? "bg-emerald-500/10 hover:bg-emerald-500/15"
                                      : allOutForDelivery
                                        ? "bg-slate-50 hover:bg-slate-100"
                                        : allReady
                                          ? "bg-emerald-50 hover:bg-green-500/15"
                                          : "bg-slate-50 hover:bg-slate-100"
                                  }`}
                                >
                                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                                    <div
                                      className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                        allCompleted
                                          ? "bg-emerald-500/20"
                                          : allOutForDelivery
                                            ? "bg-blue-100"
                                            : allReady
                                              ? "bg-emerald-100"
                                              : "bg-blue-100"
                                      }`}
                                    >
                                      <Layers
                                        className={`w-4 h-4 ${
                                          allCompleted
                                            ? "text-emerald-400"
                                            : allOutForDelivery
                                              ? "text-blue-600"
                                              : allReady
                                                ? "text-emerald-600"
                                                : "text-blue-600"
                                        }`}
                                      />
                                    </div>
                                    <div className="text-left min-w-0 flex-1">
                                      <div className="flex items-start justify-between gap-2">
                                        <span className="text-slate-600 font-semibold text-sm leading-tight">
                                          {group.setName}
                                        </span>
                                        <span className="font-bold text-slate-900 text-sm whitespace-nowrap">
                                          ${formatCurrency(totalPrice)}
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-between mt-1">
                                        <div className="flex items-center gap-2">
                                          <span className="px-2 py-0.5 rounded-full bg-blue-100 text-slate-600 text-xs font-medium whitespace-nowrap">
                                            {group.items.length} pieces
                                          </span>
                                          <span className="text-xs text-slate-400">
                                            {allCompleted
                                              ? "All complete"
                                              : allReady
                                                ? "All ready for delivery"
                                                : hasReady
                                                  ? `${statusCounts.ready || 0}/${group.items.length} ready`
                                                  : "Waiting on items"}
                                          </span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                          {hasDelivered && (
                                            <span className="text-xs text-emerald-400">
                                              {statusCounts.delivered}✓
                                            </span>
                                          )}
                                          {hasPickedUp && (
                                            <span className="text-xs text-purple-500">
                                              {statusCounts.picked_up}📦
                                            </span>
                                          )}
                                          {hasOutForDelivery && (
                                            <span className="text-xs text-blue-600">
                                              {statusCounts.out_for_delivery}🚚
                                            </span>
                                          )}
                                          {hasReady && !hasCompleted && (
                                            <span className="text-xs text-emerald-600">
                                              {statusCounts.ready}✓
                                            </span>
                                          )}
                                          {statusCounts.waiting > 0 && (
                                            <span className="text-xs text-slate-500">
                                              {statusCounts.waiting}⏳
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <ChevronDown
                                    className={`w-5 h-5 text-blue-600 transition-transform duration-200 flex-shrink-0 ${isExpanded ? "rotate-180" : ""}`}
                                  />
                                </button>



                                {/* Set Content */}
                                <div
                                  className={`overflow-hidden transition-all duration-200 ease-in-out ${
                                    isExpanded
                                      ? "max-h-[2000px] opacity-100"
                                      : "max-h-0 opacity-0"
                                  }`}
                                >
                                  <div className="p-2 space-y-2 bg-slate-50/50 border-t border-slate-300">
                                    {group.items.map((item, idx) => (
                                      <div key={item.id || idx}>
                                        {renderItemCard(item, true)}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            );
                          }

                          // RENDER SINGLE ITEM
                          if (group.type === "single") {
                            return renderItemCard(group.item, false);
                          }

                          // RENDER SKU GROUP
                          if (group.type === "sku") {
                            const items = group.items;
                            const sku = group.sku;
                            const isExpanded = expandedSkuGroups[sku] || false;
                            const firstItem = items[0];
                            const totalPrice = items.reduce(
                              (sum, item) => sum + (item.price || 0),
                              0,
                            );

                            // Calculate aggregate status for the group
                            const statusCounts = items.reduce((acc, item) => {
                              const status =
                                itemStatusMap.get(item.id)?.status || "waiting";
                              acc[status] = (acc[status] || 0) + 1;
                              return acc;
                            }, {});

                            const allDelivered =
                              statusCounts.delivered === items.length;
                            const allReady =
                              statusCounts.ready === items.length;
                            const allOutForDelivery =
                              statusCounts.out_for_delivery === items.length;
                            const hasReady = statusCounts.ready > 0;
                            const hasDelivered = statusCounts.delivered > 0;
                            const hasOutForDelivery =
                              statusCounts.out_for_delivery > 0;

                            return (
                              <div
                                key={sku}
                                className="rounded-xl border border-slate-300 overflow-hidden"
                              >
                                {/* Drawer Header - Always visible */}
                                <button
                                  data-tip="Shows or hides the units for this item"
                                  onClick={() =>
                                    setExpandedSkuGroups((prev) => ({
                                      ...prev,
                                      [sku]: !prev[sku],
                                    }))
                                  }
                                  className={`w-full p-3 flex items-center justify-between transition-all ${
                                    allDelivered
                                      ? "bg-emerald-500/10 hover:bg-emerald-500/15"
                                      : allOutForDelivery
                                        ? "bg-slate-50 hover:bg-slate-100"
                                        : allReady
                                          ? "bg-emerald-500/5 hover:bg-emerald-500/10"
                                          : "bg-white/[0.03] hover:bg-white/[0.06]"
                                  }`}
                                >
                                  <div className="flex items-center space-x-3">
                                    <div
                                      className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                        allDelivered
                                          ? "bg-emerald-500/20"
                                          : allOutForDelivery
                                            ? "bg-blue-100"
                                            : allReady
                                              ? "bg-emerald-500/20"
                                              : hasReady || hasDelivered
                                                ? "bg-blue-600/20"
                                                : "bg-slate-200"
                                      }`}
                                    >
                                      <Package
                                        className={`w-4 h-4 ${
                                          allDelivered
                                            ? "text-emerald-400"
                                            : allOutForDelivery
                                              ? "text-blue-600"
                                              : allReady
                                                ? "text-emerald-400"
                                                : hasReady || hasDelivered
                                                  ? "text-blue-600"
                                                  : "text-slate-500"
                                        }`}
                                      />
                                    </div>
                                    <div className="text-left">
                                      <div className="flex items-center space-x-2">
                                        <span className="text-blue-600 font-mono font-semibold text-sm">
                                          {sku}
                                        </span>
                                        <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-medium">
                                          {items.length} items
                                        </span>
                                      </div>
                                      <p className="text-xs text-slate-400 mt-0.5">
                                        {firstItem.name || firstItem.item_name}
                                        {firstItem.color && (
                                          <span className="ml-1">
                                            • {firstItem.color}
                                          </span>
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-3">
                                    <div className="text-right">
                                      <p className="font-bold text-slate-900 text-sm">
                                        ${formatCurrency(totalPrice)}
                                      </p>
                                      <div className="flex items-center justify-end space-x-1 mt-0.5">
                                        {hasDelivered && (
                                          <span className="text-xs text-emerald-400">
                                            {statusCounts.delivered}✓
                                          </span>
                                        )}
                                        {hasOutForDelivery && (
                                          <span className="text-xs text-blue-600">
                                            {statusCounts.out_for_delivery}🚚
                                          </span>
                                        )}
                                        {hasReady && (
                                          <span className="text-xs text-emerald-400">
                                            {statusCounts.ready} ready
                                          </span>
                                        )}
                                        {statusCounts.waiting > 0 && (
                                          <span className="text-xs text-slate-500">
                                            {statusCounts.waiting}⏳
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <ChevronDown
                                      className={`w-5 h-5 text-slate-500 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                                    />
                                  </div>
                                </button>



                                {/* Drawer Content - Expanded items */}
                                <div
                                  className={`overflow-hidden transition-all duration-200 ease-in-out ${
                                    isExpanded
                                      ? "max-h-[2000px] opacity-100"
                                      : "max-h-0 opacity-0"
                                  }`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="p-2 space-y-2 bg-slate-100 border-t border-slate-200">
                                    {items.map((item, idx) => (
                                      <div key={item.id || idx}>
                                        {renderItemCard(item, true)}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            );
                          }

                          return null;
                        });
                      })()}
                    </div>

                    {/* Bulk fulfillment bar — sticky inside the drawer scroll area.
                        Replaces the old below-the-fold "Create Delivery Order" panel. */}
                    {!isEditingInModal && (
                      <BulkActionBar
                        items={selectedReceipt.items || []}
                        statusMap={getAllItemStatusesForReceipt(
                          selectedReceipt.items || [],
                          selectedReceipt.inventoryUnits || [],
                          allInventoryUnits,
                          selectedReceipt?.itemStatuses ||
                            selectedReceipt?.item_statuses ||
                            [],
                        )}
                        selected={selectedItemsForDelivery}
                        canRoute={tabVisibility.deliveries}
                        isCreatingRoute={isCreatingDeliveryFromReceipt}
                        onClear={clearSelectedItemsForDelivery}
                        onCreateRoute={handleCreateDeliveryFromReceipt}
                        onMarkReadyItem={(it) =>
                          handleMarkItemReady(selectedReceipt.id, it)
                        }
                        onMarkStatusItem={(it, status) =>
                          handleMarkUnitStatus(selectedReceipt.id, it, status, {
                            skipConfirm: true,
                          })
                        }
                      />
                    )}
                  </div>

                  {/* Delivery Services Display - View Mode */}
                  {!isEditingInModal &&
                    (selectedReceipt.deliveryServices ||
                      selectedReceipt.delivery_services) &&
                    (() => {
                      const services =
                        selectedReceipt.deliveryServices ||
                        selectedReceipt.delivery_services ||
                        {};
                      const hasAnyService =
                        services.delivery?.enabled ||
                        services.setup?.enabled ||
                        services.wastePickup?.enabled ||
                        services.pickup?.enabled;
                      if (!hasAnyService) return null;
                      return (
                        <div className="p-4 rounded-2xl bg-gradient-to-br from-blue-50 to-cyan-500/5 border border-slate-300">
                          <h3 className="text-sm font-semibold text-blue-600 uppercase tracking-wider mb-3 flex items-center space-x-2">
                            <Truck className="w-4 h-4" />
                            <span>Delivery Services</span>
                          </h3>
                          <div className="space-y-2 text-sm">
                            {services.delivery?.enabled && (
                              <div className="flex justify-between">
                                <span className="text-slate-500">Delivery</span>
                                <span className="text-slate-900">
                                  $
                                  {formatCurrency(
                                    services.delivery.price || 0,
                                  )}
                                </span>
                              </div>
                            )}
                            {services.setup?.enabled && (
                              <div className="flex justify-between">
                                <span className="text-slate-500">
                                  Setup / Assembly
                                </span>
                                <span className="text-slate-900">
                                  $
                                  {formatCurrency(
                                    services.setup.price || 0,
                                  )}
                                </span>
                              </div>
                            )}
                            {services.wastePickup?.enabled && (
                              <div className="flex justify-between">
                                <span className="text-slate-500">
                                  Waste Pickup
                                </span>
                                <span className="text-slate-900">
                                  $
                                  {formatCurrency(
                                    services.wastePickup.price || 0,
                                  )}
                                </span>
                              </div>
                            )}
                            {services.pickup?.enabled && (
                              <div className="flex justify-between">
                                <span className="text-slate-500">
                                  Customer Pickup
                                </span>
                                <span className="text-slate-900">
                                  $
                                  {formatCurrency(
                                    services.pickup.price || 0,
                                  )}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                  {/* Payment & Totals */}
                  <div className="p-4 rounded-2xl bg-gradient-to-br from-blue-50 to-cyan-500/5 border border-slate-300">
                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center space-x-2">
                      <CreditCard className="w-4 h-4" />
                      <span>Payment</span>
                    </h3>
                    <div className="space-y-2 text-sm">
                      {(() => {
                        const items =
                          editFormData?.items || selectedReceipt.items || [];
                        const services =
                          editFormData?.deliveryServices ||
                          selectedReceipt.deliveryServices ||
                          selectedReceipt.delivery_services;
                        const totals = calculateTotals(items, services);
                        return (
                          <>
                            <div className="flex justify-between">
                              <span className="text-slate-500">Subtotal</span>
                              <span className="text-slate-900">
                                ${formatCurrency(totals.subtotal)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-500">
                                Tax ({storeSettings.tax_rate || 8}%)
                              </span>
                              <span className="text-slate-900">
                                ${formatCurrency(totals.tax)}
                              </span>
                            </div>
                            {totals.servicesTotal > 0 && (
                              <div className="flex justify-between">
                                <span className="text-emerald-400">
                                  Services
                                </span>
                                <span className="text-emerald-400">
                                  ${formatCurrency(totals.servicesTotal)}
                                </span>
                              </div>
                            )}
                            <div className="flex justify-between border-t border-slate-300 pt-2 mt-2">
                              <span className="text-slate-900 font-bold">
                                Total
                              </span>
                              <span className="text-slate-900 font-bold text-lg">
                                ${formatCurrency(totals.total)}
                              </span>
                            </div>
                          </>
                        );
                      })()}
                      <div className="flex justify-between items-center mt-3 pt-3 border-t border-slate-300">
                        <span className="text-slate-500">Amount Paid</span>
                        <span className="text-slate-600 font-semibold">
                          $
                          {formatCurrency(
                            parseFloat(
                              editFormData?.amountPaid ||
                                selectedReceipt.amountPaid ||
                                selectedReceipt.amount_paid,
                            ) || 0
                          )}
                        </span>
                      </div>
                      {(() => {
                        const items =
                          editFormData?.items || selectedReceipt.items || [];
                        const services =
                          editFormData?.deliveryServices ||
                          selectedReceipt.deliveryServices ||
                          selectedReceipt.delivery_services;
                        const totals = calculateTotals(items, services);
                        const paid =
                          Math.round(
                            (parseFloat(
                              editFormData?.amountPaid ||
                                selectedReceipt.amountPaid ||
                                selectedReceipt.amount_paid,
                            ) || 0) * 100,
                          ) / 100;
                        const balance =
                          Math.round((totals.total - paid) * 100) / 100;
                        return balance > 0 ? (
                          <div className="flex justify-between">
                            <span className="text-slate-500 font-medium">
                              Balance Due
                            </span>
                            <span className="text-slate-500 font-bold">
                              ${formatCurrency(balance)}
                            </span>
                          </div>
                        ) : (
                          <div className="flex justify-between">
                            <span className="text-emerald-400 font-medium">
                              Status
                            </span>
                            <span className="text-emerald-400 font-bold flex items-center space-x-1">
                              <CheckCircle className="w-4 h-4" />
                              <span>Paid in Full</span>
                            </span>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Payment History Section */}
                    {(() => {
                      const paymentHistory =
                        editFormData?.paymentHistory ||
                        selectedReceipt.payment_history ||
                        selectedReceipt.paymentHistory ||
                        [];
                      return (
                        <div className="mt-4 pt-4 border-t border-slate-300">
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-xs text-slate-600 font-semibold uppercase tracking-wider flex items-center gap-1.5">
                              <List className="w-3.5 h-3.5" />
                              Payment History
                            </p>
                            {isEditingInModal && (
                              <button
                                data-tip="Shows a form to record a payment on this receipt"
                                onClick={() =>
                                  setShowPaymentForm(!showPaymentForm)
                                }
                                className="text-xs px-2 py-1 rounded-lg bg-blue-50 border border-blue-300 text-blue-600 hover:bg-blue-100 transition-colors flex items-center gap-1"
                              >
                                <Plus className="w-3 h-3" />
                                Add Payment
                              </button>
                            )}
                          </div>

                          {/* Add Payment Form */}
                          {isEditingInModal && showPaymentForm && (
                            <div className="mb-3 p-3 rounded-xl bg-emerald-500/10 border border-slate-300 space-y-2">
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="text-xs text-slate-500 mb-1 block">
                                    Amount *
                                  </label>
                                  <div className="flex items-center">
                                    <span className="text-slate-500 mr-1">
                                      $
                                    </span>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={paymentFormData.amount}
                                      onChange={(e) =>
                                        setPaymentFormData({
                                          ...paymentFormData,
                                          amount: e.target.value,
                                        })
                                      }
                                      placeholder="0.00"
                                      className="w-full px-2 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 text-sm focus:outline-none focus:border-blue-500"
                                    />
                                  </div>
                                </div>
                                <div>
                                  <label className="text-xs text-slate-500 mb-1 block">
                                    Date
                                  </label>
                                  <input
                                    type="date"
                                    value={paymentFormData.date}
                                    onChange={(e) =>
                                      setPaymentFormData({
                                        ...paymentFormData,
                                        date: e.target.value,
                                      })
                                    }
                                    className="w-full px-2 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 text-sm focus:outline-none focus:border-blue-500"
                                  />
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="text-xs text-slate-500 mb-1 block">
                                    Payment Type
                                  </label>
                                  <select
                                    value={
                                      paymentFormData.paymentType || "cash"
                                    }
                                    onChange={(e) =>
                                      setPaymentFormData({
                                        ...paymentFormData,
                                        paymentType: e.target.value,
                                      })
                                    }
                                    className="w-full px-2 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 text-sm focus:outline-none focus:border-blue-500"
                                  >
                                    <option value="cash">Cash</option>
                                    <option value="card">Card</option>
                                    <option value="check">Check</option>
                                    <option value="financing">Financing</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="text-xs text-slate-500 mb-1 block">
                                    Note (optional)
                                  </label>
                                  <input
                                    type="text"
                                    value={paymentFormData.note}
                                    onChange={(e) =>
                                      setPaymentFormData({
                                        ...paymentFormData,
                                        note: e.target.value,
                                      })
                                    }
                                    placeholder="e.g. Deposit..."
                                    className="w-full px-2 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 text-sm placeholder-slate-400 focus:outline-none focus:border-blue-500"
                                  />
                                </div>
                              </div>
                              <div className="flex gap-2 pt-1">
                                <button
                                  data-tip="Adds this payment to the receipt's payment history"
                                  onClick={() => handleAddPayment("edit")}
                                  disabled={
                                    !paymentFormData.amount ||
                                    parseFloat(paymentFormData.amount) <= 0
                                  }
                                  className="flex-1 py-1.5 rounded-lg bg-blue-50 border border-blue-300 text-emerald-400 text-xs font-semibold hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  Record Payment
                                </button>
                                <button
                                  onClick={() => {
                                    setShowPaymentForm(false);
                                    setPaymentFormData({
                                      amount: "",
                                      date: new Date()
                                        .toISOString()
                                        .split("T")[0],
                                      note: "",
                                      paymentType: "cash",
                                    });
                                  }}
                                  className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-500 text-xs hover:bg-white/20 transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Payment List */}
                          {paymentHistory.length > 0 ? (
                            <div className="space-y-2">
                              {paymentHistory.map((payment, idx) => (
                                <div
                                  key={payment.id || idx}
                                  className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50 border border-slate-200"
                                >
                                  <div className="flex items-center gap-3">
                                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                                    <div>
                                      <p className="text-slate-900 text-sm font-semibold">
                                        ${formatCurrency(payment.amount)}
                                      </p>
                                      <p className="text-slate-400 text-xs">
                                        {new Date(
                                          payment.date,
                                        ).toLocaleDateString("en-US", {
                                          month: "short",
                                          day: "numeric",
                                          year: "numeric",
                                        })}
                                        {payment.paymentType && (
                                          <span className="ml-1 px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 text-[10px] uppercase font-medium">
                                            {payment.paymentType}
                                          </span>
                                        )}
                                        {payment.note && ` • ${payment.note}`}
                                      </p>
                                    </div>
                                  </div>
                                  {isEditingInModal && (
                                    <button
                                      aria-label="Remove payment"
                                      onClick={() => {
                                        if (
                                          window.confirm(
                                            `Remove payment of $${formatCurrency(payment.amount)}?`,
                                          )
                                        ) {
                                          handleRemovePayment(
                                            payment.id,
                                            "edit",
                                          );
                                        }
                                      }}
                                      className="p-1.5 rounded-lg bg-red-500/10 text-red-600 hover:bg-red-100 transition-colors"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-slate-400 text-xs text-center py-2">
                              No payment history recorded
                            </p>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Delivery Info & Preferences */}
                  <div className="space-y-3">
                    {/* Customer Contact Info */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 rounded-xl bg-slate-50 border border-slate-300">
                        <p className="text-xs text-slate-400 mb-1">Email</p>
                        {isEditingInModal && editFormData ? (
                          <input
                            type="email"
                            value={editFormData.customerEmail || ""}
                            onChange={(e) =>
                              setEditFormData({
                                ...editFormData,
                                customerEmail: e.target.value,
                              })
                            }
                            placeholder="customer@email.com"
                            className="w-full bg-transparent border-b border-gray-600 text-slate-900 text-sm focus:outline-none focus:border-blue-500 placeholder-gray-600"
                          />
                        ) : (
                          <p className="text-slate-900 font-semibold text-sm truncate">
                            {selectedReceipt.customerEmail ||
                              selectedReceipt.customer_email || (
                                <span className="text-slate-400 italic">
                                  Not provided
                                </span>
                              )}
                          </p>
                        )}
                      </div>
                      <div className="p-3 rounded-xl bg-slate-50 border border-slate-300">
                        <p className="text-xs text-slate-400 mb-1">
                          Delivery Address
                        </p>
                        {isEditingInModal && editFormData ? (
                          <input
                            type="text"
                            value={editFormData.deliveryAddress || ""}
                            onChange={(e) =>
                              setEditFormData({
                                ...editFormData,
                                deliveryAddress: e.target.value,
                              })
                            }
                            placeholder="123 Main St, City, CA"
                            className="w-full bg-transparent border-b border-gray-600 text-slate-900 text-sm focus:outline-none focus:border-blue-500 placeholder-gray-600"
                          />
                        ) : (
                          <p className="text-slate-900 font-semibold text-sm truncate">
                            {selectedReceipt.deliveryAddress ||
                              selectedReceipt.delivery_address || (
                                <span className="text-slate-400 italic">
                                  Not provided
                                </span>
                              )}
                          </p>
                        )}
                      </div>
                      <div className="p-3 rounded-xl bg-slate-50 border border-slate-300">
                        <p className="text-xs text-slate-400 mb-1">
                          Apt / Suite / Unit
                        </p>
                        {isEditingInModal && editFormData ? (
                          <input
                            type="text"
                            value={editFormData.apartmentNumber || ""}
                            onChange={(e) =>
                              setEditFormData({
                                ...editFormData,
                                apartmentNumber: e.target.value,
                              })
                            }
                            placeholder="Apt, Suite, Unit #"
                            className="w-full bg-transparent border-b border-gray-600 text-slate-900 text-sm focus:outline-none focus:border-blue-500 placeholder-gray-600"
                          />
                        ) : (
                          <p className="text-slate-900 font-semibold text-sm truncate">
                            {selectedReceipt.apartmentNumber ||
                              selectedReceipt.apartment_number || (
                                <span className="text-slate-400 italic">—</span>
                              )}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 rounded-xl bg-slate-50 border border-slate-300">
                        <p className="text-xs text-slate-400 mb-1">
                          Expected Delivery
                        </p>
                        {isEditingInModal && editFormData ? (
                          <input
                            type="date"
                            value={editFormData.deliveryDate}
                            onChange={(e) =>
                              setEditFormData({
                                ...editFormData,
                                deliveryDate: e.target.value,
                              })
                            }
                            className="w-full bg-transparent border-b border-gray-600 text-slate-900 text-sm focus:outline-none focus:border-blue-500"
                          />
                        ) : (
                          <p className="text-slate-900 font-semibold">
                            {selectedReceipt.deliveryDate ||
                              selectedReceipt.delivery_date ||
                              "TBD"}
                          </p>
                        )}
                      </div>
                      <div className="p-3 rounded-xl bg-slate-50 border border-slate-300">
                        <p className="text-xs text-slate-400 mb-1">
                          Receipt Status
                        </p>
                        <div className="flex items-center justify-between gap-2">
                          <p
                            className={`font-semibold text-sm ${selectedReceipt.sentAt || selectedReceipt.sent_at ? "text-blue-600" : "text-slate-500"}`}
                          >
                            {selectedReceipt.sentAt || selectedReceipt.sent_at
                              ? "✓ Sent"
                              : "Not sent"}
                          </p>
                          {!isEditingInModal && (
                            <button
                              onClick={() => handleSendSMS(selectedReceipt)}
                              disabled={
                                sendingSMSReceiptId === selectedReceipt.id
                              }
                              className={`flex items-center space-x-1 px-2 py-1 rounded-lg text-xs font-medium transition-all disabled:opacity-60 disabled:cursor-not-allowed ${
                                smsSentReceipts[selectedReceipt.id]
                                  ? "bg-blue-50 border border-blue-300 text-emerald-400"
                                  : selectedReceipt.sentAt ||
                                      selectedReceipt.sent_at
                                    ? "bg-blue-500/20 border border-blue-500/30 text-blue-600 hover:bg-blue-500/30"
                                    : "bg-blue-50 border border-blue-300 text-blue-600 hover:bg-blue-100"
                              }`}
                              title={
                                selectedReceipt.sentAt ||
                                selectedReceipt.sent_at
                                  ? "Resend Receipt via SMS"
                                  : "Send Receipt via SMS"
                              }
                            >
                              {sendingSMSReceiptId === selectedReceipt.id ? (
                                <>
                                  <RefreshCw className="w-3 h-3 animate-spin" />
                                  <span>Sending...</span>
                                </>
                              ) : smsSentReceipts[selectedReceipt.id] ? (
                                <>
                                  <CheckCircle className="w-3 h-3" />
                                  <span>Sent ✓</span>
                                </>
                              ) : selectedReceipt.sentAt ||
                                selectedReceipt.sent_at ? (
                                <>
                                  <RotateCcw className="w-3 h-3" />
                                  <span>Resend</span>
                                </>
                              ) : (
                                <>
                                  <Send className="w-3 h-3" />
                                  <span>Send</span>
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Delivery Preferences Row */}
                    {isEditingInModal && editFormData ? (
                      <div className="p-4 rounded-xl bg-gradient-to-br from-blue-50 to-cyan-500/10 border border-slate-300 space-y-3">
                        <p className="text-sm text-blue-600 font-semibold flex items-center gap-2">
                          <Truck className="w-4 h-4" />
                          Delivery Info
                        </p>

                        {/* Tax on Delivery Services Toggle */}
                        <div className="flex items-center justify-between p-2.5 bg-amber-50 rounded-lg border border-amber-200">
                          <div className="flex-1 pr-2">
                            <label
                              htmlFor="edit-tax-exempt-services"
                              className="text-xs font-semibold text-slate-800 cursor-pointer block"
                            >
                              Exempt delivery services from tax
                            </label>
                            <p className="text-[11px] text-slate-600 mt-0.5">
                              When on, delivery, setup, and waste removal are
                              NOT taxed.
                            </p>
                          </div>
                          <label
                            htmlFor="edit-tax-exempt-services"
                            className="relative inline-flex items-center cursor-pointer shrink-0"
                          >
                            <input
                              id="edit-tax-exempt-services"
                              type="checkbox"
                              className="sr-only peer"
                              checked={
                                !!editFormData.deliveryServices?.taxExempt
                              }
                              onChange={(e) =>
                                setEditFormData({
                                  ...editFormData,
                                  deliveryServices: {
                                    ...editFormData.deliveryServices,
                                    taxExempt: e.target.checked,
                                  },
                                })
                              }
                            />
                            <div className="w-10 h-5 bg-slate-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-amber-400 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-amber-500"></div>
                          </label>
                        </div>

                        {/* Delivery Services */}
                        <div className="space-y-2">
                          {/* Delivery */}
                          <div className="flex items-center justify-between p-2.5 bg-slate-100 rounded-lg border border-slate-300">
                            <label className="flex items-center gap-2.5 cursor-pointer flex-1">
                              <input
                                type="checkbox"
                                checked={
                                  editFormData.deliveryServices?.delivery
                                    ?.enabled || false
                                }
                                onChange={(e) =>
                                  setEditFormData({
                                    ...editFormData,
                                    deliveryServices: {
                                      ...editFormData.deliveryServices,
                                      delivery: {
                                        ...editFormData.deliveryServices
                                          ?.delivery,
                                        enabled: e.target.checked,
                                      },
                                    },
                                  })
                                }
                                className="w-4 h-4 rounded border-slate-300 bg-slate-100 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
                              />
                              <span className="text-slate-900 text-sm font-medium">
                                Delivery
                              </span>
                            </label>
                            <div className="flex items-center gap-1">
                              <span
                                className={
                                  editFormData.deliveryServices?.delivery
                                    ?.enabled
                                    ? "text-slate-500 text-sm"
                                    : "text-slate-300 text-sm"
                                }
                              >
                                $
                              </span>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={
                                  editFormData.deliveryServices?.delivery
                                    ?.price || ""
                                }
                                onChange={(e) =>
                                  setEditFormData({
                                    ...editFormData,
                                    deliveryServices: {
                                      ...editFormData.deliveryServices,
                                      delivery: {
                                        ...editFormData.deliveryServices
                                          ?.delivery,
                                        price: e.target.value,
                                      },
                                    },
                                  })
                                }
                                disabled={
                                  !editFormData.deliveryServices?.delivery
                                    ?.enabled
                                }
                                placeholder="0"
                                className={`w-16 px-2 py-1 border rounded text-sm text-right focus:outline-none ${editFormData.deliveryServices?.delivery?.enabled ? "bg-slate-50 border-slate-300 text-slate-900 focus:border-blue-500" : "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed"}`}
                              />
                            </div>
                          </div>

                          {/* Setup */}
                          <div className="flex items-center justify-between p-2.5 bg-slate-100 rounded-lg border border-slate-300">
                            <label className="flex items-center gap-2.5 cursor-pointer flex-1">
                              <input
                                type="checkbox"
                                checked={
                                  editFormData.deliveryServices?.setup
                                    ?.enabled || false
                                }
                                onChange={(e) =>
                                  setEditFormData({
                                    ...editFormData,
                                    deliveryServices: {
                                      ...editFormData.deliveryServices,
                                      setup: {
                                        ...editFormData.deliveryServices?.setup,
                                        enabled: e.target.checked,
                                      },
                                    },
                                  })
                                }
                                className="w-4 h-4 rounded border-slate-300 bg-slate-100 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
                              />
                              <span className="text-slate-900 text-sm font-medium">
                                Setup / Assembly
                              </span>
                            </label>
                            <div className="flex items-center gap-1">
                              <span
                                className={
                                  editFormData.deliveryServices?.setup?.enabled
                                    ? "text-slate-500 text-sm"
                                    : "text-slate-300 text-sm"
                                }
                              >
                                $
                              </span>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={
                                  editFormData.deliveryServices?.setup?.price ||
                                  ""
                                }
                                onChange={(e) =>
                                  setEditFormData({
                                    ...editFormData,
                                    deliveryServices: {
                                      ...editFormData.deliveryServices,
                                      setup: {
                                        ...editFormData.deliveryServices?.setup,
                                        price: e.target.value,
                                      },
                                    },
                                  })
                                }
                                disabled={
                                  !editFormData.deliveryServices?.setup?.enabled
                                }
                                placeholder="0"
                                className={`w-16 px-2 py-1 border rounded text-sm text-right focus:outline-none ${editFormData.deliveryServices?.setup?.enabled ? "bg-slate-50 border-slate-300 text-slate-900 focus:border-blue-500" : "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed"}`}
                              />
                            </div>
                          </div>

                          {/* Waste Pickup */}
                          <div className="flex items-center justify-between p-2.5 bg-slate-100 rounded-lg border border-slate-300">
                            <label className="flex items-center gap-2.5 cursor-pointer flex-1">
                              <input
                                type="checkbox"
                                checked={
                                  editFormData.deliveryServices?.wastePickup
                                    ?.enabled || false
                                }
                                onChange={(e) =>
                                  setEditFormData({
                                    ...editFormData,
                                    deliveryServices: {
                                      ...editFormData.deliveryServices,
                                      wastePickup: {
                                        ...editFormData.deliveryServices
                                          ?.wastePickup,
                                        enabled: e.target.checked,
                                      },
                                    },
                                  })
                                }
                                className="w-4 h-4 rounded border-slate-300 bg-slate-100 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
                              />
                              <span className="text-slate-900 text-sm font-medium">
                                Waste Pickup
                              </span>
                            </label>
                            <div className="flex items-center gap-1">
                              <span
                                className={
                                  editFormData.deliveryServices?.wastePickup
                                    ?.enabled
                                    ? "text-slate-500 text-sm"
                                    : "text-slate-300 text-sm"
                                }
                              >
                                $
                              </span>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={
                                  editFormData.deliveryServices?.wastePickup
                                    ?.price || ""
                                }
                                onChange={(e) =>
                                  setEditFormData({
                                    ...editFormData,
                                    deliveryServices: {
                                      ...editFormData.deliveryServices,
                                      wastePickup: {
                                        ...editFormData.deliveryServices
                                          ?.wastePickup,
                                        price: e.target.value,
                                      },
                                    },
                                  })
                                }
                                disabled={
                                  !editFormData.deliveryServices?.wastePickup
                                    ?.enabled
                                }
                                placeholder="0"
                                className={`w-16 px-2 py-1 border rounded text-sm text-right focus:outline-none ${editFormData.deliveryServices?.wastePickup?.enabled ? "bg-slate-50 border-slate-300 text-slate-900 focus:border-blue-500" : "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed"}`}
                              />
                            </div>
                          </div>

                          {/* Customer Pickup */}
                          <div className="flex items-center justify-between p-2.5 bg-slate-100 rounded-lg border border-slate-300">
                            <label className="flex items-center gap-2.5 cursor-pointer flex-1">
                              <input
                                type="checkbox"
                                checked={
                                  editFormData.deliveryServices?.pickup
                                    ?.enabled || false
                                }
                                onChange={(e) =>
                                  setEditFormData({
                                    ...editFormData,
                                    deliveryServices: {
                                      ...editFormData.deliveryServices,
                                      pickup: {
                                        ...editFormData.deliveryServices
                                          ?.pickup,
                                        enabled: e.target.checked,
                                      },
                                    },
                                  })
                                }
                                className="w-4 h-4 rounded border-slate-300 bg-slate-100 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
                              />
                              <span className="text-slate-900 text-sm font-medium">
                                Customer Pickup
                              </span>
                            </label>
                            <div className="flex items-center gap-1">
                              <span
                                className={
                                  editFormData.deliveryServices?.pickup?.enabled
                                    ? "text-slate-500 text-sm"
                                    : "text-slate-300 text-sm"
                                }
                              >
                                $
                              </span>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={
                                  editFormData.deliveryServices?.pickup
                                    ?.price || ""
                                }
                                onChange={(e) =>
                                  setEditFormData({
                                    ...editFormData,
                                    deliveryServices: {
                                      ...editFormData.deliveryServices,
                                      pickup: {
                                        ...editFormData.deliveryServices
                                          ?.pickup,
                                        price: e.target.value,
                                      },
                                    },
                                  })
                                }
                                disabled={
                                  !editFormData.deliveryServices?.pickup
                                    ?.enabled
                                }
                                placeholder="0"
                                className={`w-16 px-2 py-1 border rounded text-sm text-right focus:outline-none ${editFormData.deliveryServices?.pickup?.enabled ? "bg-slate-50 border-slate-300 text-slate-900 focus:border-blue-500" : "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed"}`}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Preferences */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-slate-400 mb-1">
                              Preferred Day
                            </label>
                            <input
                              type="text"
                              value={
                                editFormData.customerPreferences
                                  ?.preferredDeliveryDay || ""
                              }
                              onChange={(e) =>
                                setEditFormData({
                                  ...editFormData,
                                  customerPreferences: {
                                    ...editFormData.customerPreferences,
                                    preferredDeliveryDay: e.target.value,
                                  },
                                })
                              }
                              placeholder="e.g., Weekends, Mondays"
                              className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-slate-900 text-sm placeholder-slate-400 focus:outline-none focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-slate-400 mb-1">
                              Preferred Time
                            </label>
                            <select
                              value={
                                editFormData.customerPreferences
                                  ?.preferredTimeWindow || ""
                              }
                              onChange={(e) =>
                                setEditFormData({
                                  ...editFormData,
                                  customerPreferences: {
                                    ...editFormData.customerPreferences,
                                    preferredTimeWindow: e.target.value,
                                  },
                                })
                              }
                              className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-slate-900 text-sm focus:outline-none focus:border-blue-500"
                            >
                              <option value="" className="bg-white">
                                Any Time
                              </option>
                              <option
                                value="Morning (8am-12pm)"
                                className="bg-white"
                              >
                                Morning (8am-12pm)
                              </option>
                              <option
                                value="Afternoon (12pm-5pm)"
                                className="bg-white"
                              >
                                Afternoon (12pm-5pm)
                              </option>
                              <option
                                value="Evening (5pm-8pm)"
                                className="bg-white"
                              >
                                Evening (5pm-8pm)
                              </option>
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs text-slate-400 mb-1">
                            Delivery Instructions
                          </label>
                          <input
                            type="text"
                            value={
                              editFormData.customerPreferences?.notes || ""
                            }
                            onChange={(e) =>
                              setEditFormData({
                                ...editFormData,
                                customerPreferences: {
                                  ...editFormData.customerPreferences,
                                  notes: e.target.value,
                                },
                              })
                            }
                            placeholder="e.g., Use side entrance, call 30 min before arrival"
                            className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-slate-900 text-sm placeholder-slate-400 focus:outline-none focus:border-blue-500"
                          />
                        </div>
                      </div>
                    ) : (
                      (selectedReceipt.customerPreferences
                        ?.preferredDeliveryDay ||
                        selectedReceipt.customerPreferences
                          ?.preferredTimeWindow ||
                        selectedReceipt.customerPreferences?.notes) && (
                        <div className="p-3 rounded-xl bg-slate-50/50 border border-slate-300">
                          <p className="text-xs text-blue-600 font-medium mb-2">
                            🚚 Delivery Preferences
                          </p>
                          <div className="flex flex-wrap gap-3 text-sm">
                            {selectedReceipt.customerPreferences
                              ?.preferredDeliveryDay && (
                              <span className="text-slate-600">
                                <span className="text-slate-400">Day:</span>{" "}
                                {
                                  selectedReceipt.customerPreferences
                                    .preferredDeliveryDay
                                }
                              </span>
                            )}
                            {selectedReceipt.customerPreferences
                              ?.preferredTimeWindow && (
                              <span className="text-slate-600">
                                <span className="text-slate-400">Time:</span>{" "}
                                {
                                  selectedReceipt.customerPreferences
                                    .preferredTimeWindow
                                }
                              </span>
                            )}
                            {selectedReceipt.customerPreferences?.notes && (
                              <span className="text-slate-600 w-full">
                                <span className="text-slate-400">
                                  Instructions:
                                </span>{" "}
                                {selectedReceipt.customerPreferences.notes}
                              </span>
                            )}
                          </div>
                        </div>
                      )
                    )}
                  </div>

                  {/* Receipt Notes */}
                  <div className="p-4 rounded-xl bg-gradient-to-br from-blue-50 to-cyan-500/5 border border-slate-300">
                    <div className="flex items-center space-x-2 mb-2">
                      <Bot className="w-4 h-4 text-blue-600" />
                      <p className="text-sm font-semibold text-blue-600">
                        Receipt Notes
                      </p>
                      <span className="text-xs text-slate-400">
                        (AI uses for customer questions)
                      </span>
                    </div>
                    {isEditingInModal && editFormData ? (
                      <textarea
                        value={editFormData.internalNotes}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            internalNotes: e.target.value,
                          })
                        }
                        placeholder="Add notes about this order...&#10;e.g., Sofa in stock, chairs arriving Nov 16"
                        rows="3"
                        className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-slate-900 text-base placeholder-slate-400 focus:outline-none focus:border-blue-500 resize-none"
                      />
                    ) : (
                      <p className="text-slate-600 text-sm">
                        {selectedReceipt.internalNotes ||
                          selectedReceipt.internal_notes || (
                            <span className="text-slate-400 italic">
                              No notes added
                            </span>
                          )}
                      </p>
                    )}
                  </div>

                  {/* Delivery Signatures Button - Compact */}
                  {(selectedReceipt.delivery_signature ||
                    selectedReceipt.deliverySignature) && (
                    <button
                      data-tip="Opens the delivery signatures captured for this order"
                      onClick={() => openSignaturesDrawer(selectedReceipt)}
                      className="p-4 rounded-xl bg-slate-50 border border-slate-300 hover:bg-slate-100 transition-all w-full"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 rounded-lg bg-blue-50 border border-blue-300 flex items-center justify-center">
                            <FileText className="w-6 h-6 text-blue-600" />
                          </div>
                          <div className="text-left">
                            <p className="text-sm font-semibold text-slate-900">
                              Delivery Signatures
                            </p>
                            <p className="text-xs text-blue-600">
                              ✓ Customer confirmed delivery
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </button>
                  )}

                  {/* Customer Satisfaction Survey - Desktop */}
                  {(selectedReceipt?.delivery_status === "delivered" ||
                    selectedReceipt?.deliveryStatus === "delivered") && (
                    <SurveyFeedbackDisplay receipt={selectedReceipt} />
                  )}
                </div>
              </div>

              {/* ===== RIGHT PANEL: Chat (collapsible — toggled from header) ===== */}
              {isChatPanelOpen && (
              <div className="w-1/2 flex flex-col bg-slate-100">
                {/* Chat Header */}
                <div className="flex-shrink-0 px-5 py-4 border-b border-slate-300">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-slate-900 flex items-center space-x-2">
                      <MessageSquare className="w-5 h-5 text-blue-600" />
                      <span>Customer Chat</span>
                    </h3>
                    <div className="flex items-center space-x-2">
                      {selectedReceipt.is_ai_handling !== false &&
                      selectedReceipt.isAIHandling !== false ? (
                        <button
                          data-tip="Stops the AI replying so you answer this customer yourself"
                          onClick={handleTakeOverChat}
                          disabled={isTakingOver}
                          className="flex items-center space-x-2 px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-400 text-blue-600 hover:bg-blue-100 transition-colors text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isTakingOver ? (
                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Users className="w-3.5 h-3.5" />
                          )}
                          <span>
                            {isTakingOver ? "Taking Over..." : "Take Over"}
                          </span>
                        </button>
                      ) : (
                        <button
                          data-tip="Lets the AI reply to this customer again"
                          onClick={handleReturnToAI}
                          disabled={isReturningToAI}
                          className="flex items-center space-x-2 px-3 py-1.5 rounded-lg bg-blue-100 border border-blue-400/30 text-blue-600 hover:bg-slate-100 transition-colors text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isReturningToAI ? (
                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Bot className="w-3.5 h-3.5" />
                          )}
                          <span>
                            {isReturningToAI ? "Returning..." : "Return to AI"}
                          </span>
                        </button>
                      )}
                    </div>
                  </div>
                  {/* AI Status Banner */}
                  <div
                    className={`px-3 py-2 rounded-lg flex items-center space-x-2 ${
                      isStaffMode
                        ? "bg-emerald-50 border border-emerald-200"
                        : "bg-blue-50 border border-blue-200"
                    }`}
                  >
                    <div
                      className={`w-2 h-2 rounded-full animate-pulse ${isStaffMode ? "bg-emerald-500" : "bg-blue-500"}`}
                    ></div>
                    <span
                      className={`text-xs font-semibold ${isStaffMode ? "text-emerald-700" : "text-blue-700"}`}
                    >
                      {isStaffMode
                        ? "You are handling this conversation"
                        : "AI is handling this conversation"}
                    </span>
                  </div>
                  {/* SMS Opt-Out Warning Banner */}
                  {selectedReceipt?.sms_opted_out && (
                    <div className="mt-3 px-4 py-3 rounded-lg bg-red-50 border border-red-300 flex items-start space-x-3">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-red-800">
                          Customer Has Opted Out of SMS
                        </p>
                        <p className="text-xs text-red-600 mt-0.5">
                          This customer replied STOP. You cannot send messages
                          until they text START to re-subscribe.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Messages */}
                <div
                  ref={chatContainerRef}
                  onScroll={handleChatScroll}
                  className="flex-1 overflow-y-auto p-5 space-y-3"
                >
                  {!selectedReceipt.messages ||
                  selectedReceipt.messages.filter(
                    (m) => m.message_type !== "survey",
                  ).length === 0 ? (
                    isLoadingReceiptDetails ? (
                      /* Drawer opened instantly with seed data — the real
                         message thread is still on its way. Spinner here
                         prevents a misleading "No messages yet" flash on
                         receipts that DO have messages. */
                      <div className="flex flex-col items-center justify-center h-full text-center py-12">
                        <RefreshCw className="w-6 h-6 text-slate-300 animate-spin mb-3" />
                        <p className="text-slate-400 text-sm font-medium">
                          Loading conversation…
                        </p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full text-center py-12">
                        <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center mb-4">
                          <MessageSquare className="w-8 h-8 text-gray-600" />
                        </div>
                        <p className="text-slate-500 font-medium">
                          No messages yet
                        </p>
                        <p className="text-slate-400 text-sm mt-1">
                          Messages will appear here when the customer responds
                        </p>
                      </div>
                    )
                  ) : (
                    selectedReceipt.messages
                      .filter((m) => m.message_type !== "survey")
                      .map((message, index) => (
                        <div
                          key={index}
                          className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                        >
                          <div className="max-w-[80%]">
                            <div
                              className={`px-4 py-3 rounded-2xl ${
                                message.role === "user"
                                  ? "bg-blue-600 text-white"
                                  : message.role === "staff"
                                    ? "bg-blue-100 border border-blue-300 text-slate-900"
                                    : "bg-slate-100 border border-slate-300 text-slate-900"
                              }`}
                            >
                              {message.role === "staff" && (
                                <p className="text-xs text-blue-600 mb-1 font-semibold">
                                  {message.staffName || "Staff"}
                                </p>
                              )}
                              {message.role === "assistant" && (
                                <p className="text-xs text-blue-600 mb-1 font-semibold flex items-center space-x-1">
                                  <Bot className="w-3 h-3" />
                                  <span>AI Assistant</span>
                                </p>
                              )}
                              <p className="text-sm">
                                {renderMessageContent(
                                  message.content,
                                  message.role === "user",
                                )}
                              </p>
                            </div>
                            <p className="text-xs text-slate-400 mt-1 px-2">
                              {formatMessageTime(message)}
                            </p>
                          </div>
                        </div>
                      ))
                  )}
                </div>

                {/* Message Input */}
                {selectedReceipt?.sms_opted_out ? (
                  <div className="flex-shrink-0 p-4 border-t border-red-200 bg-red-50">
                    <div className="flex items-center justify-center space-x-2 text-red-700">
                      <AlertCircle className="w-5 h-5" />
                      <p className="text-sm font-medium">
                        Messaging Disabled - Customer Opted Out
                      </p>
                    </div>
                    <p className="text-xs text-red-500 text-center mt-1">
                      Customer must text START to re-subscribe before you can
                      message them
                    </p>
                  </div>
                ) : isStaffMode ? (
                  <div className="flex-shrink-0 p-4 border-t border-slate-300 bg-slate-200">
                    {/* Image Preview */}
                    {staffImage && (
                      <div className="mb-3 relative inline-block">
                        <img
                          src={URL.createObjectURL(staffImage)}
                          alt="Attachment preview"
                          className="max-h-32 rounded-lg border border-slate-300"
                        />
                        <button
                          aria-label="Remove attached image"
                          onClick={() => setStaffImage(null)}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    <div className="flex items-end space-x-3">
                      <textarea
                        value={staffMessage}
                        onChange={(e) => setStaffMessage(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSendStaffMessage();
                          }
                        }}
                        placeholder={
                          staffImage
                            ? "Add a caption (optional)..."
                            : "Type your message..."
                        }
                        rows="2"
                        className="flex-1 px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-400 transition-colors resize-none text-base"
                      />
                      {/* Image Upload Button */}
                      <label className="p-3 rounded-xl bg-slate-100 border border-slate-300 text-slate-600 hover:bg-slate-200 hover:border-slate-400 transition-all cursor-pointer">
                        <ImagePlus className="w-5 h-5" />
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              // Validate file size (max 5MB for MMS)
                              if (file.size > 5 * 1024 * 1024) {
                                addNotification({
                                  title: "Image Too Large",
                                  message: "Please select an image under 5MB",
                                  isEscalation: true,
                                });
                                return;
                              }
                              setStaffImage(file);
                            }
                            e.target.value = ""; // Reset input
                          }}
                        />
                      </label>
                      <button
                        aria-label="Send message"
                        onClick={handleSendStaffMessage}
                        disabled={
                          (!staffMessage.trim() && !staffImage) ||
                          sendingMessage
                        }
                        className="p-3 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Send className="w-5 h-5" />
                      </button>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">
                      {staffImage
                        ? "MMS with image will be sent to customer"
                        : "Message will be sent via SMS to customer"}
                    </p>
                  </div>
                ) : (
                  <div className="flex-shrink-0 p-4 border-t border-slate-200 bg-blue-50">
                    <div className="flex items-center justify-center space-x-2 text-blue-700">
                      <Bot className="w-5 h-5" />
                      <p className="text-sm font-medium">
                        AI is handling this conversation
                      </p>
                    </div>
                    <p className="text-xs text-slate-500 text-center mt-1">
                      Click "Take Over" to respond personally
                    </p>
                  </div>
                )}
              </div>
              )}
            </div>
          </div>

          {/* ============================================
              MOBILE CHAT OVERLAY - Full Screen
          ============================================ */}
          {!mobileV2 && isMobileChatOpen && (
            <div className="sm:hidden fixed inset-0 bg-white z-[110] flex flex-col animate-slideUp">
              {/* Status bar background */}
              <div
                className="flex-shrink-0 bg-white"
                style={{ height: "env(safe-area-inset-top)" }}
              />

              {/* Chat Header */}
              <div className="flex-shrink-0 px-4 py-3 border-b border-slate-300">
                <div className="flex items-center justify-between mb-3">
                  <button
                    onClick={() => setIsMobileChatOpen(false)}
                    className="flex items-center space-x-2 text-slate-500"
                  >
                    <ChevronLeft className="w-5 h-5" />
                    <span className="text-sm font-medium">Back</span>
                  </button>
                  <h3 className="text-lg font-bold text-slate-900">Chat</h3>
                  <button
                    aria-label="Close receipt"
                    onClick={() => {
                      setIsMobileChatOpen(false);
                      handleAttemptCloseReceiptModal();
                    }}
                    className="p-2 rounded-lg bg-red-100 text-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Take Over / Return to AI */}
                <div className="flex items-center space-x-2">
                  {selectedReceipt.is_ai_handling !== false &&
                  selectedReceipt.isAIHandling !== false ? (
                    <button
                      data-tip="Stops the AI replying so you answer this customer yourself"
                      onClick={handleTakeOverChat}
                      disabled={isTakingOver}
                      className="flex-1 flex items-center justify-center space-x-2 px-4 py-2.5 rounded-xl bg-emerald-100 border border-emerald-400 text-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isTakingOver ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Users className="w-4 h-4" />
                      )}
                      <span className="font-semibold text-sm">
                        {isTakingOver ? "Taking Over..." : "Take Over Chat"}
                      </span>
                    </button>
                  ) : (
                    <button
                      data-tip="Lets the AI reply to this customer again"
                      onClick={handleReturnToAI}
                      disabled={isReturningToAI}
                      className="flex-1 flex items-center justify-center space-x-2 px-4 py-2.5 rounded-xl bg-blue-100 border border-blue-400 text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isReturningToAI ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Bot className="w-4 h-4" />
                      )}
                      <span className="font-semibold text-sm">
                        {isReturningToAI ? "Returning..." : "Return to AI"}
                      </span>
                    </button>
                  )}
                </div>

                {/* Status */}
                <div
                  className={`mt-2 px-3 py-1.5 rounded-lg flex items-center justify-center space-x-2 ${
                    isStaffMode
                      ? "bg-emerald-50 border border-emerald-200"
                      : "bg-blue-50 border border-blue-200"
                  }`}
                >
                  <div
                    className={`w-2 h-2 rounded-full animate-pulse ${isStaffMode ? "bg-emerald-500" : "bg-blue-500"}`}
                  />
                  <span
                    className={`text-xs font-medium ${isStaffMode ? "text-emerald-700" : "text-blue-700"}`}
                  >
                    {isStaffMode ? "You are responding" : "AI is handling"}
                  </span>
                </div>
                {/* SMS Opt-Out Warning Banner - Mobile */}
                {selectedReceipt?.sms_opted_out && (
                  <div className="mt-2 px-3 py-2 rounded-lg bg-red-50 border border-red-300 flex items-start space-x-2">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-red-800">
                        Customer Opted Out
                      </p>
                      <p className="text-[10px] text-red-600">
                        Cannot message until they text START
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Messages */}
              <div
                ref={mobileChatContainerRef}
                onScroll={handleChatScroll}
                className="flex-1 overflow-y-auto p-4 space-y-3"
              >
                {!selectedReceipt.messages ||
                selectedReceipt.messages.filter(
                  (m) => m.message_type !== "survey",
                ).length === 0 ? (
                  isLoadingReceiptDetails ? (
                    /* Same instant-open guard as the desktop chat panel:
                       spinner until the real thread arrives. */
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <RefreshCw className="w-6 h-6 text-slate-300 animate-spin mb-3" />
                      <p className="text-slate-400 text-sm">
                        Loading conversation…
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <MessageSquare className="w-12 h-12 text-gray-600 mb-3" />
                      <p className="text-slate-500 text-sm">No messages yet</p>
                      <p className="text-slate-400 text-xs mt-1">
                        Messages appear when customer responds
                      </p>
                    </div>
                  )
                ) : (
                  selectedReceipt.messages
                    .filter((m) => m.message_type !== "survey")
                    .map((message, index) => (
                      <div
                        key={index}
                        className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                      >
                        <div className="max-w-[80%]">
                          <div
                            className={`p-3 rounded-2xl ${
                              message.role === "user"
                                ? "bg-blue-600 text-white"
                                : message.role === "staff"
                                  ? "bg-blue-100 border border-blue-300 text-slate-900"
                                  : "bg-slate-100 border border-slate-300 text-slate-900"
                            }`}
                          >
                            {message.role === "staff" && (
                              <p className="text-xs text-blue-600 mb-1 font-semibold">
                                {message.staffName}
                              </p>
                            )}
                            {message.role === "assistant" && (
                              <p className="text-xs text-blue-600 mb-1 font-semibold flex items-center space-x-1">
                                <Bot className="w-3 h-3" />
                                <span>AI</span>
                              </p>
                            )}
                            <p className="text-sm">
                              {renderMessageContent(
                                message.content,
                                message.role === "user",
                              )}
                            </p>
                          </div>
                          <p className="text-xs text-slate-400 mt-1 px-2">
                            {formatMessageTime(message)}
                          </p>
                        </div>
                      </div>
                    ))
                )}
              </div>

              {/* Message Input - Mobile */}
              {selectedReceipt?.sms_opted_out ? (
                <div
                  className="flex-shrink-0 p-4 border-t border-red-200 bg-red-50"
                  style={{
                    paddingBottom: "calc(env(safe-area-inset-bottom) + 1rem)",
                  }}
                >
                  <div className="flex items-center justify-center space-x-2 text-red-700">
                    <AlertCircle className="w-4 h-4" />
                    <p className="text-sm font-medium">Messaging Disabled</p>
                  </div>
                  <p className="text-xs text-red-500 text-center mt-1">
                    Customer must text START
                  </p>
                </div>
              ) : isStaffMode ? (
                <div
                  className="flex-shrink-0 p-4 border-t border-slate-300 bg-slate-50"
                  style={{
                    paddingBottom: "calc(env(safe-area-inset-bottom) + 1rem)",
                  }}
                >
                  {/* Image Preview */}
                  {staffImage && (
                    <div className="mb-3 relative inline-block">
                      <img
                        src={URL.createObjectURL(staffImage)}
                        alt="Attachment preview"
                        className="max-h-24 rounded-lg border border-slate-300"
                      />
                      <button
                        aria-label="Remove attached image"
                        onClick={() => setStaffImage(null)}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  <div className="flex items-end space-x-2">
                    <textarea
                      value={staffMessage}
                      onChange={(e) => setStaffMessage(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSendStaffMessage();
                        }
                      }}
                      placeholder={
                        staffImage ? "Add a caption..." : "Type your message..."
                      }
                      rows="2"
                      className="flex-1 px-4 py-3 bg-slate-100 border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-400 resize-none"
                    />
                    {/* Image Upload Button */}
                    <label className="p-3 rounded-xl bg-slate-200 border border-slate-300 text-slate-600 active:bg-slate-300 cursor-pointer">
                      <ImagePlus className="w-5 h-5" />
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            if (file.size > 5 * 1024 * 1024) {
                              addNotification({
                                title: "Image Too Large",
                                message: "Max 5MB",
                                isEscalation: true,
                              });
                              return;
                            }
                            setStaffImage(file);
                          }
                          e.target.value = "";
                        }}
                      />
                    </label>
                    <button
                      aria-label="Send message"
                      onClick={handleSendStaffMessage}
                      disabled={
                        (!staffMessage.trim() && !staffImage) || sendingMessage
                      }
                      className="p-3 rounded-xl bg-blue-600 text-white disabled:opacity-50"
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="flex-shrink-0 p-4 border-t border-slate-200 bg-blue-50"
                  style={{
                    paddingBottom: "calc(env(safe-area-inset-bottom) + 1rem)",
                  }}
                >
                  <div className="flex items-center justify-center space-x-2 text-blue-700">
                    <Bot className="w-4 h-4" />
                    <p className="text-sm font-medium">
                      AI is handling this conversation
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        </>
      )}

      {/* ==========================================
          PARTIAL DELIVERY MODAL - NEW
      ========================================== */}
      {showPartialDeliveryModal && partialDeliveryReceipt && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="w-full sm:max-w-lg max-h-[90vh] bg-white sm:rounded-3xl border-t sm:border border-slate-200 overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-slate-300 bg-blue-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-100 to-blue-50 border border-blue-300 flex items-center justify-center">
                    <Truck className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">
                      Mark Items Delivered
                    </h2>
                    <p className="text-sm text-slate-500">
                      {partialDeliveryReceipt.customer_name ||
                        partialDeliveryReceipt.customerName}
                    </p>
                  </div>
                </div>
                <button
                  aria-label="Close"
                  onClick={() => {
                    setShowPartialDeliveryModal(false);
                    setPartialDeliveryReceipt(null);
                    setSelectedDeliveryItems({});
                  }}
                  className="p-2 rounded-lg bg-slate-100 text-slate-500 hover:text-slate-800 hover:bg-white/20 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Item List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              <p className="text-slate-500 text-sm mb-4">
                Select the items being delivered today. Remaining items will be
                marked for a future delivery.
              </p>

              {(partialDeliveryReceipt.items || []).map((item, idx) => {
                // Use inventory_units status (source of truth for serialization)
                const skuKey = item.sku?.toLowerCase();
                const unitStatus =
                  partialDeliveryReceipt.inventoryUnitStatus?.[skuKey];
                const quantity = item.quantity || 1;

                // Determine item state from inventory units
                const deliveredCount = unitStatus?.delivered || 0;
                const reservedCount = unitStatus?.reserved || 0;
                const isFullyDelivered = deliveredCount >= quantity;
                const isPartiallyDelivered =
                  deliveredCount > 0 && deliveredCount < quantity;
                const hasUnitsReady = reservedCount > 0;
                const isSelected = selectedDeliveryItems[item.sku] || false;

                return (
                  <div
                    key={idx}
                    onClick={() =>
                      !isFullyDelivered &&
                      hasUnitsReady &&
                      toggleDeliveryItem(item.sku)
                    }
                    className={`p-4 rounded-xl border transition-all ${
                      isFullyDelivered
                        ? "bg-emerald-50 border-emerald-400 opacity-60 cursor-not-allowed"
                        : !hasUnitsReady
                          ? "bg-gray-500/10 border-gray-500/30 opacity-60 cursor-not-allowed"
                          : isSelected
                            ? "bg-blue-100 border-blue-400 ring-2 ring-blue-300 cursor-pointer"
                            : "bg-blue-600/10 border-blue-500/30 hover:border-blue-500 cursor-pointer"
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        {/* Checkbox */}
                        <div
                          className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center mt-0.5 ${
                            isFullyDelivered
                              ? "bg-green-500 border-green-500"
                              : isSelected
                                ? "bg-blue-600 border-blue-600"
                                : !hasUnitsReady
                                  ? "bg-gray-600 border-gray-600"
                                  : "border-gray-500"
                          }`}
                        >
                          {(isFullyDelivered || isSelected) && (
                            <CheckCircle className="w-4 h-4 text-white" />
                          )}
                          {!hasUnitsReady && !isFullyDelivered && (
                            <Clock className="w-3 h-3 text-gray-400" />
                          )}
                        </div>

                        <div className="flex-1">
                          <p className="text-slate-900 font-semibold">
                            {item.item_name || item.name}
                          </p>
                          <p className="text-slate-500 text-sm">
                            SKU: {item.sku}
                          </p>
                          {item.color && (
                            <p className="text-slate-400 text-xs mt-1">
                              Color: {item.color}
                            </p>
                          )}
                          {(item.manufacturer?.name ||
                            manufacturers.find(
                              (m) =>
                                m.id === item.manufacturer_id ||
                                m.id === item.manufacturerId,
                            )?.name) && (
                            <p className="text-blue-500 text-xs mt-0.5">
                              {item.manufacturer?.name ||
                                manufacturers.find(
                                  (m) =>
                                    m.id === item.manufacturer_id ||
                                    m.id === item.manufacturerId,
                                )?.name}
                            </p>
                          )}
                          {quantity > 1 && (
                            <p className="text-slate-400 text-xs">
                              Qty: {quantity}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Status Badge */}
                      <div
                        className={`px-2 py-1 rounded-lg text-xs font-semibold ${
                          isFullyDelivered
                            ? "bg-emerald-100 text-emerald-600"
                            : isPartiallyDelivered
                              ? "bg-blue-100 text-blue-600"
                              : hasUnitsReady
                                ? "bg-blue-100 text-blue-600"
                                : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {isFullyDelivered
                          ? "✓ Delivered"
                          : isPartiallyDelivered
                            ? `${deliveredCount}/${quantity} Delivered`
                            : hasUnitsReady
                              ? `${reservedCount} Ready`
                              : "Not in Stock"}
                      </div>
                    </div>

                    {/* Show unit details if partially delivered */}
                    {isPartiallyDelivered && (
                      <p className="text-blue-600 text-xs mt-2 ml-9">
                        {reservedCount} more unit
                        {reservedCount !== 1 ? "s" : ""} ready to deliver
                      </p>
                    )}

                    {!hasUnitsReady && !isFullyDelivered && (
                      <p className="text-slate-600 text-xs mt-2 ml-9">
                        Waiting for inventory - cannot deliver yet
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-300 bg-slate-200">
              {/* Summary */}
              <div className="mb-4 p-3 bg-slate-50 rounded-xl">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Selected for delivery:</span>
                  <span className="text-blue-600 font-semibold">
                    {
                      Object.values(selectedDeliveryItems).filter(Boolean)
                        .length
                    }{" "}
                    item(s)
                  </span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-slate-500">Already delivered:</span>
                  <span className="text-emerald-600 font-semibold">
                    {(() => {
                      // Count items fully delivered from inventory_units
                      let delivered = 0;
                      (partialDeliveryReceipt.items || []).forEach((item) => {
                        const skuKey = item.sku?.toLowerCase();
                        const unitStatus =
                          partialDeliveryReceipt.inventoryUnitStatus?.[skuKey];
                        const qty = item.quantity || 1;
                        if (unitStatus && unitStatus.delivered >= qty) {
                          delivered++;
                        }
                      });
                      return delivered;
                    })()}{" "}
                    of {(partialDeliveryReceipt.items || []).length} item(s)
                  </span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-slate-500">Waiting for stock:</span>
                  <span className="text-slate-600 font-semibold">
                    {(() => {
                      // Count items with no reserved units
                      let waiting = 0;
                      (partialDeliveryReceipt.items || []).forEach((item) => {
                        const skuKey = item.sku?.toLowerCase();
                        const unitStatus =
                          partialDeliveryReceipt.inventoryUnitStatus?.[skuKey];
                        const qty = item.quantity || 1;
                        const isFullyDelivered =
                          unitStatus && unitStatus.delivered >= qty;
                        const hasUnitsReady =
                          unitStatus && unitStatus.reserved > 0;
                        if (!isFullyDelivered && !hasUnitsReady) {
                          waiting++;
                        }
                      });
                      return waiting;
                    })()}{" "}
                    item(s)
                  </span>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowPartialDeliveryModal(false);
                    setPartialDeliveryReceipt(null);
                    setSelectedDeliveryItems({});
                  }}
                  className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-900 font-semibold hover:bg-white/20 transition-colors"
                >
                  Cancel
                </button>
                <button
                  data-tip="Marks the selected items as delivered"
                  onClick={handlePartialDelivery}
                  disabled={
                    isProcessingPartialDelivery ||
                    Object.values(selectedDeliveryItems).filter(Boolean)
                      .length === 0
                  }
                  className="flex-1 py-3 rounded-xl bg-blue-600 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg hover:bg-blue-700 transition-all flex items-center justify-center space-x-2"
                >
                  {isProcessingPartialDelivery ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <Truck className="w-4 h-4" />
                      <span>Confirm Delivery</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==========================================
          INVOICE SCANNER MODAL - NEW
      ========================================== */}
      {showScanner && (
        <InvoiceScanner
          storeId={currentStore?.id}
          onOrderSkus={onOrderSkusForScanner}
          onClose={() => setShowScanner(false)}
          onInvoiceProcessed={(result) => {
            console.log(
              `📦 Invoice processed: ${result.unitsCreated || 0} units created, ${result.unitsAssigned || 0} auto-assigned`,
            );
            loadInvoices();
            loadReceipts();
            setShowScanner(false);
          }}
        />
      )}

      {/* Mobile Invoice Detail Modal */}
      {/*No test data yet - remove this when we have test data*/}
      {selectedMobileInvoice && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[100] flex items-end sm:items-center justify-center">
          <div className="w-full sm:max-w-lg max-h-[85vh] bg-white sm:rounded-3xl border-t sm:border border-slate-200 overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-4 border-b border-slate-300 flex items-start justify-between bg-slate-50">
              <div>
                <h3 className="text-lg font-bold text-slate-900">
                  {selectedMobileInvoice.supplier_name || "Invoice Details"}
                </h3>
                <p className="text-slate-400 text-sm">
                  #{selectedMobileInvoice.invoice_number}
                </p>
              </div>
              <button
                aria-label="Close"
                onClick={() => setSelectedMobileInvoice(null)}
                className="p-2 rounded-xl bg-slate-100 text-slate-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {(selectedMobileInvoice.supplier_invoice_items || []).map(
                (item, idx) => (
                  <div
                    key={idx}
                    className={`p-4 rounded-xl border ${
                      item.status === "matched"
                        ? "bg-emerald-500/10 border-slate-300"
                        : "bg-slate-50 border-slate-300"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-mono font-bold ${
                              item.status === "matched"
                                ? "bg-blue-100 text-emerald-300"
                                : "bg-blue-600/30 text-blue-600"
                            }`}
                          >
                            {item.sku}
                          </span>
                          {item.status === "matched" && (
                            <span className="text-emerald-400 text-xs">
                              ✓ Matched
                            </span>
                          )}
                        </div>
                        <p className="text-slate-900 text-sm">
                          {item.description || "No description"}
                        </p>
                        <p className="text-slate-400 text-xs mt-1">
                          Qty: {item.quantity} • $
                          {formatCurrency(item.unit_price || 0)} each
                        </p>
                      </div>
                      <p className="text-slate-900 font-semibold">
                        ${formatCurrency(item.amount || 0)}
                      </p>
                    </div>
                  </div>
                ),
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-slate-300 bg-slate-50">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-slate-500">Total Items</span>
                <span className="text-slate-900 font-semibold">
                  {selectedMobileInvoice.supplier_invoice_items?.length || 0}
                </span>
              </div>
              <button
                onClick={() => setSelectedMobileInvoice(null)}
                className="w-full py-3 rounded-xl bg-slate-100 text-slate-900 font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Item Modal */}
      {showItemModal && (
        <div
          className="fixed inset-0 bg-slate-900/50 z-[120] overflow-hidden sm:flex sm:items-stretch sm:justify-end item-drawer-backdrop"
          style={{ touchAction: "none" }}
          onClick={(e) => e.target === e.currentTarget && handleCloseItemModal()}
        >
          <style>{`
            @keyframes itemDrawerSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
            @keyframes itemDrawerSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
            @keyframes itemDrawerBackdropFade { from { opacity: 0; } to { opacity: 1; } }
            .item-drawer-backdrop { animation: itemDrawerBackdropFade 160ms ease-out; }
            .item-drawer-panel { animation: itemDrawerSlideUp 240ms cubic-bezier(0.32, 0.72, 0, 1); }
            @media (min-width: 640px) {
              .item-drawer-panel { animation: itemDrawerSlideIn 240ms cubic-bezier(0.32, 0.72, 0, 1); }
            }
          `}</style>
          <div
            className="absolute inset-x-0 bottom-0 sm:static sm:inset-auto bg-white rounded-t-3xl sm:rounded-none border-t sm:border-t-0 sm:border-l border-slate-200 sm:border-slate-300 w-full sm:w-[440px] md:w-[480px] max-h-[calc(100vh-env(safe-area-inset-top,0px)-12px)] sm:max-h-screen sm:h-screen flex flex-col shadow-2xl overflow-hidden item-drawer-panel"
            style={{ touchAction: "pan-y", overscrollBehavior: "contain" }}
          >
            {/* Drag handle - mobile only */}
            <div className="sm:hidden flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-slate-300" />
            </div>
            {/* Modal Header */}
            <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-slate-200 bg-slate-50">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
                  <Package className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">
                    {editingItemIndex !== null ? "Edit Item" : "Add Item"}
                  </h3>
                  <p className="text-xs text-slate-500">
                    {editingItemIndex !== null
                      ? "Update item details"
                      : "Enter item details"}
                  </p>
                </div>
              </div>
              <button
                aria-label="Close"
                onClick={handleCloseItemModal}
                className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            {/* Modal Body - Form */}
            <div
              className="flex-1 overflow-y-auto p-4 space-y-4"
              style={{ WebkitOverflowScrolling: "touch", overscrollBehavior: "contain" }}
            >
              {/* Item Name + SKU with catalog autocomplete */}
              <ItemAutocomplete
                itemFormData={itemFormData}
                setItemFormData={setItemFormData}
                onPick={(p) => {
                  const mfrId = resolveBrandToManufacturerId(p.brand);
                  console.log("[brand autofill]", {
                    pickedBrand: p.brand,
                    matchedManufacturerId: mfrId,
                    storeManufacturers: manufacturers.map((m) => m.name),
                  });
                  if (mfrId) {
                    setItemFormData((prev) => ({
                      ...prev,
                      manufacturerId: mfrId,
                      manufacturer_id: mfrId,
                    }));
                  }
                }}
              />

              {/* Color */}
              <div>
                <label className="block text-sm text-slate-500 mb-2">
                  Color
                </label>
                <input
                  type="text"
                  value={itemFormData.color}
                  onChange={(e) =>
                    setItemFormData({
                      ...itemFormData,
                      color: e.target.value,
                    })
                  }
                  placeholder="e.g. Brown"
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>

              {/* Manufacturer (Required) */}
              <div>
                <label className="block text-sm text-slate-500 mb-2">
                  Manufacturer / Brand <span className="text-red-400">*</span>
                </label>
                {!showNewManufacturerInput ? (
                  <div className="flex gap-2">
                    <select
                      value={itemFormData.manufacturerId || ""}
                      onChange={(e) => {
                        if (e.target.value === "__new__") {
                          setShowNewManufacturerInput(true);
                        } else {
                          setItemFormData({
                            ...itemFormData,
                            manufacturerId: e.target.value || null,
                          });
                        }
                      }}
                      className={`flex-1 px-4 py-3 bg-slate-50 border rounded-xl text-slate-900 focus:outline-none focus:border-blue-500 transition-colors ${!itemFormData.manufacturerId ? "border-red-300" : "border-slate-300"}`}
                    >
                      <option value="">— Select manufacturer —</option>
                      {manufacturers.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name}
                        </option>
                      ))}
                      <option value="__new__">+ Add new...</option>
                    </select>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newManufacturerName}
                      onChange={(e) => setNewManufacturerName(e.target.value)}
                      onKeyDown={(e) =>
                        e.key === "Enter" && handleCreateNewManufacturer("item")
                      }
                      placeholder="e.g. Ashley, Coaster..."
                      className="flex-1 px-4 py-3 bg-slate-50 border border-blue-400 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
                      autoFocus
                      disabled={isCreatingManufacturer}
                    />
                    <button
                      data-tip="Creates this manufacturer and selects it for the item"
                      onClick={() => handleCreateNewManufacturer("item")}
                      disabled={
                        !newManufacturerName.trim() || isCreatingManufacturer
                      }
                      className="px-3 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm font-medium"
                    >
                      {isCreatingManufacturer ? "..." : "Add"}
                    </button>
                    <button
                      aria-label="Cancel new manufacturer"
                      onClick={() => {
                        setShowNewManufacturerInput(false);
                        setNewManufacturerName("");
                      }}
                      className="px-3 py-2 bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 transition-colors text-sm"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Quantity & Price Row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-slate-500 mb-2">
                    Quantity
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={itemFormData.quantity}
                    onChange={(e) =>
                      setItemFormData({
                        ...itemFormData,
                        quantity: e.target.value,
                      })
                    }
                    placeholder="1"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-500 mb-2">
                    Price ($)
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={itemFormData.price}
                    onChange={(e) =>
                      setItemFormData({
                        ...itemFormData,
                        price: e.target.value,
                      })
                    }
                    placeholder="0.00"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
                  />
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div
              className="flex-shrink-0 flex flex-wrap items-center justify-between gap-3 p-4 border-t border-slate-300 bg-white"
              style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)" }}
            >
              <div className="flex-shrink-0 min-h-[2.5rem] flex items-center">
                {itemModalTarget === "edit" &&
                  editingItemIndex !== null &&
                  editFormData?.items?.[editingItemIndex]?.id &&
                  selectedReceipt?.id &&
                  (() => {
                    const base = editFormData.items[editingItemIndex];
                    const mergedForReady = {
                      ...base,
                      name: itemFormData.name,
                      item_name: itemFormData.name,
                      sku: itemFormData.sku,
                      color: itemFormData.color,
                      quantity:
                        parseInt(String(itemFormData.quantity), 10) || 1,
                      price: itemFormData.price,
                      manufacturerId: itemFormData.manufacturerId,
                      manufacturer_id: itemFormData.manufacturerId,
                    };
                    // Status comes from item_statuses (5th arg) — the sole
                    // fulfillment source. The old call here omitted it, so
                    // every item read as "waiting" and the button never
                    // changed. Now the footer reflects the real state:
                    // waiting → Mark as Ready, ready → Undo Ready,
                    // on-truck / completed → informational (no action).
                    const readyInfo = getItemStatusByInventory(
                      mergedForReady,
                      selectedReceipt.inventoryUnits || [],
                      allInventoryUnits || [],
                      new Set(),
                      selectedReceipt.itemStatuses || [],
                    );
                    const itemStatus = readyInfo?.status || "waiting";
                    if (itemStatus === "ready") {
                      return (
                        <button
                          type="button"
                          onClick={() =>
                            handleUndoReady(selectedReceipt.id, mergedForReady)
                          }
                          disabled={markingItemReady}
                          title="Move this line back to Waiting"
                          className="px-4 py-2.5 rounded-xl border border-amber-300 bg-amber-50 text-amber-800 font-semibold text-sm hover:bg-amber-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {markingItemReady ? "Working…" : "Undo Ready"}
                        </button>
                      );
                    }
                    if (itemStatus === "out_for_delivery") {
                      return (
                        <span className="inline-flex items-center px-4 py-2.5 rounded-xl border border-indigo-200 bg-indigo-50 text-indigo-700 font-semibold text-sm">
                          On truck
                        </span>
                      );
                    }
                    if (itemStatus === "delivered" || itemStatus === "picked_up") {
                      return (
                        <span className="inline-flex items-center px-4 py-2.5 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 font-semibold text-sm">
                          {itemStatus === "picked_up" ? "Picked up" : "Delivered"}
                        </span>
                      );
                    }
                    return (
                      <button
                        data-tip="Marks this line as ready for delivery or pickup"
                        type="button"
                        onClick={() =>
                          handleMarkItemReady(
                            selectedReceipt.id,
                            mergedForReady,
                          )
                        }
                        disabled={markingItemReady}
                        className="px-4 py-2.5 rounded-xl border border-emerald-300 bg-emerald-50 text-emerald-800 font-semibold text-sm hover:bg-emerald-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {markingItemReady ? "Working…" : "Mark as Ready"}
                      </button>
                    );
                  })()}
              </div>
              <div className="flex items-center justify-end space-x-3 ml-auto">
                <button
                  onClick={handleCloseItemModal}
                  className="px-4 py-2.5 rounded-xl bg-slate-100 text-slate-900 hover:bg-white/20 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveItemFromModal}
                  disabled={
                    !itemFormData.name.trim() || !itemFormData.manufacturerId
                  }
                  className="px-6 py-2.5 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editingItemIndex !== null ? "Update Item" : "Add Item"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Add SKUs Modal */}
      {showBulkAddModal && (
        <div className="fixed inset-0 bg-slate-900/50 z-[120] flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-md overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-slate-50">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <List className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">
                    Bulk Add Items
                  </h3>
                  <p className="text-xs text-slate-500">
                    Paste multiple SKUs at once
                  </p>
                </div>
              </div>
              <button
                aria-label="Close"
                onClick={() => {
                  setShowBulkAddModal(false);
                  setBulkSkuInput("");
                }}
                className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-slate-500 mb-2">
                  Paste or type SKUs (comma, space, or line separated)
                </label>
                <textarea
                  value={bulkSkuInput}
                  onChange={(e) => setBulkSkuInput(e.target.value)}
                  placeholder="2392K-1, 2392K-2, MT-G06T, BR-60T7"
                  rows={4}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors resize-none font-mono text-base"
                  autoFocus
                />
              </div>

              {/* Preview count */}
              {bulkSkuInput.trim() && (
                <div className="flex items-center space-x-2 text-sm">
                  <Package className="w-4 h-4 text-blue-600" />
                  <span className="text-slate-500">
                    {
                      bulkSkuInput.split(/[\s,\t\n]+/).filter((s) => s.trim())
                        .length
                    }{" "}
                    items detected
                  </span>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end space-x-3 p-4 border-t border-slate-300">
              <button
                onClick={() => {
                  setShowBulkAddModal(false);
                  setBulkSkuInput("");
                }}
                className="px-4 py-2 rounded-lg bg-slate-100 text-slate-900 hover:bg-white/20 transition-colors"
              >
                Cancel
              </button>
              <button
                data-tip="Creates one item for each SKU you entered"
                onClick={handleBulkAddItems}
                disabled={!bulkSkuInput.trim()}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add{" "}
                {bulkSkuInput.trim()
                  ? bulkSkuInput.split(/[\s,\t\n]+/).filter((s) => s.trim())
                      .length
                  : 0}{" "}
                Items
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Grouped Items Modal */}
      {showBulkSetModal && (
        <div className="fixed inset-0 bg-slate-900/50 z-[120] flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-2xl overflow-hidden max-h-[90vh] flex flex-col shadow-2xl">
            {/* Modal Header */}
            <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-slate-200 bg-slate-50">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                  <Layers className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">
                    {editingSetId ? "Edit Grouped Items" : "Add Grouped Items"}
                  </h3>
                  <p className="text-xs text-slate-600">
                    {editingSetId
                      ? "Modify items in this group"
                      : "Add multiple items that belong together"}
                  </p>
                </div>
              </div>
              <button
                aria-label="Close"
                onClick={handleCloseGroupedItemsModal}
                className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Group Name & Price - Side by Side */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-slate-500 mb-1.5">
                    Group Name
                  </label>
                  <input
                    type="text"
                    value={setName}
                    onChange={(e) => setSetName(e.target.value)}
                    placeholder="e.g. 5-Piece Bedroom Set"
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-500 mb-1.5">
                    Group Price ($)
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={setPrice}
                    onChange={(e) => setSetPrice(e.target.value)}
                    placeholder="e.g. 1899.99"
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
                  />
                </div>
              </div>

              <div className="border-t border-slate-200" />

              {/* Items Section Header */}
              <label className="text-sm font-medium text-slate-700">
                Items in Group (
                {groupedItems
                  .filter((i) => i.name && i.name.trim())
                  .reduce(
                    (sum, item) => sum + (parseInt(item.quantity) || 1),
                    0,
                  )}{" "}
                pieces)
              </label>

              {/* Inline Editable Rows with Collapse */}
              {groupedItems.length === 0 ? (
                <div className="p-6 rounded-xl border border-dashed border-blue-400 bg-slate-50/50 text-center">
                  <Package className="w-10 h-10 text-blue-600/50 mx-auto mb-2" />
                  <p className="text-slate-500 text-sm">No items added yet</p>
                  <div className="mt-3 flex items-center justify-center">
                    <button
                      data-tip="Adds a blank item row to this group"
                      onClick={handleAddInlineGroupRow}
                      className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-all"
                    >
                      + Add First Item
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {groupedItems.map((item, index) => {
                    const isFilled =
                      item.name && item.name.trim() && item.manufacturerId;
                    const isExpanded =
                      editingGroupItemIndex === index || !isFilled;
                    const mfrName = item.manufacturerId
                      ? manufacturers.find((m) => m.id === item.manufacturerId)
                          ?.name
                      : null;

                    // COLLAPSED: compact single-line pill for filled items
                    if (!isExpanded && isFilled) {
                      return (
                        <div
                          key={index}
                          onClick={() => setEditingGroupItemIndex(index)}
                          className="flex items-center justify-between px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-all group"
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">
                              {index + 1}
                            </span>
                            {(item.quantity || 1) > 1 && (
                              <span className="flex-shrink-0 px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-xs font-bold">
                                {item.quantity}x
                              </span>
                            )}
                            <span className="text-slate-900 font-medium text-sm truncate">
                              {item.name}
                            </span>
                            {item.sku && (
                              <span className="flex-shrink-0 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-xs font-mono">
                                {item.sku}
                              </span>
                            )}
                            {item.color && (
                              <span className="flex-shrink-0 text-slate-400 text-xs">
                                · {item.color}
                              </span>
                            )}
                            {mfrName && (
                              <span className="flex-shrink-0 text-blue-500 text-xs">
                                {mfrName}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 ml-2">
                            <ChevronDown className="w-3.5 h-3.5 text-slate-400 group-hover:text-blue-500 transition-colors" />
                            <button
                              aria-label="Remove this item from the group"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveGroupItem(index);
                              }}
                              className="p-1 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    }

                    // EXPANDED: full inline editable form
                    return (
                      <div
                        key={index}
                        className={`p-3 rounded-xl border transition-all ${isFilled ? "bg-slate-50/80 border-slate-200" : "bg-blue-50/40 border-blue-200"}`}
                      >
                        {/* Row Header */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                              Item {index + 1}
                            </span>
                            {isFilled && (
                              <button
                                onClick={() => setEditingGroupItemIndex(null)}
                                className="text-xs text-blue-500 hover:text-blue-700 font-medium"
                              >
                                ▲ Collapse
                              </button>
                            )}
                          </div>
                          <button
                            onClick={() => handleRemoveGroupItem(index)}
                            className="p-1 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                            title="Remove item"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>

                        {/* Name */}
                        <div className="mb-2">
                          <GroupItemNameAutocomplete
                            inputId={`group-row-name-${index}`}
                            value={item.name || ""}
                            onChange={(val) =>
                              handleUpdateGroupItemInline(index, "name", val)
                            }
                            onSelect={(name, sku, brand) => {
                              const fields = { name, sku };
                              const mfrId =
                                resolveBrandToManufacturerId(brand);
                              if (mfrId) {
                                fields.manufacturerId = mfrId;
                              }
                              handleUpdateGroupItemFields(index, fields);
                            }}
                            onSelectMany={(picks) => {
                              const resolved = picks.map((p) => ({
                                name: p.name,
                                sku: p.sku,
                                quantity: p.quantity || 1,
                                manufacturerId:
                                  resolveBrandToManufacturerId(p.brand) || null,
                              }));
                              handleAddGroupItemsBulk(index, resolved);
                            }}
                            onEnter={() =>
                              document
                                .getElementById(`group-row-qty-${index}`)
                                ?.focus()
                            }
                            placeholder="Item name (e.g. Queen Headboard)"
                            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors text-sm"
                          />
                        </div>

                        {/* Qty, SKU, Color, Manufacturer */}
                        <div className="grid grid-cols-12 gap-2">
                          <div className="col-span-2">
                            <input
                              id={`group-row-qty-${index}`}
                              type="text"
                              inputMode="numeric"
                              value={
                                item.quantity === undefined ||
                                item.quantity === null
                                  ? "1"
                                  : item.quantity
                              }
                              onChange={(e) => {
                                const raw = e.target.value.replace(
                                  /[^0-9]/g,
                                  "",
                                );
                                handleUpdateGroupItemInline(
                                  index,
                                  "quantity",
                                  raw,
                                );
                              }}
                              onBlur={() => {
                                const val = parseInt(item.quantity) || 1;
                                handleUpdateGroupItemInline(
                                  index,
                                  "quantity",
                                  Math.max(1, val),
                                );
                              }}
                              className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-slate-900 focus:outline-none focus:border-blue-500 transition-colors text-sm text-center font-semibold"
                            />
                            <span className="block text-center text-[10px] text-slate-400 mt-0.5">
                              Qty
                            </span>
                          </div>
                          <div className="col-span-3">
                            <input
                              type="text"
                              value={item.sku || ""}
                              onChange={(e) =>
                                handleUpdateGroupItemInline(
                                  index,
                                  "sku",
                                  e.target.value.toUpperCase(),
                                )
                              }
                              placeholder="SKU"
                              className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors text-sm font-mono uppercase"
                            />
                            <span className="block text-[10px] text-slate-400 mt-0.5 pl-1">
                              SKU
                            </span>
                          </div>
                          <div className="col-span-3">
                            <input
                              type="text"
                              value={item.color || ""}
                              onChange={(e) =>
                                handleUpdateGroupItemInline(
                                  index,
                                  "color",
                                  e.target.value,
                                )
                              }
                              placeholder="Color"
                              className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors text-sm"
                            />
                            <span className="block text-[10px] text-slate-400 mt-0.5 pl-1">
                              Color
                            </span>
                          </div>
                          <div className="col-span-4">
                            {!showNewManufacturerInput ||
                            editingGroupItemIndex !== index ? (
                              <select
                                value={item.manufacturerId || ""}
                                onChange={(e) => {
                                  if (e.target.value === "__new__") {
                                    setEditingGroupItemIndex(index);
                                    setShowNewManufacturerInput(true);
                                  } else {
                                    handleUpdateGroupItemInline(
                                      index,
                                      "manufacturerId",
                                      e.target.value || null,
                                    );
                                  }
                                }}
                                className={`w-full px-2 py-1.5 bg-white border rounded-lg text-slate-900 focus:outline-none focus:border-blue-500 transition-colors text-sm ${!item.manufacturerId ? "border-red-300 bg-red-50/30" : "border-slate-200"}`}
                              >
                                <option value="">Brand *</option>
                                {manufacturers.map((m) => (
                                  <option key={m.id} value={m.id}>
                                    {m.name}
                                  </option>
                                ))}
                                <option value="__new__">+ Add new...</option>
                              </select>
                            ) : (
                              <div className="flex gap-1">
                                <input
                                  type="text"
                                  value={newManufacturerName}
                                  onChange={(e) =>
                                    setNewManufacturerName(e.target.value)
                                  }
                                  onKeyDown={(e) =>
                                    e.key === "Enter" &&
                                    handleCreateNewManufacturer("group")
                                  }
                                  placeholder="Brand name..."
                                  className="flex-1 px-2 py-1.5 bg-white border border-blue-400 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors text-sm"
                                  autoFocus
                                  disabled={isCreatingManufacturer}
                                />
                                <button
                                  aria-label="Create manufacturer"
                                  onClick={() =>
                                    handleCreateNewManufacturer("group")
                                  }
                                  disabled={
                                    !newManufacturerName.trim() ||
                                    isCreatingManufacturer
                                  }
                                  className="px-1.5 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-xs"
                                >
                                  {isCreatingManufacturer ? "..." : "✓"}
                                </button>
                                <button
                                  aria-label="Cancel new manufacturer"
                                  onClick={() => {
                                    setShowNewManufacturerInput(false);
                                    setNewManufacturerName("");
                                  }}
                                  className="px-1.5 py-1 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            )}
                            <span className="block text-[10px] text-slate-400 mt-0.5 pl-1">
                              Brand
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Add Another Row Button */}
                  <button
                    data-tip="Adds a blank item row to this group"
                    onClick={handleAddInlineGroupRow}
                    className="w-full py-2.5 rounded-xl bg-blue-50 border border-blue-300 text-blue-600 text-sm font-semibold hover:bg-blue-100 hover:border-blue-400 transition-all flex items-center justify-center gap-1.5"
                  >
                    <Plus className="w-4 h-4" />
                    Add Another Item
                  </button>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex-shrink-0 flex items-center justify-end space-x-3 p-4 border-t border-slate-300">
              <button
                onClick={handleCloseGroupedItemsModal}
                className="px-4 py-2.5 rounded-xl bg-slate-100 text-slate-900 hover:bg-white/20 transition-colors"
              >
                Cancel
              </button>
              <button
                data-tip={editingSetId ? "Saves your changes to this set" : "Adds these items to the receipt as one set"}
                onClick={handleAddSet}
                disabled={
                  groupedItems.filter(
                    (i) => i.name && i.name.trim() && i.manufacturerId,
                  ).length === 0
                }
                className="px-6 py-2.5 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingSetId ? "Save" : "Add"} Group (
                {groupedItems
                  .filter((i) => i.name && i.name.trim())
                  .reduce(
                    (sum, item) => sum + (parseInt(item.quantity) || 1),
                    0,
                  )}{" "}
                pieces)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Store Settings Modal */}
      {showStoreSettings && (
        <StoreSettings
          store={currentStore}
          campaignsTabEnabled={tabVisibility.campaigns}
          openSubscriptionModal={openSubscriptionModal}
          isSubscriptionDisabledForUser={isSubscriptionDisabledForUser}
          onClose={() => setShowStoreSettings(false)}
          onSaved={(savedSettings) => {
            console.log("Store settings saved:", savedSettings);
            // Update local storeSettings state with new tax rate
            setStoreSettings((prev) => ({
              ...prev,
              tax_rate:
                savedSettings.tax_rate !== undefined
                  ? savedSettings.tax_rate
                  : prev.tax_rate,
            }));
            setShowStoreSettings(false);
          }}
        />
      )}

      <SalesTaxExportModal
        open={showSalesTaxExportModal}
        onClose={() => setShowSalesTaxExportModal(false)}
        receipts={receipts}
        currentStore={currentStore}
        storeSettings={storeSettings}
      />

      {/* Signatures Drawer - NEW */}
      {/*No test data yet - remove this when we have test data*/} 
      {showSignaturesDrawer && (
        <div className="fixed inset-0 z-[110] flex items-end md:items-center justify-center">
          {/* z-[110] is above receipt modal z-[100] */}
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
            onClick={() => setShowSignaturesDrawer(false)}
          />

          {/* Drawer */}
          <div className="relative w-full md:max-w-2xl bg-white rounded-t-3xl md:rounded-3xl border border-slate-200 shadow-2xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex-shrink-0 p-4 border-b border-slate-300 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-emerald-400" />
                  Delivery Signatures
                </h3>
                <p className="text-sm text-slate-500 mt-0.5">
                  {selectedReceipt?.customer_name ||
                    selectedReceipt?.customerName}
                </p>
              </div>
              <button
                aria-label="Close"
                onClick={() => setShowSignaturesDrawer(false)}
                className="p-2 bg-slate-100 rounded-lg text-slate-500 hover:bg-white/20 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Signatures List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {loadingSignatures ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-2" />
                    <p className="text-slate-500 text-sm">
                      Loading signatures...
                    </p>
                  </div>
                </div>
              ) : signatures.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <FileText className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-slate-500">No signatures found</p>
                    <p className="text-slate-400 text-sm mt-1">
                      Signatures will appear here after delivery
                    </p>
                  </div>
                </div>
              ) : (
                signatures.map((sig, index) => (
                  <div
                    key={sig.id}
                    className="bg-slate-200 rounded-xl border border-slate-300 overflow-hidden"
                  >
                    {/* Signature Header */}
                    <div className="p-4 bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border-b border-slate-300">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <CheckCircle className="w-4 h-4 text-emerald-400" />
                            <span className="text-sm font-semibold text-slate-900">
                              {sig.is_partial
                                ? "Partial Delivery"
                                : "Full Delivery"}{" "}
                              #{signatures.length - index}
                            </span>
                            {sig.is_partial && (
                              <span className="px-2 py-0.5 bg-slate-200 border border-slate-300 rounded text-[10px] text-slate-500 font-semibold">
                                PARTIAL
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500">
                            {new Date(sig.signed_at).toLocaleString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-slate-400">
                            Items Delivered
                          </p>
                          <p className="text-lg font-bold text-slate-900">
                            {sig.total_items_in_signature}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Items Delivered */}
                    {sig.items_delivered && sig.items_delivered.length > 0 && (
                      <div className="p-4 border-b border-slate-300">
                        <p className="text-xs font-semibold text-slate-500 uppercase mb-2">
                          Items in This Delivery:
                        </p>
                        <div className="space-y-2">
                          {sig.items_delivered.map((item, itemIdx) => (
                            <div
                              key={itemIdx}
                              className="flex items-start justify-between text-sm p-2 bg-slate-50 rounded-lg"
                            >
                              <div className="flex-1">
                                <p className="text-slate-900 font-medium">
                                  {item.quantity}x {item.name}
                                </p>
                                <p className="text-xs text-slate-400 font-mono">
                                  {item.sku}
                                </p>
                                {item.item_numbers &&
                                  item.item_numbers.length > 0 && (
                                    <div className="mt-1 flex flex-wrap gap-1">
                                      {item.item_numbers.map((num, numIdx) => (
                                        <span
                                          key={numIdx}
                                          className="px-1.5 py-0.5 bg-blue-600/20 border border-blue-600/30 rounded text-[10px] text-blue-600 font-mono"
                                        >
                                          #{num}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Signature Image */}
                    <div className="p-4">
                      <p className="text-xs font-semibold text-slate-500 uppercase mb-2">
                        Customer Signature:
                      </p>
                      <div className="bg-white rounded-lg p-2">
                        <img
                          src={sig.signature_data}
                          alt="Customer Signature"
                          className="w-full h-32 object-contain"
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Unsaved Changes Warning Dialog */}
      {showUnsavedChangesWarning && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-[200]">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl max-w-sm w-full overflow-hidden">
            {/* Header */}
            <div className="p-5 text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertCircle className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                Unsaved Changes
              </h3>
              <p className="text-slate-500 text-sm">
                You have unsaved changes. Do you want to save them before
                closing?
              </p>
            </div>

            {/* Actions */}
            <div className="p-4 bg-slate-50 border-t border-slate-200 flex flex-col gap-2">
              <button
                onClick={async () => {
                  await handleSaveEditInModal();
                  setShowUnsavedChangesWarning(false);
                }}
                className="w-full py-3 px-4 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
              >
                Save Changes
              </button>
              <button
                data-tip="Closes the receipt without saving your edits"
                onClick={confirmDiscardChanges}
                className="w-full py-3 px-4 rounded-xl bg-red-100 text-red-600 font-semibold hover:bg-red-200 transition-colors"
              >
                Discard Changes
              </button>
              <button
                onClick={cancelDiscardChanges}
                className="w-full py-2.5 px-4 rounded-xl text-slate-500 font-medium hover:bg-slate-100 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal Unsaved Changes Warning Dialog */}
      {showEditModalUnsavedWarning && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-[200]">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl max-w-sm w-full overflow-hidden">
            {/* Header */}
            <div className="p-5 text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertCircle className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                Unsaved Changes
              </h3>
              <p className="text-slate-500 text-sm">
                You have unsaved changes. Do you want to save them before
                closing?
              </p>
            </div>

            {/* Actions */}
            <div className="p-4 bg-slate-50 border-t border-slate-200 flex flex-col gap-2">
              <button
                onClick={async () => {
                  await handleSaveReceipt();
                  setShowEditModalUnsavedWarning(false);
                }}
                className="w-full py-3 px-4 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
              >
                Save Changes
              </button>
              <button
                data-tip="Closes the form without saving your edits"
                onClick={confirmDiscardEditModalChanges}
                className="w-full py-3 px-4 rounded-xl bg-red-100 text-red-600 font-semibold hover:bg-red-200 transition-colors"
              >
                Discard Changes
              </button>
              <button
                onClick={cancelDiscardEditModalChanges}
                className="w-full py-2.5 px-4 rounded-xl text-slate-500 font-medium hover:bg-slate-100 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Receipt Warning Dialog */}
      {showDeleteWarning && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-[200]">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl max-w-sm w-full overflow-hidden">
            {/* Header */}
            <div className="p-5 text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="w-7 h-7 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                Delete Receipt?
              </h3>
              <p className="text-slate-500 text-sm">
                This will permanently delete this receipt and all associated
                messages, items, and data. This action cannot be undone.
              </p>
            </div>

            {/* Actions */}
            <div className="p-4 bg-slate-50 border-t border-slate-200 flex flex-col gap-2">
              <button
                data-tip="Permanently removes this receipt from the store"
                onClick={confirmDeleteReceipt}
                disabled={isDeletingReceipt}
                className="w-full py-3 px-4 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isDeletingReceipt ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete Receipt
                  </>
                )}
              </button>
              <button
                onClick={cancelDeleteReceipt}
                disabled={isDeletingReceipt}
                className="w-full py-2.5 px-4 rounded-xl text-slate-500 font-medium hover:bg-slate-100 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sign out confirmation */}
      {logoutConfirmOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-[200]">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl max-w-sm w-full overflow-hidden">
            <div className="p-5 text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
                <LogOut className="w-7 h-7 text-slate-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                Sign out?
              </h3>
              <p className="text-slate-500 text-sm">
                Are you sure? You will be signed out of all stores on this
                device.
              </p>
            </div>
            <div className="p-4 bg-slate-50 border-t border-slate-200 flex flex-col gap-2">
              <button
                type="button"
                onClick={confirmLogout}
                disabled={isLoggingOut}
                className="w-full py-3 px-4 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoggingOut ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Signing out…
                  </>
                ) : (
                  <>
                    <LogOut className="w-4 h-4" />
                    Sign out
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={cancelLogout}
                disabled={isLoggingOut}
                className="w-full py-2.5 px-4 rounded-xl text-slate-500 font-medium hover:bg-slate-100 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <SubscriptionPlansModal
        isOpen={isSubscriptionModalOpen}
        isDisabledForUser={isSubscriptionDisabledForUser}
        user={session?.user}
        currentPlanNumber={currentStore?.plan_number}
        storeId={currentStore?.id}
        onClose={closeSubscriptionModal}
      />

    </>
  );
};

export default ReceiptDashboard;